<template>
  <div id="question" class="page-container">
    <div class="page-content">
      <div class="flex-row-center mt-16">
        <img
          src="../assets/images/navbar/ic_questions.webp"
          class="icon-pages"
        />
        <span class="page-title">{{ $t("page.questions") }}</span>
        <span class="page-des page-des-line-button">{{
          $t("page.questions_description")
        }}</span>
        <div class="button-title">
          <el-popover
            v-if="canAdjustIntensityFromHeader"
            placement="bottom-end"
            :width="560"
            trigger="click"
            v-model="questionsIntensityPopoverVisible"
            popper-class="questions-intensity-popover"
          >
            <div class="qi-panel">
              <div class="qi-panel__head">
                <div class="qi-panel__eyebrow">Check-in intensity</div>
                <div class="qi-panel__title">How often your team is asked</div>
                <div class="qi-panel__lead">
                  <span class="qi-panel__lead-label">Currently</span>
                  <span
                    class="qi-state-chip"
                    :data-level="currentIntensityCadence || 'unset'"
                  >
                    <span class="qi-state-chip__bars" aria-hidden="true">
                      <i v-for="n in 3" :key="n" :class="{ on: n <= intensityLevelIndex }"></i>
                    </span>
                    {{ currentIntensityCadenceLabel }}
                  </span>
                  <span class="qi-panel__lead-divider" aria-hidden="true">·</span>
                  <span class="qi-panel__summary">{{ currentIntensityHeadline }}</span>
                </div>
              </div>
              <div class="qi-panel__divider"></div>
              <IntensityCadencePicker
                :value="currentIntensityCadence"
                :disabled="!hasLoadedIntensityState || savingHeaderIntensity || undoingIntensityChange"
                aria-label="Questions header intensity"
                @input="onHeaderCadenceChange"
              />
              <div class="qi-panel__foot">
                <span v-if="savingHeaderIntensity || undoingIntensityChange" class="qi-saving">
                  <span class="qi-saving__spinner" aria-hidden="true"></span>
                  {{ undoingIntensityChange ? 'Reverting…' : 'Saving…' }}
                </span>
                <div
                  v-else-if="pendingIntensityRevert"
                  class="qi-undo"
                  :key="pendingIntensityRevertExpiresAt"
                >
                  <span class="qi-undo__check" aria-hidden="true">✓</span>
                  <span class="qi-undo__text">
                    Switched to <strong>{{ currentIntensityCadenceLabel }}</strong>
                  </span>
                  <button
                    type="button"
                    class="qi-undo__btn"
                    @click="onUndoIntensityChange"
                  >
                    Undo
                  </button>
                  <span class="qi-undo__bar" aria-hidden="true"></span>
                </div>
                <span v-else class="qi-panel__helper">{{ intensityPanelHelperText }}</span>
              </div>
            </div>
            <el-tooltip
              slot="reference"
              effect="dark"
              placement="bottom"
              :open-delay="350"
              :content="`Check-in intensity · ${currentIntensityCadenceLabel}`"
            >
              <button
                type="button"
                class="questions-intensity-btn mr-14"
                :class="{
                  'is-open': questionsIntensityPopoverVisible,
                  'is-saving': savingHeaderIntensity,
                }"
                :data-level="currentIntensityCadence || 'unset'"
                :aria-label="`Check-in intensity, currently ${currentIntensityCadenceLabel}`"
              >
                <span class="qi-meter" aria-hidden="true">
                  <i v-for="n in 3" :key="n" :class="{ on: n <= intensityLevelIndex }"></i>
                </span>
              </button>
            </el-tooltip>
          </el-popover>
          <div
            @click="openCalendarModal()"
            class="flex-mid mr-14 cursor-pointer calendar-modal"
            :title="$t('questions.CALENDAR_VIEW')"
          >
            <happily-icon name="calendar" :size="22" />
          </div>
          <el-button
            class="btnAddQuestion"
            type="primary"
            @click="addQuestion"
            >{{ $t("questions.add_a_question") }}</el-button
          >
        </div>
      </div>

      <el-tabs class="style-bar" v-model="activeName">
        <el-tab-pane name="standard">
          <span slot="label" class="title-bar">{{ $t("page.standard") }}</span>
          <standard
            v-if="shouldRenderTab('standard')"
            :view-ready="tabReady.standard"
          ></standard>
        </el-tab-pane>
        <el-tab-pane name="custom">
          <span slot="label" class="title-bar">{{ $t("page.custom") }}</span>
          <custom
            v-if="shouldRenderTab('custom')"
            :view-ready="tabReady.custom"
          ></custom>
        </el-tab-pane>
      </el-tabs>

      <!-- create question -->
      <div v-if="questionFormMounted" id="questions_form_modal">
        <el-dialog
          width="1050px"
          top="5vh"
          :visible.sync="dialogFormVisible"
          :close-on-click-modal="false"
          :close-on-press-escape="false"
          @close="cancelDialog"
        >
          <div class="grid-2-section">
            <div class="grid-left aq-preview-backdrop">
              <div class="aq-preview-eyebrow">{{ $t("questions.preview_for_managers") }}</div>
              <div class="aq-email-shell">
                <div class="aq-email-meta">
                  <span class="aq-email-avatar">H</span>
                  <div class="aq-email-meta-text">
                    <div class="aq-email-from">Happily Insights</div>
                    <div class="aq-email-subject">{{ $t("questions.email_subject_today") }}</div>
                  </div>
                  <span class="aq-email-date">{{ $t("questions.today") }}</span>
                </div>
                <div class="aq-email-intro">
                  <span class="aq-email-intro-pill">{{ $t("questions.three_insights") }}</span>
                  {{ $t("questions.email_intro_shared") }}
                </div>
                <div class="aq-email-body">
                  <div
                    class="aq-preview-card"
                    :class="[
                      previewType === 'open' ? 'is-purple' : 'is-green',
                      { 'is-empty': previewType === 'empty' }
                    ]"
                  >
                    <div
                      class="aq-preview-card-title"
                      :class="{ 'is-skeleton': !previewQuestionText }"
                    >
                      <template v-if="previewQuestionText">{{ previewQuestionText }}</template>
                    </div>

                <!-- Yes/No (agreement-style) — two-stat layout -->
                <template v-if="previewType === 'yesno'">
                  <div class="aq-stat-row">
                    <div class="row">
                      <span class="big">100%</span>
                      <span class="delta">(↑40%)</span>
                    </div>
                    <div class="caption">{{ $t("questions.caption_of_four_direct") }}</div>
                    <div class="aq-progress-bar">
                      <div class="aq-progress-fill" style="width: 100%"></div>
                    </div>
                  </div>
                  <div class="aq-stat-row">
                    <div class="row">
                      <span class="big">86%</span>
                      <span class="delta">(↑14%)</span>
                    </div>
                    <div class="caption">{{ $t("questions.caption_of_fourteen_overall") }}</div>
                    <div class="aq-progress-bar">
                      <div class="aq-progress-fill" style="width: 86%"></div>
                    </div>
                  </div>
                  <div class="aq-card-caption">
                    {{ $t("questions.responded_word") }} "{{ previewTopOptionLabel }}"
                  </div>
                  <a class="aq-view-details">{{ $t("questions.view_details") }}</a>
                </template>

                <!-- Multiple Choice (other) — single-stat layout -->
                <template v-else-if="previewType === 'multi'">
                  <div class="aq-stat-row">
                    <div class="row">
                      <span class="big">64%</span>
                      <span class="delta">(↑12%)</span>
                    </div>
                    <div class="caption">{{ $t("questions.caption_of_fourteen_overall") }}</div>
                    <div class="aq-progress-bar">
                      <div class="aq-progress-fill" style="width: 64%"></div>
                    </div>
                  </div>
                  <div class="aq-card-caption">
                    {{ $t("questions.responded_word") }} "{{ previewTopOptionLabel }}"
                  </div>
                  <a class="aq-view-details">{{ $t("questions.view_details") }}</a>
                </template>

                <!-- Scale / NPS — average + illustrative band strip -->
                <template v-else-if="previewType === 'scale'">
                  <div class="aq-stat-row">
                    <div class="row">
                      <span class="big">8.3</span>
                      <span class="delta">(↑0.4)</span>
                    </div>
                    <div class="caption">{{ $t("questions.caption_of_fourteen_overall") }}</div>
                  </div>
                  <div class="aq-band-strip" aria-hidden="true">
                    <span class="score-band-zero"></span>
                    <span class="score-band-low"></span>
                    <span class="score-band-moderate"></span>
                    <span class="score-band-high"></span>
                    <span class="score-band-promoter"></span>
                  </div>
                  <a class="aq-view-details">{{ $t("questions.view_details") }}</a>
                </template>

                <!-- Open-Ended — quoted sample response -->
                <template v-else-if="previewType === 'open'">
                  <div class="aq-response-quote">
                    "{{ $t("questions.sample_response_placeholder") }}"
                  </div>
                  <div class="aq-response-replied">{{ $t("questions.youve_replied") }}</div>
                  <a class="aq-view-details">{{ $t("questions.view_details") }}</a>
                </template>

                <!-- Empty — skeleton placeholders until type is chosen -->
                <template v-else>
                  <div class="aq-stat-row is-skeleton">
                    <div class="row">
                      <span class="big">&nbsp;</span>
                    </div>
                    <div class="caption">&nbsp;</div>
                    <div class="aq-progress-bar"></div>
                  </div>
                  <div class="aq-stat-row is-skeleton">
                    <div class="row">
                      <span class="big">&nbsp;</span>
                    </div>
                    <div class="caption">&nbsp;</div>
                    <div class="aq-progress-bar"></div>
                  </div>
                </template>
                  </div>
                  <!-- Ghost sibling: visually suggests that this question sits
                       alongside other insights in the manager's daily email. -->
                  <div class="aq-ghost-tile">
                    <div class="aq-ghost-bar w-60"></div>
                    <div class="aq-ghost-bar w-40"></div>
                    <div class="aq-ghost-stat">
                      <div class="aq-ghost-big"></div>
                      <div class="aq-ghost-fill"></div>
                    </div>
                  </div>
                </div>
                <div class="aq-email-footer">
                  <span>{{ $t("questions.view_full_daily_report") }}</span>
                  <span class="aq-email-footer-arrow">→</span>
                </div>
              </div>
            </div>
            <div class="grid-right fc-000000">
              <div class="fs-20 fw-700">{{ title_dialog }}</div>

              <div v-if="questionSaveSuccess" class="aq-success-panel">
                <div class="aq-success-icon">
                  <i class="el-icon-check"></i>
                </div>
                <div class="aq-success-eyebrow">
                  {{
                    savedQuestionSummary && savedQuestionSummary.isProgramOnly
                      ? $t("questions.saved_program_only_question")
                      : savedQuestionSummary && savedQuestionSummary.isFollowUp
                        ? $t("questions.saved_follow_up_question")
                        : $t("questions.question_saved")
                  }}
                </div>
                <div class="aq-success-title">
                  {{ savedQuestionSummary ? savedQuestionSummary.question : "" }}
                </div>
                <div class="aq-success-meta" v-if="savedQuestionSummary">
                  <span>{{ savedQuestionSummary.typeName }}</span>
                  <span>{{ savedQuestionSummary.frequencyLabel }}</span>
                  <span v-if="savedQuestionSummary.scheduleLabel">{{ savedQuestionSummary.scheduleLabel }}</span>
                </div>
                <div class="aq-success-copy">
                  {{
                    savedQuestionSummary && savedQuestionSummary.isProgramOnly
                      ? $t("questions.saved_program_only_question_hint")
                      : savedQuestionSummary && savedQuestionSummary.isFollowUp
                        ? $t("questions.saved_follow_up_question_hint")
                        : $t("questions.saved_question_calendar_hint")
                  }}
                </div>
                <div class="aq-success-actions">
                  <el-button
                    v-if="savedQuestionSummary && !savedQuestionSummary.isParked"
                    class="btn-add bigger"
                    type="primary"
                    @click="viewSavedQuestionInCalendar"
                    :disabled="isOpeningSavedQuestionCalendar"
                  >
                    <i v-if="isOpeningSavedQuestionCalendar" class="el-icon-loading"></i>
                    {{ $t("questions.view_in_calendar") }}
                  </el-button>
                  <el-button class="btn-cancel bigger" @click="startAnotherQuestion">
                    {{ $t("questions.add_another_question") }}
                  </el-button>
                  <el-button class="btn-cancel bigger" @click="finishQuestionSuccess">
                    {{ $t("questions.done") }}
                  </el-button>
                </div>
              </div>

              <div v-if="!questionSaveSuccess" class="aq-generate-block">
                <el-input
                  type="textarea"
                  v-model="questions_text"
                  auto-complete="off"
                  class="custom-textarea"
                  :placeholder="$t('questions.generate_placeholder')"
                ></el-input>
                <div class="aq-generate-actions">
                  <el-button
                    class="generate-question"
                    type="primary"
                    @click="genQuestion"
                  >{{ $t("questions.generate_question") }}</el-button>
                </div>
              </div>

              <div style="display: flex;justify-content: center;margin-top: 30px;" v-if="!questionSaveSuccess && getquestionLoading"> 
                <loading-even></loading-even>
              </div>
              <div v-else-if="!questionSaveSuccess">
              <el-form
                :model="dynamicValidateForm"
                ref="dynamicValidateForm"
                style="margin-left: 30%; margin-right: 32px"
              >
              <div class="aq-lang-tabs" role="tablist">
                <span
                  v-for="(item, index) in language"
                  :key="index"
                  class="aq-lang-tab"
                  :class="{ 'is-active': activeIndex === item.code }"
                  role="tab"
                  :aria-selected="activeIndex === item.code"
                  @click="handleSelect(item.code)"
                >{{ item.text }}</span>
              </div>




                <!-- <el-menu
                  style
                  :default-active="activeIndex"
                  class="el-menu-demo"
                  mode="horizontal"
                  @select="handleSelect"
                >
                  <el-menu-item style="padding-left: 0" index="1">{{
                    $t("global.english")
                  }}</el-menu-item>
                  <el-menu-item index="2" v-if="!engonly">{{
                    $t("global.thai")
                  }}</el-menu-item>
                </el-menu> -->
                <br />
                <div style="margin-left: -130px">
                  <el-form-item :label-width="formLabelWidth">
                    <div class="ml-auto recipients-count">
                      <span
                        class="top-header cursor-pointer"
                        @click="openRecipientsDialog"
                        >{{
                          dynamicValidateForm.recipients
                            ? dynamicValidateForm.recipients.length
                            : allUsers.length
                        }}
                        {{ $t("powerUp.recipients") }}</span
                      >
                    </div>
                  </el-form-item>
                </div>
                <div style="margin-left: -130px">
                  <el-form-item
                    :label="$t('questions.send_to')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-cascader
                      ref="refTo"
                      style="width: 100%"
                      class="defaulttags"
                      :placeholder="$t('powerUp.Name_of_Recipient_s')"
                      v-model="dynamicValidateForm.cascaderTo"
                      :options="recipientOptions"
                      :props="propsCofig"
                      :disabled="!refreshInput"
                      @change="changeDataToCascader"
                      @paste.native="handleRecipientPaste"
                      :collapse-tags="
                        dynamicValidateForm.cascaderTo
                          ? dynamicValidateForm.cascaderTo.length > 8
                          : dynamicValidateForm.cascaderTo
                      "
                      :debounce="500"
                      :filter-method="filterToFunction"
                      filterable
                      :class="[ blurMode ?  'blurelement': '' ]"
                    >
                      <template slot-scope="{ data }">
                        <span :class="[ blurMode ?  'blurelement': '' ]"
                        >{{ data.label }}</span>
                      </template>
                    </el-cascader>
                  </el-form-item>
                </div>

                





                <div  style="margin-left: -130px">
                  <el-form-item
                    :label="$t('global.type')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <div class="flex">
                      <div class="mr-6">
                        <el-select
                          v-model="dynamicValidateForm.category"
                          style="width: 100%"
                          :placeholder="$t('questions.Select_Dimension')"
                        >
                          <el-option
                            v-for="item in optionsCategory"
                            :key="item.key"
                            :label="item.category"
                            :value="item.key"
                          ></el-option>
                        </el-select>
                      </div>
                      <div class="ml-6">
                        <el-select
                          v-model="dynamicValidateForm.type"
                          style="width: 100%"
                          :placeholder="$t('questions.Question_Format')"
                          @change="onChangeQuestionsType(dynamicValidateForm.type)"
                        >
                          <el-option
                            :label="$t('questions.open_ended')"
                            value="Text Area Field"
                          ></el-option>
                          <el-option
                            :label="$t('questions.yes_or_no')"
                            value="Multiple Choice"
                          ></el-option>
                          <el-option
                            :label="$t('questions.multiple_choice')"
                            value="Multiple Choice(other)"
                          ></el-option>
                          <el-option :label="$t('questions.Scale')" value="Scale"></el-option>
                        </el-select>
                      </div>
                    </div>
                  </el-form-item>
                  <el-form-item
                    :label="$t('questions.question')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-input
                      v-model="dynamicValidateForm.questions[activeIndex]"
                      auto-complete="off"
                      :placeholder="
                        questionPlaceholder(dynamicValidateForm.type, '')
                      "
                    ></el-input>
                  </el-form-item>
                  <div v-if="dynamicValidateForm.type == 'Multiple Choice'">


                    <el-form-item
                    v-for="(options, index) in getActiveOptions()"
                    :key="options.key"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                    :label="options.placeholder + ' ' + $t('questions.option')"
                    class="title-input-form"
                    :prop="activeIndex === 'en' || activeIndex === 'th' 
                      ? `options_${activeIndex}.` + index + '.value' 
                      : `options_YN.${activeIndex}.` + index + '.value'"
                  >
                 
                    <div class="flex">
                      <div class="mr-6">
                        <el-input
                          v-model="options.value"
                          auto-complete="off"
                          @input="updateOptionValue(index, $event)"
                          :readonly="isReadonly()"
                          :key="isReadonly() ? 'readonly-input' : 'editable-input'"
                        ></el-input>
                      </div>
                      <div class="ml-6">
                        <div v-if="index == 0">
                          <el-select
                            v-model="dynamicValidateForm.feelingY"
                            style="width:95%"
                            @change="onChangeY(dynamicValidateForm.feelingY)"
                          >
                            <el-option
                              :label="$t('questions.positive')"
                              value="Positive"
                            ></el-option>
                            <el-option
                              :label="$t('questions.neutral')"
                              value="Neutral"
                            ></el-option>
                            <el-option
                              :label="$t('questions.negative')"
                              value="Negative"
                            ></el-option>
                          </el-select>
                        </div>
                        <div v-else>
                          <el-select
                            v-model="dynamicValidateForm.feelingN"
                            style="width:95%"
                            @change="onChangeN(dynamicValidateForm.feelingN)"
                          >
                            <el-option
                              :label="$t('questions.positive')"
                              value="Positive"
                            ></el-option>
                            <el-option
                              :label="$t('questions.neutral')"
                              value="Neutral"
                            ></el-option>
                            <el-option
                              :label="$t('questions.negative')"
                              value="Negative"
                            ></el-option>
                          </el-select>
                        </div>
                      </div>
                      <div>
                        <el-button
                          :style="`width:40px;padding: 12px 3px;${getstylefollowUp(options)}`"
                          @click="getfollow_up_question(index, options)"
                        >
                          O->
                        </el-button>
                      </div>
                    </div>
                  </el-form-item>
                    <!-- <el-form-item
                      v-if="activeIndex=='en'||activeIndex=='th'"
                      :label-width="formLabelWidth"
                      style="margin-bottom: 12px"
                      v-for="(options, index) in dynamicValidateForm[`options_${activeIndex}`]"
                      :label="
                        options.placeholder + ' ' + $t('questions.option')
                      "
                      class="title-input-form"
                      :key="options.key"
                      :prop="`options_${activeIndex}.` + index + '.value'"
                    >
                      <div class="flex">
                        <div class="mr-6">
                          <el-input
                            v-model="options.value"
                            auto-complete="off"
                            readonly
                             :key="'readonly-input'"
                          ></el-input>
                         
                        </div>
                        <div class="ml-6">
                          <div v-if="index == 0">
                            <el-select
                              v-model="dynamicValidateForm.feelingY"
                              style="width:95%"
                              @change="onChangeY(dynamicValidateForm.feelingY)"
                            >
                              <el-option
                                :label="$t('questions.positive')"
                                value="Positive"
                              ></el-option>
                              <el-option
                                :label="$t('questions.neutral')"
                                value="Neutral"
                              ></el-option>
                              <el-option
                                :label="$t('questions.negative')"
                                value="Negative"
                              ></el-option>
                            </el-select>
                          </div>
                          <div v-else>
                            <el-select
                              v-model="dynamicValidateForm.feelingN"
                              style="width:95%"
                              @change="onChangeN(dynamicValidateForm.feelingN)"
                            >
                              <el-option
                                :label="$t('questions.positive')"
                                value="Positive"
                              ></el-option>
                              <el-option
                                :label="$t('questions.neutral')"
                                value="Neutral"
                              ></el-option>
                              <el-option
                                :label="$t('questions.negative')"
                                value="Negative"
                              ></el-option>
                            </el-select>
                          </div>
                        </div>
                        <div>
                          <el-button 
                          :style="`width:40px;padding: 12px 3px;${getstylefollowUp(options)}`"
                          @click="getfollow_up_question(index,options)">
                            O->
                          </el-button>
                        </div>
                      </div>
                    </el-form-item> -->
                  

                  </div>
                  <div
                    v-if="dynamicValidateForm.type == 'Multiple Choice(other)'"
                  >
                    <el-form-item
                      :label-width="formLabelWidth"
                      style="margin-bottom: 12px"
                      v-for="(options, index) in dynamicValidateForm.options"
                      :label="$t('questions.option') + ' ' + increase(index)"
                      class="title-input-form"
                      :key="options.key"
                    >
                      <div class="flex">
                        <div class="mr-6">
                          <div v-if="index < 2">
                            <el-input
                              auto-complete="off"
                              v-model="options[activeIndex]"
                              :placeholder="'Option ' + increase(index)"
                            >
                            </el-input>
                          </div>
                          <div v-else style="width: 172.2px">
                            <el-input
                              auto-complete="off"
                              v-model="options[activeIndex]"
                              :placeholder="'Option ' + increase(index)"
                            >
                              <i
                                slot="suffix"
                                class="el-icon-remove-outline"
                                @click="deleteOptions(options)"
                              ></i>
                            </el-input>
                          </div>
                        </div>
                        <div class="ml-6">
                          <el-select
                            v-model="options.feeling"
                            style="width:95%"
                            :placeholder="$t('questions.assign_a_sentiment')"
                          >
                            <el-option
                              :label="$t('questions.positive')"
                              value="Positive"
                            ></el-option>
                            <el-option
                              :label="$t('questions.neutral')"
                              value="Neutral"
                            ></el-option>
                            <el-option
                              :label="$t('questions.negative')"
                              value="Negative"
                            ></el-option>
                          </el-select>
                        </div>

                        
                        <div>
                          <el-button 
                          :style="`width:40px;padding: 12px 3px;${getstylefollowUp(options)}`"
                          @click="getfollow_up_question(index,options)">
                            O->
                          </el-button>
                        </div>
                      </div>

                    </el-form-item>
                    <el-form-item
                      :label-width="formLabelWidth"
                      style="margin-bottom: 12px"
                    >
                      <el-button class="btnAddOption" @click="addOptions">{{
                        $t("questions.add_option")
                      }}</el-button>
                    </el-form-item>
                  </div>
                  <div v-if="dynamicValidateForm.type == 'Scale'">
                    <el-form-item
                      :label-width="formLabelWidth"
                      style="margin-bottom: 12px"
                      :label="$t('questions.min_label')"
                      class="title-input-form"
                    >
                      <el-col :span="13">
                        <el-input
                          auto-complete="off"
                          v-model="dynamicValidateForm.less_txts[activeIndex]"
                          :placeholder="$t('questions.Not_at_all')"
                        >
                        </el-input>
                      </el-col>
                      <el-col :span="1">&nbsp;</el-col>
                      <el-col :span="10">
                        <el-input
                          auto-complete="off"
                          :disabled="true"
                          v-model="dynamicValidateForm.min"
                        >
                        </el-input>
                      </el-col>
                    </el-form-item>
                    <el-form-item
                      :label-width="formLabelWidth"
                      style="margin-bottom: 12px"
                      :label="$t('questions.max_label')"
                      class="title-input-form"
                    >
                      <el-col :span="13">
                        <el-input
                          auto-complete="off"
                          :placeholder="$t('questions.Very_important')"
                          v-model="dynamicValidateForm.max_txts[activeIndex]"
                        >
                        </el-input>
                      </el-col>
                      <el-col :span="1">&nbsp;</el-col>
                      <el-col :span="10">
                        <el-input
                          auto-complete="off"
                          :disabled="true"
                          v-model="dynamicValidateForm.max"
                        >
                        </el-input>
                      </el-col>
                    </el-form-item>
                  </div>
                  <el-form-item
                    :label="$t('questions.reward')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-input
                      v-model="dynamicValidateForm.coins"
                      type="number"
                      :min="0"
                      auto-complete="off"
                      style="width: 100%"
                      :placeholder="$t('questions.coins')"
                    >
                      <template slot="append">{{ $t("questions.coins") }}</template>
                    </el-input>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.type == 'Text Area Field'"
                    :label="$t('questions.power_skill')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.powerSkill"
                      style="width: 100%"
                      :placeholder="$t('questions.select_power_skill')"
                      clearable
                    >
                      <el-option
                        :label="$t('category.power_skill.empathy')"
                        value="ps_empathy"
                      ></el-option>
                      <el-option
                        :label="$t('category.power_skill.self_awareness')"
                        value="ps_self_awareness"
                      ></el-option>
                      <el-option
                        :label="$t('category.power_skill.leadership')"
                        value="ps_leadership"
                      ></el-option>
                      <el-option
                        :label="$t('category.power_skill.initiative_making')"
                        value="ps_initiative_making"
                      ></el-option>
                      <el-option
                        :label="$t('category.power_skill.optimism')"
                        value="ps_optimism"
                      ></el-option>
                      <el-option
                        :label="$t('category.power_skill.critical_thinking')"
                        value="ps_critical_thinking"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    :label="$t('questions.frequency')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.frequency"
                      style="width: 100%"
                      @change="scheduleChange"
                      :placeholder="$t('questions.how_often_do')"
                    >
                      <el-option
                        :label="$t('category.frequency.one_time')"
                        value="onetime"
                      ></el-option>
                      <el-option
                        :label="$t('category.frequency.weekly')"
                        value="weekly"
                      ></el-option>
                      <el-option
                        :label="$t('category.frequency.monthly')"
                        value="monthly"
                      ></el-option>
                      <el-option
                        :label="$t('category.frequency.quarterly')"
                        value="quarterly"
                      ></el-option>
                      <el-option
                        :label="$t('category.frequency.biannually')"
                        value="biannually"
                      ></el-option>
                      <el-option
                        :label="$t('category.frequency.yearly')"
                        value="yearly"
                      ></el-option>
                      <el-option
                        :label="$t('questions.follow_up')"
                        value="Follow-up"
                      ></el-option>
                      <el-option
                        :label="$t('questions.program_only')"
                        value="Program-only"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency!='Follow-up' && dynamicValidateForm.frequency!='Program-only'"
                    class="title-input-form"
                    :label="$t('questions.day_of_week')"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.dayofweek"
                      style="width: 100%"
                      @change="scheduleChange"
                      :placeholder="$t('questions.what_day_of')"
                    >
                      <el-option
                        :label="$t('category.day_of_week.monday')"
                        value="monday"
                      ></el-option>
                      <el-option
                        :label="$t('category.day_of_week.tuesday')"
                        value="tuesday"
                      ></el-option>
                      <el-option
                        :label="$t('category.day_of_week.wednesday')"
                        value="wednesday"
                      ></el-option>
                      <el-option
                        :label="$t('category.day_of_week.thursday')"
                        value="thursday"
                      ></el-option>
                      <el-option
                        :label="$t('category.day_of_week.friday')"
                        value="friday"
                      ></el-option>
                      <el-option
                        v-if="dataCompany && dataCompany.alldays == true"
                        :label="$t('category.day_of_week.saturday')"
                        value="saturday"
                      ></el-option>
                      <el-option
                        v-if="dataCompany && dataCompany.alldays == true"
                        :label="$t('category.day_of_week.sunday')"
                        value="sunday"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == ''"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      :no-data-text="$t('questions.please_select_frequency')"
                      style="width: 100%"
                      :placeholder="$t('questions.and_when')"
                    ></el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'onetime'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option
                        v-for="item in optionsScheduleOnetime"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value"
                      ></el-option>
                      <div class="styleMoreOption" @click="moreOptionClick">
                        <span>{{ $t("questions.more") }}...</span>
                      </div>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'weekly'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option
                        :label="`Every ${
                          dynamicValidateForm.dayofweek
                            .charAt(0)
                            .toUpperCase() +
                          dynamicValidateForm.dayofweek.slice(1)
                        }`"
                        :value="dynamicValidateForm.dayofweek"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'monthly'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option
                        :label="`First ${
                          dynamicValidateForm.dayofweek
                            .charAt(0)
                            .toUpperCase() +
                          dynamicValidateForm.dayofweek.slice(1)
                        } of each month`"
                        value="week1"
                      ></el-option>
                      <el-option
                        :label="`Second ${
                          dynamicValidateForm.dayofweek
                            .charAt(0)
                            .toUpperCase() +
                          dynamicValidateForm.dayofweek.slice(1)
                        } of each month`"
                        value="week2"
                      ></el-option>
                      <el-option
                        :label="`Third ${
                          dynamicValidateForm.dayofweek
                            .charAt(0)
                            .toUpperCase() +
                          dynamicValidateForm.dayofweek.slice(1)
                        } of each month`"
                        value="week3"
                      ></el-option>
                      <el-option
                        :label="`Fourth ${
                          dynamicValidateForm.dayofweek
                            .charAt(0)
                            .toUpperCase() +
                          dynamicValidateForm.dayofweek.slice(1)
                        } of each month`"
                        value="week4"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'quarterly'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option-group
                        v-for="group in options_quarterly"
                        :key="group.label"
                        :label="group.label"
                      >
                        <el-option
                          v-for="item in group.options"
                          :key="item.value"
                          :label="item.label"
                          :value="item.value"
                        ></el-option>
                      </el-option-group>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'biannually'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option-group
                        v-for="group in options_biannually"
                        :key="group.label"
                        :label="group.label"
                      >
                        <el-option
                          v-for="item in group.options"
                          :key="item.value"
                          :label="item.label"
                          :value="item.value"
                        ></el-option>
                      </el-option-group>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'yearly'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option-group
                        v-for="group in options_yearly"
                        :key="group.label"
                        :label="group.label"
                      >
                        <el-option
                          v-for="item in group.options"
                          :key="item.value"
                          :label="item.label"
                          :value="item.value"
                        ></el-option>
                      </el-option-group>
                    </el-select>
                  </el-form-item>
                </div>







                <!-- <div v-if="activeIndex == '1'" style="margin-left: -130px">
                  <el-form-item
                    :label="$t('global.type')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <div class="flex">
                      <div class="mr-6">
                        <el-select
                          v-model="dynamicValidateForm.category"
                          style="width: 100%"
                          placeholder="Select Dimension"
                        >
                          <el-option
                            v-for="item in optionsCategory"
                            :key="item.key"
                            :label="item.category"
                            :value="item.key"
                          ></el-option>
                        </el-select>
                      </div>
                      <div class="ml-6">
                        <el-select
                          v-model="dynamicValidateForm.type"
                          style="width: 100%"
                          :placeholder="$t('questions.Question_Format')"
                          @change="onChangeQuestionsType(dynamicValidateForm.type)"
                        >
                          <el-option
                            :label="$t('questions.open_ended')"
                            value="Text Area Field"
                          ></el-option>
                          <el-option
                            :label="$t('questions.yes_or_no')"
                            value="Multiple Choice"
                          ></el-option>
                          <el-option
                            :label="$t('questions.multiple_choice')"
                            value="Multiple Choice(other)"
                          ></el-option>
                          <el-option label="Scale" value="Scale"></el-option>
                        </el-select>
                      </div>
                    </div>
                  </el-form-item>
                  <el-form-item
                    :label="$t('questions.question')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-input
                      v-model="dynamicValidateForm.question_en"
                      auto-complete="off"
                      :placeholder="
                        questionPlaceholder(dynamicValidateForm.type, '')
                      "
                    ></el-input>
                  </el-form-item>
                  <div v-if="dynamicValidateForm.type == 'Multiple Choice'">
                    <el-form-item
                      :label-width="formLabelWidth"
                      style="margin-bottom: 12px"
                      v-for="(options, index) in dynamicValidateForm.options_en"
                      :label="
                        options.placeholder + ' ' + $t('questions.option')
                      "
                      class="title-input-form"
                      :key="options.key"
                      :prop="'options_en.' + index + '.value'"
                    >
                      <div class="flex">
                        <div class="mr-6">
                          <el-input
                            v-model="options.value"
                            auto-complete="off"
                            readonly
                          ></el-input>
                        </div>
                        <div class="ml-6">
                          <div v-if="index == 0">
                            <el-select
                              v-model="dynamicValidateForm.feelingY"
                              style="width:95%"
                              @change="onChangeY(dynamicValidateForm.feelingY)"
                            >
                              <el-option
                                :label="$t('questions.positive')"
                                value="Positive"
                              ></el-option>
                              <el-option
                                :label="$t('questions.neutral')"
                                value="Neutral"
                              ></el-option>
                              <el-option
                                :label="$t('questions.negative')"
                                value="Negative"
                              ></el-option>
                            </el-select>
                          </div>
                          <div v-else>
                            <el-select
                              v-model="dynamicValidateForm.feelingN"
                              style="width:95%"
                              @change="onChangeN(dynamicValidateForm.feelingN)"
                            >
                              <el-option
                                :label="$t('questions.positive')"
                                value="Positive"
                              ></el-option>
                              <el-option
                                :label="$t('questions.neutral')"
                                value="Neutral"
                              ></el-option>
                              <el-option
                                :label="$t('questions.negative')"
                                value="Negative"
                              ></el-option>
                            </el-select>
                          </div>
                        </div>
                        <div>
                          <el-button 
                          :style="`width:40px;padding: 12px 3px;${getstylefollowUp(options)}`"
                          @click="getfollow_up_question(index,options)">
                            O->
                          </el-button>
                        </div>
                      </div>
                    </el-form-item>
                  </div>
                  <div
                    v-if="dynamicValidateForm.type == 'Multiple Choice(other)'"
                  >
                    <el-form-item
                      :label-width="formLabelWidth"
                      style="margin-bottom: 12px"
                      v-for="(options, index) in dynamicValidateForm.options"
                      :label="$t('questions.option') + ' ' + increase(index)"
                      class="title-input-form"
                      :key="options.key"
                    >
                      <div class="flex">
                        <div class="mr-6">
                          <div v-if="index < 2">
                            <el-input
                              auto-complete="off"
                              v-model="options.en"
                              :placeholder="'Option ' + increase(index)"
                            >
                            </el-input>
                          </div>
                          <div v-else style="width: 172.2px">
                            <el-input
                              auto-complete="off"
                              v-model="options.en"
                              :placeholder="'Option ' + increase(index)"
                            >
                              <i
                                slot="suffix"
                                class="el-icon-remove-outline"
                                @click="deleteOptions(options)"
                              ></i>
                            </el-input>
                          </div>
                        </div>
                        <div class="ml-6">
                          <el-select
                            v-model="options.feeling"
                            style="width:95%"
                            :placeholder="$t('questions.assign_a_sentiment')"
                          >
                            <el-option
                              :label="$t('questions.positive')"
                              value="Positive"
                            ></el-option>
                            <el-option
                              :label="$t('questions.neutral')"
                              value="Neutral"
                            ></el-option>
                            <el-option
                              :label="$t('questions.negative')"
                              value="Negative"
                            ></el-option>
                          </el-select>
                        </div>

                        
                        <div>
                          <el-button 
                          :style="`width:40px;padding: 12px 3px;${getstylefollowUp(options)}`"
                          @click="getfollow_up_question(index,options)">
                            O->
                          </el-button>
                        </div>
                      </div>

                    </el-form-item>
                    <el-form-item
                      :label-width="formLabelWidth"
                      style="margin-bottom: 12px"
                    >
                      <el-button class="btnAddOption" @click="addOptions">{{
                        $t("questions.add_option")
                      }}</el-button>
                    </el-form-item>
                  </div>
                  <div v-if="dynamicValidateForm.type == 'Scale'">
                    <el-form-item
                      :label-width="formLabelWidth"
                      style="margin-bottom: 12px"
                      :label="$t('questions.min_label')"
                      class="title-input-form"
                    >
                      <el-col :span="13">
                        <el-input
                          auto-complete="off"
                          v-model="dynamicValidateForm.less_txt_en"
                          placeholder="Not at all"
                        >
                        </el-input>
                      </el-col>
                      <el-col :span="1">&nbsp;</el-col>
                      <el-col :span="10">
                        <el-input
                          auto-complete="off"
                          :disabled="true"
                          v-model="dynamicValidateForm.min"
                        >
                        </el-input>
                      </el-col>
                    </el-form-item>
                    <el-form-item
                      :label-width="formLabelWidth"
                      style="margin-bottom: 12px"
                      :label="$t('questions.max_label')"
                      class="title-input-form"
                    >
                      <el-col :span="13">
                        <el-input
                          auto-complete="off"
                          placeholder="Very important"
                          v-model="dynamicValidateForm.max_txt_en"
                        >
                        </el-input>
                      </el-col>
                      <el-col :span="1">&nbsp;</el-col>
                      <el-col :span="10">
                        <el-input
                          auto-complete="off"
                          :disabled="true"
                          v-model="dynamicValidateForm.max"
                        >
                        </el-input>
                      </el-col>
                    </el-form-item>
                  </div>
                  <el-form-item
                    :label="$t('questions.reward')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-input
                      v-model="dynamicValidateForm.coins"
                      type="number"
                      :min="0"
                      auto-complete="off"
                      style="width: 100%"
                      :placeholder="$t('questions.coins')"
                    >
                      <template slot="append">{{ $t("questions.coins") }}</template>
                    </el-input>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.type == 'Text Area Field'"
                    :label="$t('questions.power_skill')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.powerSkill"
                      style="width: 100%"
                      :placeholder="$t('questions.select_power_skill')"
                      clearable
                    >
                      <el-option
                        :label="$t('category.power_skill.empathy')"
                        value="ps_empathy"
                      ></el-option>
                      <el-option
                        :label="$t('category.power_skill.self_awareness')"
                        value="ps_self_awareness"
                      ></el-option>
                      <el-option
                        :label="$t('category.power_skill.leadership')"
                        value="ps_leadership"
                      ></el-option>
                      <el-option
                        :label="$t('category.power_skill.initiative_making')"
                        value="ps_initiative_making"
                      ></el-option>
                      <el-option
                        :label="$t('category.power_skill.optimism')"
                        value="ps_optimism"
                      ></el-option>
                      <el-option
                        :label="$t('category.power_skill.critical_thinking')"
                        value="ps_critical_thinking"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    :label="$t('questions.frequency')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.frequency"
                      style="width: 100%"
                      @change="scheduleChange"
                      :placeholder="$t('questions.how_often_do')"
                    >
                      <el-option
                        :label="$t('category.frequency.one_time')"
                        value="onetime"
                      ></el-option>
                      <el-option
                        :label="$t('category.frequency.weekly')"
                        value="weekly"
                      ></el-option>
                      <el-option
                        :label="$t('category.frequency.monthly')"
                        value="monthly"
                      ></el-option>
                      <el-option
                        :label="$t('category.frequency.quarterly')"
                        value="quarterly"
                      ></el-option>
                      <el-option
                        :label="$t('category.frequency.biannually')"
                        value="biannually"
                      ></el-option>
                      <el-option
                        :label="$t('category.frequency.yearly')"
                        value="yearly"
                      ></el-option>
                      <el-option
                        :label="$t('questions.follow_up')"
                        value="Follow-up"
                      ></el-option>
                      <el-option
                        :label="$t('questions.program_only')"
                        value="Program-only"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency!='Follow-up' && dynamicValidateForm.frequency!='Program-only'"
                    class="title-input-form"
                    :label="$t('questions.day_of_week')"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.dayofweek"
                      style="width: 100%"
                      @change="scheduleChange"
                      :placeholder="$t('questions.what_day_of')"
                    >
                      <el-option
                        :label="$t('category.day_of_week.monday')"
                        value="monday"
                      ></el-option>
                      <el-option
                        :label="$t('category.day_of_week.tuesday')"
                        value="tuesday"
                      ></el-option>
                      <el-option
                        :label="$t('category.day_of_week.wednesday')"
                        value="wednesday"
                      ></el-option>
                      <el-option
                        :label="$t('category.day_of_week.thursday')"
                        value="thursday"
                      ></el-option>
                      <el-option
                        :label="$t('category.day_of_week.friday')"
                        value="friday"
                      ></el-option>
                      <el-option
                        v-if="dataCompany && dataCompany.alldays == true"
                        :label="$t('category.day_of_week.saturday')"
                        value="saturday"
                      ></el-option>
                      <el-option
                        v-if="dataCompany && dataCompany.alldays == true"
                        :label="$t('category.day_of_week.sunday')"
                        value="sunday"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == ''"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      :no-data-text="$t('questions.please_select_frequency')"
                      style="width: 100%"
                      :placeholder="$t('questions.and_when')"
                    ></el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'onetime'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option
                        v-for="item in optionsScheduleOnetime"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value"
                      ></el-option>
                      <div class="styleMoreOption" @click="moreOptionClick">
                        <span>{{ $t("questions.more") }}...</span>
                      </div>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'weekly'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option
                        :label="`Every ${
                          dynamicValidateForm.dayofweek
                            .charAt(0)
                            .toUpperCase() +
                          dynamicValidateForm.dayofweek.slice(1)
                        }`"
                        :value="dynamicValidateForm.dayofweek"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'monthly'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option
                        :label="`First ${
                          dynamicValidateForm.dayofweek
                            .charAt(0)
                            .toUpperCase() +
                          dynamicValidateForm.dayofweek.slice(1)
                        } of each month`"
                        value="week1"
                      ></el-option>
                      <el-option
                        :label="`Second ${
                          dynamicValidateForm.dayofweek
                            .charAt(0)
                            .toUpperCase() +
                          dynamicValidateForm.dayofweek.slice(1)
                        } of each month`"
                        value="week2"
                      ></el-option>
                      <el-option
                        :label="`Third ${
                          dynamicValidateForm.dayofweek
                            .charAt(0)
                            .toUpperCase() +
                          dynamicValidateForm.dayofweek.slice(1)
                        } of each month`"
                        value="week3"
                      ></el-option>
                      <el-option
                        :label="`Fourth ${
                          dynamicValidateForm.dayofweek
                            .charAt(0)
                            .toUpperCase() +
                          dynamicValidateForm.dayofweek.slice(1)
                        } of each month`"
                        value="week4"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'quarterly'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option-group
                        v-for="group in options_quarterly"
                        :key="group.label"
                        :label="group.label"
                      >
                        <el-option
                          v-for="item in group.options"
                          :key="item.value"
                          :label="item.label"
                          :value="item.value"
                        ></el-option>
                      </el-option-group>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'biannually'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option-group
                        v-for="group in options_biannually"
                        :key="group.label"
                        :label="group.label"
                      >
                        <el-option
                          v-for="item in group.options"
                          :key="item.value"
                          :label="item.label"
                          :value="item.value"
                        ></el-option>
                      </el-option-group>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'yearly'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option-group
                        v-for="group in options_yearly"
                        :key="group.label"
                        :label="group.label"
                      >
                        <el-option
                          v-for="item in group.options"
                          :key="item.value"
                          :label="item.label"
                          :value="item.value"
                        ></el-option>
                      </el-option-group>
                    </el-select>
                  </el-form-item>
                </div>
                <div v-if="activeIndex == '2'" style="margin-left: -130px">
                  <el-form-item
                    :label="$t('global.type')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <div class="flex">
                      <div class="mr-6">
                        <el-select
                          v-model="dynamicValidateForm.category"
                          style="width: 100%"
                          placeholder="Select Dimension"
                        >
                          <el-option
                            v-for="item in optionsCategory"
                            :key="item.key"
                            :label="item.category"
                            :value="item.key"
                          ></el-option>
                        </el-select>
                      </div>
                      <div class="ml-6">
                        <el-select
                          v-model="dynamicValidateForm.type"
                          style="width: 100%"
                          :placeholder="$t('questions.Question_Format')"
                          @change="onChangeQuestionsType(dynamicValidateForm.type)"
                        >
                          <el-option
                            :label="$t('questions.open_ended')"
                            value="Text Area Field"
                          ></el-option>
                          <el-option
                            :label="$t('questions.yes_or_no')"
                            value="Multiple Choice"
                          ></el-option>
                          <el-option
                            :label="$t('questions.multiple_choice')"
                            value="Multiple Choice(other)"
                          ></el-option>
                          <el-option label="Scale" value="Scale"></el-option>
                        </el-select>
                      </div>
                    </div>
                  </el-form-item>
                  <el-form-item
                    class="title-input-form"
                    :label="$t('questions.question')"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-input
                      v-model="dynamicValidateForm.question_th"
                      auto-complete="off"
                      :placeholder="
                        questionPlaceholder(dynamicValidateForm.type, '(Thai)')
                      "
                    ></el-input>
                  </el-form-item>
                  <div v-if="dynamicValidateForm.type == 'Multiple Choice'">
                    <el-form-item
                      :label-width="formLabelWidth"
                      style="margin-bottom: 12px"
                      v-for="(options, index) in dynamicValidateForm.options_th"
                      :label="options.placeholder + ' Option'"
                      class="title-input-form"
                      :key="options.key"
                      :prop="'options_th.' + index + '.value'"
                    >
                      <div class="flex">
                        <div class="mr-6" :span="12">
                          <el-input
                            v-model="options.value"
                            auto-complete="off"
                            readonly
                          ></el-input>
                        </div>
                        <div class="ml-6">
                          <div class="ml-6"></div>
                          <div v-if="index == 0">
                            <el-select
                              v-model="dynamicValidateForm.feelingY"
                              style="width: 95%"
                              @change="onChangeY(dynamicValidateForm.feelingY)"
                            >
                              <el-option
                                :label="$t('questions.positive')"
                                value="Positive"
                              ></el-option>
                              <el-option
                                :label="$t('questions.neutral')"
                                value="Neutral"
                              ></el-option>
                              <el-option
                                :label="$t('questions.negative')"
                                value="Negative"
                              ></el-option>
                            </el-select>
                          </div>
                          <div v-else>
                            <el-select
                              v-model="dynamicValidateForm.feelingN"
                              style="width: 95%"
                              @change="onChangeN(dynamicValidateForm.feelingN)"
                            >
                              <el-option
                                :label="$t('questions.positive')"
                                value="Positive"
                              ></el-option>
                              <el-option
                                :label="$t('questions.neutral')"
                                value="Neutral"
                              ></el-option>
                              <el-option
                                :label="$t('questions.negative')"
                                value="Negative"
                              ></el-option>
                            </el-select>
                          </div>
                        </div>
                        <div>
                          <el-button 
                          :style="`width:40px;padding: 12px 3px;${getstylefollowUp(options)}`"
                          @click="getfollow_up_question(index,options)">
                            O->
                          </el-button>
                        </div>
                      </div>
                    </el-form-item>
                  </div>
                  <div
                    v-if="dynamicValidateForm.type == 'Multiple Choice(other)'"
                  >
                    <el-form-item
                      :label-width="formLabelWidth"
                      style="margin-bottom: 12px"
                      v-for="(options, index) in dynamicValidateForm.options"
                      :label="$t('questions.option') + ' ' + increase(index)"
                      class="title-input-form"
                      :key="options.key"
                    >
                      <div class="flex">
                        <div class="mr-6">
                          <div v-if="index < 2">
                            <el-input
                              auto-complete="off"
                              v-model="options.th"
                              :placeholder="
                                $t('questions.option') + ' ' + increase(index)
                              "
                            >
                            </el-input>
                          </div>
                          <div v-else style="width: 172.2px">
                            <el-input
                              auto-complete="off"
                              v-model="options.th"
                              :placeholder="
                                $t('questions.option') + ' ' + increase(index)
                              "
                            >
                              <i
                                slot="suffix"
                                class="el-icon-remove-outline"
                                @click="deleteOptions(options)"
                              ></i>
                            </el-input>
                          </div>
                        </div>
                        <div class="ml-6">
                          <el-select
                            v-model="options.feeling"
                            style="width: 95%"
                            :placeholder="$t('questions.assign_a_sentiment')"
                          >
                            <el-option
                              :label="$t('questions.positive')"
                              value="Positive"
                            ></el-option>
                            <el-option
                              :label="$t('questions.neutral')"
                              value="Neutral"
                            ></el-option>
                            <el-option
                              :label="$t('questions.negative')"
                              value="Negative"
                            ></el-option>
                          </el-select>
                        </div>

                        <div>
                          <el-button 
                          :style="`width:40px;padding: 12px 3px;${getstylefollowUp(options)}`"
                          @click="getfollow_up_question(index,options)">
                            O->
                          </el-button>
                        </div>
                      </div>
                    </el-form-item>
                    <el-form-item
                      :label-width="formLabelWidth"
                      style="margin-bottom: 12px"
                    >
                      <el-button class="btnAddOption" @click="addOptions">{{
                        $t("questions.add_option")
                      }}</el-button>
                    </el-form-item>
                  </div>
                  <div v-if="dynamicValidateForm.type == 'Scale'">
                    <el-form-item
                      :label-width="formLabelWidth"
                      style="margin-bottom: 12px"
                      :label="$t('questions.min_label')"
                      class="title-input-form"
                    >
                      <el-col :span="13">
                        <el-input
                          auto-complete="off"
                          v-model="dynamicValidateForm.less_txt"
                          placeholder="Not at all"
                        >
                        </el-input>
                      </el-col>
                      <el-col :span="1">&nbsp;</el-col>
                      <el-col :span="10">
                        <el-input
                          auto-complete="off"
                          :disabled="true"
                          v-model="dynamicValidateForm.min"
                        >
                        </el-input>
                      </el-col>
                    </el-form-item>
                    <el-form-item
                      :label-width="formLabelWidth"
                      style="margin-bottom: 12px"
                      :label="$t('questions.max_label')"
                      class="title-input-form"
                    >
                      <el-col :span="13">
                        <el-input
                          auto-complete="off"
                          v-model="dynamicValidateForm.max_txt"
                          placeholder="Very important"
                        >
                        </el-input>
                      </el-col>
                      <el-col :span="1">&nbsp;</el-col>
                      <el-col :span="10">
                        <el-input
                          auto-complete="off"
                          :disabled="true"
                          v-model="dynamicValidateForm.max"
                        >
                        </el-input>
                      </el-col>
                    </el-form-item>
                  </div>
                  <el-form-item
                    :label="$t('questions.reward')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-input
                      v-model="dynamicValidateForm.coins"
                      type="number"
                      :min="0"
                      auto-complete="off"
                      style="width: 100%"
                      placeholder="Coins"
                      :label="$t('questions.coins')"
                    >
                      <template slot="append">{{
                        $t("questions.coins")
                      }}</template>
                    </el-input>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.type == 'Text Area Field'"
                    :label="$t('questions.power_skill')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.powerSkill"
                      style="width: 100%"
                      :placeholder="$t('questions.select_power_skill')"
                      clearable
                    >
                      <el-option
                        :label="$t('category.power_skill.empathy')"
                        value="ps_empathy"
                      ></el-option>
                      <el-option
                        :label="$t('category.power_skill.self_awareness')"
                        value="ps_self_awareness"
                      ></el-option>
                      <el-option
                        :label="$t('category.power_skill.leadership')"
                        value="ps_leadership"
                      ></el-option>
                      <el-option
                        :label="$t('category.power_skill.initiative_making')"
                        value="ps_initiative_making"
                      ></el-option>
                      <el-option
                        :label="$t('category.power_skill.optimism')"
                        value="ps_optimism"
                      ></el-option>
                      <el-option
                        :label="$t('category.power_skill.critical_thinking')"
                        value="ps_critical_thinking"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    :label="$t('questions.frequency')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.frequency"
                      style="width: 100%"
                      @change="scheduleChange"
                      :placeholder="$t('questions.how_often_do')"
                    >
                      <el-option
                        :label="$t('category.frequency.one_time')"
                        value="onetime"
                      ></el-option>
                      <el-option
                        :label="$t('category.frequency.weekly')"
                        value="weekly"
                      ></el-option>
                      <el-option
                        :label="$t('category.frequency.monthly')"
                        value="monthly"
                      ></el-option>
                      <el-option
                        :label="$t('category.frequency.quarterly')"
                        value="quarterly"
                      ></el-option>
                      <el-option
                        :label="$t('category.frequency.biannually')"
                        value="biannually"
                      ></el-option>
                      <el-option
                        :label="$t('category.frequency.yearly')"
                        value="yearly"
                      ></el-option>
                      <el-option
                        :label="$t('questions.follow_up')"
                        value="Follow-up"
                      ></el-option>
                      <el-option
                        :label="$t('questions.program_only')"
                        value="Program-only"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency!='Follow-up' && dynamicValidateForm.frequency!='Program-only'"
                    :label="$t('questions.day_of_week')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.dayofweek"
                      style="width: 100%"
                      @change="scheduleChange"
                      :placeholder="$t('questions.what_day_of')"
                    >
                      <el-option
                        :label="$t('category.day_of_week.monday')"
                        value="monday"
                      ></el-option>
                      <el-option
                        :label="$t('category.day_of_week.tuesday')"
                        value="tuesday"
                      ></el-option>
                      <el-option
                        :label="$t('category.day_of_week.wednesday')"
                        value="wednesday"
                      ></el-option>
                      <el-option
                        :label="$t('category.day_of_week.thursday')"
                        value="thursday"
                      ></el-option>
                      <el-option
                        :label="$t('category.day_of_week.friday')"
                        value="friday"
                      ></el-option>
                      <el-option
                        v-if="dataCompany && dataCompany.alldays == true"
                        :label="$t('category.day_of_week.saturday')"
                        value="saturday"
                      ></el-option>
                      <el-option
                        v-if="dataCompany && dataCompany.alldays == true"
                        :label="$t('category.day_of_week.sunday')"
                        value="sunday"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == ''"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      :no-data-text="$t('questions.please_select_frequency')"
                      style="width: 100%"
                      :placeholder="$t('questions.and_when')"
                    ></el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'onetime'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option
                        v-for="item in optionsScheduleOnetime"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value"
                      ></el-option>
                      <div class="styleMoreOption" @click="moreOptionClick">
                        <span>{{ $t("questions.more") }}...</span>
                      </div>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'weekly'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option
                        :label="`Every ${
                          dynamicValidateForm.dayofweek
                            .charAt(0)
                            .toUpperCase() +
                          dynamicValidateForm.dayofweek.slice(1)
                        }`"
                        :value="dynamicValidateForm.dayofweek"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'monthly'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option
                        :label="`First ${
                          dynamicValidateForm.dayofweek
                            .charAt(0)
                            .toUpperCase() +
                          dynamicValidateForm.dayofweek.slice(1)
                        } of each month`"
                        value="week1"
                      ></el-option>
                      <el-option
                        :label="`Second ${
                          dynamicValidateForm.dayofweek
                            .charAt(0)
                            .toUpperCase() +
                          dynamicValidateForm.dayofweek.slice(1)
                        } of each month`"
                        value="week2"
                      ></el-option>
                      <el-option
                        :label="`Third ${
                          dynamicValidateForm.dayofweek
                            .charAt(0)
                            .toUpperCase() +
                          dynamicValidateForm.dayofweek.slice(1)
                        } of each month`"
                        value="week3"
                      ></el-option>
                      <el-option
                        :label="`Fourth ${
                          dynamicValidateForm.dayofweek
                            .charAt(0)
                            .toUpperCase() +
                          dynamicValidateForm.dayofweek.slice(1)
                        } of each month`"
                        value="week4"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'quarterly'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option-group
                        v-for="group in options_quarterly"
                        :key="group.label"
                        :label="group.label"
                      >
                        <el-option
                          v-for="item in group.options"
                          :key="item.value"
                          :label="item.label"
                          :value="item.value"
                        ></el-option>
                      </el-option-group>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'biannually'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option-group
                        v-for="group in options_biannually"
                        :key="group.label"
                        :label="group.label"
                      >
                        <el-option
                          v-for="item in group.options"
                          :key="item.value"
                          :label="item.label"
                          :value="item.value"
                        ></el-option>
                      </el-option-group>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="dynamicValidateForm.frequency == 'yearly'"
                    :label="$t('questions.schedule')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <el-select
                      v-model="dynamicValidateForm.schedule"
                      style="width: 100%"
                    >
                      <el-option-group
                        v-for="group in options_yearly"
                        :key="group.label"
                        :label="group.label"
                      >
                        <el-option
                          v-for="item in group.options"
                          :key="item.value"
                          :label="item.label"
                          :value="item.value"
                        ></el-option>
                      </el-option-group>
                    </el-select>
                  </el-form-item>
                </div> -->



                <div v-if="dynamicValidateForm.frequency!='Follow-up' && dynamicValidateForm.frequency!='Program-only'">

                <!-- Response Settings Section (only show header if there will be a Report Card Analytics section) -->
                <div v-if="dynamicValidateForm.type === 'Multiple Choice' || dynamicValidateForm.type === 'Multiple Choice(other)' || !disabledRowHeader" class="fs-13 fw-700 fc-000000 opa-05 mb-16" style="margin-left: -24px; letter-spacing: 0.5px; text-transform: uppercase;">{{ $t("questions.response_settings") }}</div>

                <div class="flex mb-16" style="margin-left: -24px">
                  <div
                    class="relative switch-contrainer"
                  >
                    <input
                      type="checkbox"
                      id="rolling"
                      class="input-checkbox"
                      v-model="dynamicValidateForm.rolling"
                    />
                    <label for="rolling" class="lable-checkbox">{{
                      dynamicValidateForm.rolling
                    }}</label>
                  </div>
                  <div class="fs-14 fw-700 fc-000000 opa-07 mt-5 ml-12" style="position: relative">
                    {{ $t("questions.rolling_response") }}
                    <el-tooltip class="header-table-tooltip" placement="top">
                      <div slot="content" class="p-5">
                        <div style="max-width: 250px">
                          {{ $t("questions.rolling_response_tooltip") }}
                        </div>
                      </div>
                      <img :src="$utils('icons', 'ic_help_outline-16px')" class="ml-6 cursor-pointer" style="position: absolute; top: 1px;"
                alt="icons_ic_help_outline-16px" />
                    </el-tooltip>
                  </div>
                </div>

                <div class="flex mb-16" style="margin-left: -24px">
                  <div
                    class="relative switch-contrainer"
                  >
                    <input
                      type="checkbox"
                      id="showResultsInDaily"
                      class="input-checkbox"
                      v-model="dynamicValidateForm.showResultsInDaily"
                    />
                    <label for="showResultsInDaily" class="lable-checkbox">{{
                      dynamicValidateForm.showResultsInDaily
                    }}</label>
                  </div>
                  <div class="fs-14 fw-700 fc-000000 opa-07 mt-5 ml-12">
                    {{ dynamicValidateForm.showResultsInDaily ? $t("questions.submit_responses_to_managers") : $t("questions.private_feedback_mode") }}
                  </div>
                </div>
                <div class="mb-16" style="padding: 12px; background: rgba(0, 0, 0, 0.05); margin-left: -24px; margin-top: -16px; border-radius: 8px;" v-if="!dynamicValidateForm.showResultsInDaily">
                    {{ $t("questions.private_feedback_hint") }}
                </div>

                <!-- Report Card Analytics Section (only show header if there are multiple sections) -->
                <div v-if="dynamicValidateForm.type === 'Multiple Choice' || dynamicValidateForm.type === 'Multiple Choice(other)' || !disabledRowHeader" class="fs-13 fw-700 fc-000000 opa-05 mb-16 mt-32" style="margin-left: -24px; letter-spacing: 0.5px; text-transform: uppercase;">{{ $t("questions.report_card_analytics") }}</div>

                <div class="flex mb-16" style="margin-left: -24px" v-if="!disabledRowHeader">
                  <div
                    class="relative switch-contrainer"
                  >
                    <input
                      type="checkbox"
                      id="report_card"
                      class="input-checkbox"
                      v-model="dynamicValidateForm.report_card"
                      @change="generateRowHeader(dynamicValidateForm)"
                      :disabled="disabledRowHeader"
                    />
                    <label for="report_card" class="lable-checkbox">{{
                      dynamicValidateForm.report_card
                    }}</label>
                  </div>
                  <div class="fs-14 fw-700 fc-000000 opa-07 mt-5 ml-12">
                    {{ $t("questions.include_in_report_card") }}
                  </div>
                </div>
                
                <div style="margin-left: -130px" class="mb-16" v-if="!disabledRowHeader && dynamicValidateForm.report_card">
                  <el-form-item
                    :label="$t('questions.Row_Header')"
                    class="title-input-form"
                    :label-width="formLabelWidth"
                    style="margin-bottom: 12px"
                  >
                    <div class="" v-if="loadingRowHeader">
                      <loading-even></loading-even>
                    </div>
                    <el-input
                      v-else
                      auto-complete="off"
                      :placeholder="$t('questions.Row_Header_Placeholder')"
                      v-model="row_header"
                    ></el-input>

                  </el-form-item>
                </div>

                <div class="flex mb-16" style="margin-left: -24px" v-if="dynamicValidateForm.type === 'Multiple Choice' || dynamicValidateForm.type === 'Multiple Choice(other)' || dynamicValidateForm.type === 'Scale'">
                  <div
                    class="relative switch-contrainer"
                  >
                    <input
                      type="checkbox"
                      id="culture_index"
                      class="input-checkbox"
                      v-model="dynamicValidateForm.culture_index"
                    />
                    <label for="culture_index" class="lable-checkbox">{{
                      dynamicValidateForm.culture_index
                    }}</label>
                  </div>
                  <div class="fs-14 fw-700 fc-000000 opa-07 mt-5 ml-12" style="position: relative">
                    {{ $t("questions.include_in_culture_index") }}
                    <el-tooltip class="header-table-tooltip" placement="top">
                      <div slot="content" class="p-5">
                        <div style="max-width: 250px">
                          {{ $t("questions.culture_index_tooltip") }}
                        </div>
                      </div>
                      <img :src="$utils('icons', 'ic_help_outline-16px')" class="ml-6 cursor-pointer" style="position: absolute; top: 1px;"
                alt="icons_ic_help_outline-16px" />
                    </el-tooltip>
                  </div>
                </div>



                </div>

              </el-form>
              <span
                slot="footer"
                class="dialog-footer"
                style="margin-right: 32px; margin-left: 150px"
              >
                <el-button
                  class="btn-add bigger"
                  type="primary"
                  @click="saveQuestion"
                  :disabled="loadingRowHeader || isSavingQuestion"
                  >
                  <i v-if="isSavingQuestion" class="el-icon-loading"></i>
                  {{ isSavingQuestion ? $t("questions.saving_question") : $t("global.ADD") }}
                </el-button>
                <el-button class="btn-cancel bigger" @click="cancelDialog">{{
                  $t("global.CANCEL")
                }}</el-button>
              </span>
              </div>
            </div>
          </div>
        </el-dialog>
      </div>

      <div v-if="calendarModalMounted" id="calendarModal">
        <el-dialog
          class="txtHeaderModal"
          :title="$t('questions.CALENDAR_VIEW')"
          width="960px"
          :visible.sync="calendarModal"
          top="5vh"
        >
          <div class="center mt-20 mb-40 pt-20 pb-40" v-if="loadingCalendar">
            <loading-even></loading-even>
          </div>
          <div v-else>
            <div class="body mt-26 fs-18 fw-700 fc-000000">
              <div class="flex">
                <div style="min-width: 120px">
                  <span class="mr-12"
                    >{{ calendarShowing.monthLabel }}
                    {{ calendarShowing.year }}</span
                  >
                </div>
                <img
                  @click="anotherMonthData(-1)"
                  src="../assets/images/questions/chevron_left@2x.png"
                  class="cursor-pointer mr-6"
                  style="height: 24px; width: 24px"
                />
                <img
                  @click="anotherMonthData(1)"
                  src="../assets/images/questions/chevron_right@2x.png"
                  class="cursor-pointer"
                  style="height: 24px; width: 24px"
                />
                <div
                  @click="refreshCalendar()"
                  :class="['ml-12', 'refresh-calendar-btn', { 'refreshing': isRefreshingCalendar, 'cursor-pointer': !isRefreshingCalendar }]"
                  style="height: 24px; width: 24px"
                  :title="isRefreshingCalendar ? $t('questions.refreshing') : $t('questions.refresh_calendar_title')"
                >
                  <happily-icon name="refresh" :size="22" :spin="isRefreshingCalendar" />
                </div>
                <div class="legend-container">
                  <div class="legend-item more-than-five">
                    <div class="legend-box"></div>
                    <div class="legend-item-label">{{ $t("questions.legend_heavy_load") }}</div>
                  </div>
                  <div class="legend-item less-than-four">
                    <div class="legend-box"></div>
                    <div class="legend-item-label">{{ $t("questions.legend_light_load") }}</div>
                  </div>
                  <div class="legend-item open-ended-count">
                    <div class="legend-count-badge">
                      <img src="../assets/images/questions/ic_gray_openended@2x.png" class="legend-badge-icon" alt="" />
                      <span class="legend-badge-count">1</span>
                    </div>
                    <div class="legend-item-label">{{ $t("questions.legend_open_ended_questions") }}</div>
                  </div>
                </div>
              </div>
              <div class="calendar-container">
                <div class="day-of-week-title">
                  {{ $t("category.day_of_week_m.mon") }}
                </div>
                <div class="day-of-week-title">
                  {{ $t("category.day_of_week_m.tue") }}
                </div>
                <div class="day-of-week-title">
                  {{ $t("category.day_of_week_m.wed") }}
                </div>
                <div class="day-of-week-title">
                  {{ $t("category.day_of_week_m.thu") }}
                </div>
                <div class="day-of-week-title">
                  {{ $t("category.day_of_week_m.fri") }}
                </div>
                <div class="day-of-week-title">
                  {{ $t("category.day_of_week_m.sat") }}
                </div>
                <div class="day-of-week-title">
                  {{ $t("category.day_of_week_m.sun") }}
                </div>
                <div
                  v-for="(data, index) in calendarData"
                  :key="`${data.dayOfMonth}${index}_card`"
                  class="day-card"
                >
                  <input
                    type="radio"
                    :id="`${data.dayOfMonth}_${data.id}`"
                    :value="`${data.dayOfMonth}_${data.id}`"
                    v-model="selectedDay"
                  />
                  <label :for="`${data.dayOfMonth}_${data.id}`">
                    <div class="day-box" :class="{ 'more-than-five': data.questionList && data.questionList.length > 5, 'less-than-four': data.questionList && data.questionList.length < 4, 'has-recent-question': calendarDayHasRecentQuestion(data) }">
                      <div
                        v-if="getOpenEndedBadgeCount(data) > 0"
                        class="open-ended-count-badge"
                        :class="{ 'count-warning': getOpenEndedBadgeCount(data) > 1 }"
                      >
                        <img
                          v-if="selectedDay == `${data.dayOfMonth}_${data.id}`"
                          src="../assets/images/questions/ic_white_openended@2x.png"
                          class="badge-icon"
                          alt=""
                        />
                        <img
                          v-else
                          src="../assets/images/questions/ic_gray_openended@2x.png"
                          class="badge-icon"
                          alt=""
                        />
                        <span class="badge-count">{{ getOpenEndedBadgeCount(data) }}</span>
                      </div>
                      <div class="title mb-4">{{ data.dayOfMonth }}</div>
                      <div
                        v-for="(question, index) in data.questionList"
                        :key="question.id"
                      >
                        <div v-if="index < 3">
                          <div
                            v-if="
                              selectedDay == `${data.dayOfMonth}_${data.id}`
                            "
                            class="questions-card ellipsis flex-center"
                            :class="{ 'calendar-question-disabled': isCalendarQuestionDisabled(data, question) }"
                          >
                            <img
                              v-if="question.questionsType == 'Scale'"
                              src="../assets/images/questions/ic_white_scale@2x.png"
                              style="width: 10px; height: 10px"
                              alt=""
                            />
                            <img
                              v-else-if="question.questionsType == 'Yes/No'"
                              src="../assets/images/questions/ic_white_yesno@2x.png"
                              style="width: 10px; height: 10px"
                              alt=""
                            />
                            <img
                              v-else-if="question.questionsType == 'Open-ended'"
                              src="../assets/images/questions/ic_white_openended@2x.png"
                              style="width: 10px; height: 10px"
                              alt=""
                            />
                            <img
                              v-else
                              src="../assets/images/questions/ic_white_multichoice@2x.png"
                              style="width: 10px; height: 10px"
                              alt=""
                            />
                            <span class="opa-06 ellipsis">
                              {{ question.category }} {{ question.powerskill }}
                            </span>
                          </div>
                          <div
                            v-else
                            class="questions-card ellipsis flex-center"
                            :class="{ 'calendar-question-disabled': isCalendarQuestionDisabled(data, question) }"
                          >
                            <img
                              v-if="question.questionsType == 'Scale'"
                              src="../assets/images/questions/ic_gray_scale@2x.png"
                              style="width: 10px; height: 10px"
                              alt=""
                            />
                            <img
                              v-else-if="
                                question.questionsType == 'Yes/No'
                              "
                              src="../assets/images/questions/ic_gray_yesno@2x.png"
                              style="width: 10px; height: 10px"
                              alt=""
                            />
                            <img
                              v-else-if="
                                question.questionsType == 'Open-ended'
                              "
                              src="../assets/images/questions/ic_gray_openended@2x.png"
                              style="width: 10px; height: 10px"
                              alt=""
                            />
                            <img
                              v-else
                              src="../assets/images/questions/ic_gray_multichoice@2x.png"
                              style="width: 10px; height: 10px"
                              alt=""
                            />
                            <span class="opa-06 ellipsis">
                              {{ question.category }} {{ question.powerskill }}
                            </span>
                          </div>
                        </div>
                        <div
                          v-else-if="index == 3"
                          class="questions-card ellipsis"
                        >
                          <span class="opa-06">
                            + {{ data.questionList.length - 3 }}
                            {{ $t("questions.more") }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              <div class="line-shadow"></div>
              <div class="fc-000000 fs-18 fw-700 mt-24 mb-6">
                {{ questionPreviewText }}
              </div>
              <div>
                <div class="flex-row" style="margin-top: 25px;margin-bottom: 10px;">
                  <div  style="width: 30px"></div>
                  <div class="flex-column fw-700 fs-14 fc-4A4A4A" style="width: 70px">{{ $t("questions.Status") }}</div>
                  <div class="flex-column fw-700 fs-14 fc-4A4A4A" style="width: 440px">{{ $t("questions.Question") }}</div>
                  <div class="flex-column fw-700 fs-14 fc-4A4A4A" style="width: 140px">{{ $t("questions.dimension") }}</div>
                  <div class="flex-column fw-700 fs-14 fc-4A4A4A" style="width: 140px">{{ $t("questions.delivery") }}</div>
                  <div class="flex-column fw-700 fs-14 fc-4A4A4A" style="width: 100px">{{ $t("questions.Action") }}</div>
                </div>
                <draggable v-model="tableSelectedDay" group="people" @start="drag=true" @end="drag=false" class="list-group" @update="updateCradList">
                    <div class="list-group-item" :class="{ 'is-recent-question': item.id === recentlyAddedQuestionId }" style="width: 100%"
                      v-for="(item, index) in tableSelectedDay"
                      :key="item.id">
                        <div class='flex-row' style="min-height:50px;padding-top:15px;">
                          <div class="opa-08 pt-2 mr-8" style="cursor: grab; cursor: move;">
                              <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M9 19.23q-.508 0-.87-.36T7.77 18t.36-.87t.87-.36t.87.36t.36.87t-.36.87t-.87.36m6 0q-.508 0-.87-.36t-.36-.87t.36-.87t.87-.36t.87.36t.36.87t-.36.87t-.87.36m-6-6q-.508 0-.87-.36T7.77 12t.36-.87t.87-.36t.87.36t.36.87t-.36.87t-.87.36m6 0q-.508 0-.87-.36t-.36-.87t.36-.87t.87-.36t.87.36t.36.87t-.36.87t-.87.36m-6-6q-.508 0-.87-.36T7.77 6t.36-.87t.87-.36t.87.36t.36.87t-.36.87t-.87.36m6 0q-.508 0-.87-.36T13.77 6t.36-.87t.87-.36t.87.36t.36.87t-.36.87t-.87.36"/></svg>
                            </div>
                            <div style="width: 70px; padding-top: 4px;">
                                <el-tooltip
                                  :content="item.notblock ? $t('questions.essential_question_tooltip') : $t('questions.toggle_off_tooltip')"
                                  placement="top"
                                >
                                  <el-switch
                                    v-model="item.status"
                                    active-color="#13ce66"
                                    inactive-color="#ff4949"
                                    :disabled="item.notblock"
                                    @change="handleBlock(item)"
                                  ></el-switch>
                                </el-tooltip>
                            </div>
                            <div class="fs-14 flex-column fw-700 fc-000000" style="width: 440px;">
                              <span style="padding-right:16px; word-break: keep-all; margin-bottom: 6px; display: block;">{{ item.question_en }}
                                <el-tooltip v-if="hasFollowUpQuestions(item)" effect="dark" placement="top">
                                  <div slot="content">
                                    <div v-for="(followUp, index) in getFollowUpQuestionsText(item)" :key="index" style="margin-bottom: 8px;">
                                      <strong>{{ followUp.options.join(', ') }}:</strong><br>
                                      {{ followUp.text }}
                                    </div>
                                  </div>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="9" viewBox="0 0 21.5 9.121" style="display: inline-block; vertical-align: middle; margin-left: 8px;">
                                    <g transform="translate(-1.25 -7.439)">
                                      <circle cx="3" cy="3" r="3" transform="translate(2 9)" fill="none" stroke="#6C30CC" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
                                      <path d="M22,12H12m10,0c0,.562-.438.99-1.314,1.844L19.029,15.5M22,12c0-.562-.438-.99-1.314-1.844L19.03,8.5" fill="none" stroke="#6C30CC" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
                                    </g>
                                  </svg>
                                </el-tooltip>
                              </span>
                              <div class="question-meta-badges">
                                <span class="question-type-badge" :class="getQuestionTypeBadgeClass(item.type)">
                                  {{ item.questionsType }}
                                </span>
                                <span v-if="item.power_skill" class="power-skill-badge">
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="vertical-align: middle; margin-right: 2px;">
                                    <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" fill="currentColor"/>
                                  </svg>
                                  {{ item.power_skill }}
                                </span>
                              </div>
                            </div>
                            <div style="width: 140px; line-height: 16px;">
                              <div class="fs-14 fw-600" style="color: #000000; margin-bottom: 6px;">{{ item.category }} </div>
                              <span v-if="item.repeats && !item.repeats.toLowerCase().includes('follow')" 
                                    class="frequency-tag" 
                                    :class="getFrequencyTagClass(item.repeats)">
                                {{ item.repeats =='Weekly'&&(item.repeat_every?Number(item.repeat_every) >1:false) ? $t('questions.bi_weekly') : item.repeats }}
                              </span>
                            </div>
                            
                            <div style="width: 140px; line-height: 16px;">
                              <el-tooltip v-if="( !blurMode )"
                              :content="item.delivery" placement="top">
                                <div class="fs-14 fw-600 ellipsis" style="color: #4A4A4A; margin-bottom: 6px;">{{ item.delivery }} </div>
                              </el-tooltip>
                              <span class="frequency-tag tag-default">{{ item.audience }}</span>
                            </div>

                            <div>
                              <el-button
                                v-if="
                                  (item.type == 'Multiple Choice' &&
                                    item.m_choice !== undefined &&
                                    item.m_choice.en.length === 2) ||
                                  item.type == 'Text Area Field' ||
                                  item.type == 'Multiple Choice(other)' ||
                                  item.custom_type == 'multiple_choice' ||
                                  item.type == 'Scale'
                                "
                                class="btnActionView fs-15 btn-gray-outline"
                                @click="openModal(item)"
                                >{{ $t("questions.View") }}</el-button
                                >
                              <el-button v-else disabled style="background: #f4f4f5">{{
                                $t("questions.View")
                              }}</el-button>


                            </div>

                            
                        </div>

                    </div>
                    <!-- <div v-for="element in tableSelectedDay" :key="element.id">{{element.question_en}}</div> -->
                </draggable>



                <!-- <el-table
                  :data="tableSelectedDay"
                  :cell-style="{ padding: '6px 0' }"
                  fixed
                  empty-text=" "
                  style="width: 100%"
                >
                  <el-table-column
                    class="headerCenter"
                    :label="$t('questions.Status')"
                    prop="block"
                    width="100"
                    min-width="50"
                    align="center"
                  >
                    <template slot-scope="scope">
                      <el-switch
                        @change="handleBlock(scope.row)"
                        v-model="scope.row.status"
                        active-color="#13ce66"
                        inactive-color="#ff4949"
                        :disabled="scope.row.notblock"
                      ></el-switch>
                    </template>
                  </el-table-column>
                  <el-table-column
                    :label="$t('questions.Question')"
                    prop="question_en"
                  >
                    <template slot-scope="scope">
                      <div class="flex-column">
                        <span
                          class="fw-700 fs-14 fc-4A4A4A"
                          style="word-break: normal"
                          >{{ scope.row.question_en }}</span
                        >
                        <span class="fw-400 fs-14 fc-9D9D9D">{{
                          scope.row.questionsType
                        }} {{ scope.row.power_skill }}</span>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column
                    :label="$t('questions.Category')"
                    prop="category"
                    width="130"
                  >
                    <template slot-scope="scope">
                      <div class="fw-700 fs-14 fc-4A4A4A">
                        {{ scope.row.category }} 
                      </div>
                      <div class="fw-400 fs-14 fc-9D9D9D">
                        {{ scope.row.repeats }}
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column
                    :label="$t('questions.Audience')"
                    prop="audience"
                    width="130"
                  >
                    <template slot-scope="scope">
                      <div class="fw-700 fs-14 fc-4A4A4A">All</div>
                      <div class="fw-400 fs-14 fc-9D9D9D">
                        {{ scope.row.audience }}
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column
                    :label="$t('questions.Action')"
                    prop="action"
                    header-align="center"
                    width="130"
                    min-width="65"
                    align="center"
                  >
                    <template slot-scope="scope">
                      <el-button
                        v-if="
                          (scope.row.type == 'Multiple Choice' &&
                            scope.row.m_choice !== undefined &&
                            scope.row.m_choice.th.length === 2) ||
                          scope.row.type == 'Text Area Field' ||
                          scope.row.type == 'Multiple Choice(other)' ||
                          scope.row.custom_type == 'multiple_choice' ||
                          scope.row.type == 'Scale'
                        "
                        class="btnActionView fs-15 btn-gray-outline"
                        @click="openModal(scope.row)"
                        >{{ $t("questions.View") }}</el-button
                      >
                      <el-button v-else disabled style="background: #f4f4f5">{{
                        $t("questions.View")
                      }}</el-button>
                    </template>
                  </el-table-column>
                </el-table> -->
              </div>
            </div>
          </div>
        </el-dialog>
      </div>

      <div v-if="questionFormMounted" class="remove-padding-modal">
        <el-dialog :visible.sync="recipientsModal" top="12vh" width="360px">
          <div class="RecipientsModalStyle px-40">
            <div>
              <span class="fs-20 fw-700 fc-000000">{{
                $t("questions.question_recipients")
              }}</span>
            </div>
            <div class="mt-24">
              <span class="fs-18 fw-700 fc-000000"
                >{{
                  $utils(
                    "addCommasInNumber",
                    dynamicValidateForm.recipients
                      ? dynamicValidateForm.recipients.length
                      : allUsers.length
                  )
                }}
                {{ $t("powerUp.recipients") }}</span
              >
            </div>
            <div class="mt-4 pb-16">
              <span class="fc-6C30CC fs-14">{{
                $t("powerUp.based_on_your_selection")
              }}</span>
            </div>
          </div>
          <div class="divMembersRecipients thin-scrollbar px-40">
            <div
              v-for="item in dynamicValidateForm.recipients"
              :key="item.key"
              class="rowDataStyle"
            >
              <div>
                <div class="divProfile">
                  <div v-if="item.profilepic">
                    <img
                      v-lazy="item.profilepic"
                      :key="item.profilepic"
                      class="imgProfile flex-mid"
                      :class="[ blurMode ?  'blurelement': '' ]"
                    />
                  </div>
                  <div v-else class="imgProfile"
                  :class="[ blurMode ?  'blurelement': '' ]">
                    <div
                      class="flex-mid text-in-image"
                      style="width: 42px"
                      :class="[
                        `${
                          item === 0 ? 'text-in-image-44' : 'text-in-image-32'
                        }`,
                      ]"
                    >
                      {{
                        $utils("acronymName", {
                          firstName: item.firstname,
                          lastName: item.lastname,
                        })
                      }}
                    </div>
                  </div>

                  <div class="flex-column pl-16">
                    <span class="text-name"
                    :class="[ blurMode ?  'blurelement': '' ]"
                      >{{ item.firstname }} {{ item.lastname }}</span
                    >
                    <span class="text-email"
                    :class="[ blurMode ?  'blurelement': '' ]">{{ item.email }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </el-dialog>
      </div>
    </div>
    <div v-if="parentDetailDialogsMounted">
    <el-dialog
      class="txtHeaderModal question-detail-dialog"
      width="960px"
      :visible.sync="dialogOpenEnded"
      top="7vh"
    >
      <div class="flex question-detail-header">
        <div>
          <div class="ml-20">
            <span class="question-detail-eyebrow">{{
              $t("questions.QUESTION_DETAILS")
            }}</span>
          </div>
          <div class="ml-20 mt-12">
            <span class="fw-700 fs-18 fc-000000 word-break">{{
              quest_name
            }}</span>
          </div>
          <div class="ml-20 mt-4">
            <span class="fc-626262 fs-14">{{
              $t("questions.open_ended")
            }}</span>
          </div>
        </div>
        <div class="ml-auto flex-center question-detail-actions" style="padding-top: 28px;">
          <!-- Team Filter Dropdown -->
          <div class="header-pill-dropdown" :class="`${selectedTeamFilter !== 'all' ? 'active' : ''}`">
            <el-dropdown trigger="click" @command="handleTeamFilterChange">
              <div class="flex-mid" style="color: inherit; font-weight: inherit;">
                <span v-if="selectedTeamFilter !== 'all'">
                  {{ (selectedTeamInfo && selectedTeamInfo.label) || $t("questions.team_label") }}
                  <svg v-if="selectedTeamInfo && selectedTeamInfo.hasChildren" class="aggregation-indicator" width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <title>{{ $t("questions.includes_child_teams") }}</title>
                    <path d="M8 2L2 8l6 6V10h6V6h-6V2z"/>
                  </svg>
                </span>
                <span v-else>{{ $t("questions.all_teams") }}</span>
                <i class="el-icon-arrow-down"></i>
              </div>
              <el-dropdown-menu slot="dropdown">
                <div class="team-dropdown-header">
                  <span class="dropdown-header-title">{{ $t("questions.filter_by_team") }}</span>
                </div>
                <div class="team-search-container">
                  <el-input
                    v-model="teamSearchQuery"
                    :placeholder="$t('questions.search_teams_placeholder')"
                    prefix-icon="el-icon-search"
                    size="mini"
                    clearable
                  ></el-input>
                </div>
                <div class="team-dropdown-content">
                  <el-dropdown-item
                    v-for="option in availableTeamOptions"
                    :key="option.value"
                    :command="option.value"
                    :class="{ 'selected-team': selectedTeamFilter === option.value }"
                  >
                    <div class="team-option-content">
                      <div class="team-option-main">
                        <span class="team-name">{{ option.label }}</span>
                        <span class="team-response-count">({{ option.responseCount }})</span>
                      </div>
                    </div>
                  </el-dropdown-item>
                  <div v-if="availableTeamOptions.length === 1" class="no-teams-found">
                    <span>{{ $t("questions.no_teams_found") }}</span>
                  </div>
                </div>
              </el-dropdown-menu>
            </el-dropdown>
          </div>

          <el-button type="text" class="header-icon-btn" @click="downloadExcel_Open_ended()" :disabled="loading">
            <div class="flex-mid">
              <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" class="icon-export">
                <path fill="currentColor"
                  d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z" />
              </svg>
              <div class="ml-6">{{ $t("global.DOWNLOAD") }}</div>
            </div>
          </el-button>
        </div>
      </div>
      <div class="question-detail-divider" />

      <div style="margin-top: 12px">
        <!-- Aggregation Info Message -->
        <div v-if="selectedTeamFilter !== 'all' && selectedTeamInfo && selectedTeamInfo.hasChildren"
             class="aggregation-info-banner">
          <i class="el-icon-info"></i>
          <span>{{ $t("questions.combined_responses_prefix") }} {{ selectedTeamInfo.label }} {{ $t("questions.combined_responses_suffix") }}</span>
        </div>

        <el-table
          id="openEndedTable"
          v-loading="loading"
          :data="filteredDataOpenEnd"
          :cell-style="{ padding: '6px 0px' }"
          :empty-text="loading ? ' ' : (selectedTeamFilter !== 'all' ? $t('questions.no_responses_for_team') : $t('questions.no_responses_available'))"
          height="520"
        >
          <el-table-column :render-header="renderHeaderOpenEnded">
            <template slot-scope="scope">
              <!-- <div class="center mt-20 mb-40 pt-20 pb-40" v-if="loadingFeedbackAI">
                <loading-even></loading-even>
              </div> -->
              <div
                v-if="scope.row.Summary"
                class="summaryLayout"
              >
                <div class="pt-16">
                  <span
                    class="txtHeaderSummary"
                    >{{ $t("questions.summary") }}
                  </span>
                </div>
                <div class="divAllSummary pb-12">
                  <span
                    class="txtAllSummary"
                    :style="$utils('fontStyle', scope.row.Summary)"
                    >{{ scope.row.Summary }}
                  </span>
                </div>
                <div style="margin-bottom: 8px; max-height: 164px; flex-wrap: wrap" class="flex divAllSummary">
                  <div class="talkingPointsBox" v-for="(item,index) in scope.row.response_talking_points_show.slice(0, 20)" :key="index">
                    <el-tooltip effect="light" placement="top" class="custom-tooltip">
                      <div class="tooltipStyleTalkingPoints" slot="content">
                        <span class="fs-13 fw-700">{{ item.point.title }}</span>
                        <div v-if="item.point && item.point.specific_examples">
                          <div class="tooltip-items" v-for="(item_specific,index_specific) in item.point.specific_examples" :key="index+'_'+index_specific">
                            <div class="tooltip-item">
                              <div class="oi-circle">
                                <span class="fs-11">{{ index_specific+1 }}</span>
                              </div>
                              <p>
                                {{ item_specific }}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <el-tag :class="`talking-points ${item.sentiment == 'positive'
                        ? 'GreenBoxStyle'
                        : item.sentiment == 'negative'
                          ? 'RedBoxStyle'
                          : 'GreyBoxStyle'
                        }`" v-bind:style="'margin-bottom: 8px;' +
                          $utils('fontStyle', item.point.title)
                          ">{{ truncateText(item.point.title) }}</el-tag>
                    </el-tooltip>
                  </div>
                </div>
                <div class="divAllSummary pb-16">
                  <span
                    class="txtRepeats"
                    >{{ $t("questions.ai_generated_note") }}
                  </span>
                </div>
              </div>
              <div v-else style="
                  padding: 24px 0;
                  background: rgba(0, 0, 0, 0.03);
                  align-items: flex-start;
                  display: flex;
                  flex-direction: column;
                ">
                <div class="feedback-team-label" v-if="scope.row.team" :class="getTeamColorClass(getTeamNameInfo(scope.row.team).teamId || scope.row.team)">
                  <span :class="[ blurMode ?  'blurelement': '' ]">
                    {{ getTeamNameInfo(scope.row.team).name }}
                  </span>
                </div>
                <span class="txtResponse" :style="$utils('fontStyle', scope.row.answer || scope.row)">{{ scope.row.answer || scope.row }}</span>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>

    <el-dialog
      class="txtHeaderModal question-detail-dialog"
      width="960px"
      :visible.sync="dialogYesNo"
      top="7vh"
    >
      <div class="flex question-detail-header">
        <div>
          <div class="ml-20">
            <span class="question-detail-eyebrow">{{
              $t("questions.QUESTION_DETAILS")
            }}</span>
          </div>
          <div class="ml-20 mt-12">
            <span class="fw-700 fs-18 fc-000000 word-break">{{
              quest_name
            }}</span>
          </div>
          <div class="ml-20 mt-4">
            <span class="fc-626262 fs-14">{{ $t("questions.Yes_or_No") }}</span>
          </div>
        </div>
        <div class="ml-auto question-detail-actions" style="padding-top: 28px">
          <el-button
            type="text"
            class="header-icon-btn"
            @click="downloadExcel_YesorNO()"
            :disabled="loading"
          >
            <div class="flex-mid">
              <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            class="icon-export"
          >
            <path
              fill="currentColor"
              d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z"
            />
          </svg>
              <div class="ml-6">{{ $t("global.DOWNLOAD") }}</div>
            </div>
          </el-button>
        </div>
      </div>
      <div class="question-detail-divider" />

      <div class="mt-12">
        <el-table
          id="yesNoTable"
          v-loading="loading"
          :data="datalist"
          :cell-style="{ padding: '0' }"
          :empty-text="loading ? ' ' : $t('questions.no_responses_available')"
          ref="yesNoTable"
          style="width: 100%"
          height="520"
        >
          <el-table-tree-column
            file-icon="icon icon-folder"
            folder-icon="icon icon-folder"
            :allRemote="true"
            expandKey="extended"
            child-key="children"
            width="52"
            height="50"
          ></el-table-tree-column>

          <el-table-column :render-header="renderHeaderYesNo">
            <template slot-scope="scope">
              <div
                style="flex-direction: row; display: flex; margin: 5px 0;"
              >
                <div class="rowItem flex-center">
                  <div class="flex-column">
                  <span class="txtTeam"
                  :class="[ blurMode ?  'blurelement': '' ]"
                   :style="{ marginLeft: `${16 + (scope.row.depth * 16)}px` }">
                    {{ scope.row.team }}
                  </span>
                    <span v-if="scope.row.id && datamultichoices[scope.row.id]"
                      class="fs-12 fw-500 fc-9A9A9A lh-16"
                      :style="{ marginLeft: `${16 + (scope.row.depth * 16)}px` }">
                      {{ getTotalResponses(scope.row.id) }} {{ $t("questions.responses_label") }}
                    </span>
                  </div>
                </div>
                <div v-if="scope.row.id" style="margin-left: 20px">
                  <div
                    class="flex"
                    style="width: 577px; border-radius: 4px; overflow: hidden"
                  >
                    <div
                      :class="
                        !yes ? 'bar-results color-4' : 'bar-results color-2'
                      "
                      v-if="scope.row.id"
                      :style="`${getColorPercent(getNoPercent(scope.row.id))}`"
                    >
                      <span v-if="getNoPercent(scope.row.id) > 5"
                        >{{ getNoPercent(scope.row.id) }}%</span
                      >
                    </div>
                    <div
                      v-if="scope.row.id"
                      :class="
                        yes ? 'bar-results color-4' : 'bar-results color-2'
                      "
                      :style="`${getColorPercent(getYesPercent(scope.row.id))}`"
                    >
                      <span v-if="getYesPercent(scope.row.id) > 5"
                        >{{ getYesPercent(scope.row.id) }}%</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>

    <el-dialog
      class="txtHeaderModal question-detail-dialog"
      width="960px"
      :visible.sync="dialogScale"
      top="7vh"
    >
      <div class="flex question-detail-header">
        <div>
          <div class="ml-20">
            <span class="question-detail-eyebrow">{{
              $t("questions.QUESTION_DETAILS")
            }}</span>
          </div>
          <div class="ml-20 mt-12">
            <span class="fw-700 fs-18 fc-000000 word-break">{{
              quest_name
            }}</span>
          </div>
          <div class="ml-20 mt-4">
            <span class="fc-626262 fs-14">{{ $t("questions.Scale") }}</span>
          </div>
        </div>
        <div class="ml-auto question-detail-actions" style="padding-top: 28px">
          <el-button
            type="text"
            class="header-icon-btn"
            @click="downloadExcel_Scale()"
            :disabled="loading"
          >
            <div class="flex-mid">
              <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            class="icon-export"
          >
            <path
              fill="currentColor"
              d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z"
            />
          </svg>
              <div class="ml-6">{{ $t("global.DOWNLOAD") }}</div>
            </div>
          </el-button>
        </div>
      </div>
      <div class="question-detail-divider" />

      <div class="mt-12">
        <el-table
          id="scaleTable"
          v-loading="loading"
          :data="datalist"
          :cell-style="{ padding: '0' }"
          :empty-text="loading ? ' ' : $t('questions.no_responses_available')"
          ref="scaleTable"
          style="width: 100%"
          height="520"
        >
          <el-table-tree-column
            file-icon="icon icon-folder"
            folder-icon="icon icon-folder"
            :allRemote="true"
            expandKey="extended"
            child-key="children"
            width="52"
            height="50"
          ></el-table-tree-column>

          <el-table-column :render-header="renderHeaderScale">
            <template slot-scope="scope">
              <div
                style="flex-direction: row; display: flex; margin: 5px 0;"
              >
                <div class="rowItem flex-center">
                  <div class="flex-column">
                  <span class="txtTeam"
                  :class="[ blurMode ?  'blurelement': '' ]"
                   :style="{ marginLeft: `${16 + (scope.row.depth * 16)}px` }">
                    {{ scope.row.team }}
                  </span>
                    <span v-if="scope.row.id && datamultichoices[scope.row.id]"
                      class="fs-12 fw-500 fc-9A9A9A lh-16"
                      :style="{ marginLeft: `${16 + (scope.row.depth * 16)}px` }">
                      {{ getTotalResponses(scope.row.id) }} {{ $t("questions.responses_label") }}
                    </span>
                  </div>
                </div>
                <div
                  style="width: 40px"
                  class="ml-16 fs-17 fw-700 fc-000000 opa-07 flex-mid"
                >
                  <div
                    v-if="scope.row.id && datamultichoices[scope.row.id]"
                    class="ml-auto"
                  >
                    {{ datamultichoices[scope.row.id].avg }}
                  </div>
                </div>
                <div
                  v-if="scope.row.id && datamultichoices[scope.row.id]"
                  style="margin-left: 12px"
                >
                  <div class="flex" :style="`width: 537px; border-radius: 4px; overflow: hidden`">
                    <div
                      v-for="(item, index) in datamultichoices[scope.row.id]
                        .range"
                      :key="index"
                      :class="`bar-results color-` + getScaleColor(index)"
                      :style="`${getColorPercentForScale(
                        getScalePercent(scope.row.id, index)
                      )}`"
                    >
                      <span v-if="getScalePercent(scope.row.id, index) > 5"
                        >{{ getScalePercent(scope.row.id, index) }}%</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>

    <el-dialog
      class="txtHeaderModal question-detail-dialog"
      width="960px"
      :visible.sync="dialogMultipleOther"
      top="7vh"
    >
      <div class="flex question-detail-header">
        <div>
          <div class="ml-20">
            <span class="question-detail-eyebrow">{{
              $t("questions.QUESTION_DETAILS")
            }}</span>
          </div>
          <div class="ml-20 mt-12">
            <span class="fw-700 fs-18 fc-000000 word-break">{{
              quest_name
            }}</span>
          </div>
          <div class="ml-20 mt-4">
            <span class="fc-626262 fs-14">{{
              $t("questions.Multiple_Choice")
            }}</span>
          </div>
        </div>
        <div class="ml-auto question-detail-actions" style="padding-top: 28px">
          <el-button
            type="text"
            class="header-icon-btn"
            @click="downloadExcel_MultipleChoice()"
            :disabled="loading"
          >
            <div class="flex-mid">
              <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            class="icon-export"
          >
            <path
              fill="currentColor"
              d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z"
            />
          </svg>
              <div class="ml-6">{{ $t("global.DOWNLOAD") }}</div>
            </div>
          </el-button>
        </div>
      </div>
      <div class="question-detail-divider" />

      <div class="mt-12">
        <el-table
          id="otherTable"
          v-loading="loading"
          :data="datalist"
          :cell-style="{ padding: '0' }"
          :empty-text="loading ? ' ' : $t('questions.no_responses_available')"
          ref="otherTable"
          style="width: 100%"
          height="520"
        >
          <el-table-tree-column
            file-icon="icon icon-folder"
            folder-icon="icon icon-folder"
            :allRemote="true"
            expandKey="extended"
            child-key="children"
            width="52"
            height="50"
          ></el-table-tree-column>

          <el-table-column :render-header="renderHeaderMultipleOther">
            <template slot-scope="scope">
              <div
                style="flex-direction: row; display: flex; margin: 5px 0;"
              >
                <div class="rowItem flex-center">
                  <div class="flex-column">
                  <span class="txtTeam"
                  :class="[ blurMode ?  'blurelement': '' ]"
                   :style="{ marginLeft: `${16 + (scope.row.depth * 16)}px` }">
                    {{ scope.row.team }}
                  </span>
                    <span v-if="scope.row.id && datamultichoices[scope.row.id]"
                      class="fs-12 fw-500 fc-9A9A9A lh-16"
                      :style="{ marginLeft: `${16 + (scope.row.depth * 16)}px` }">
                      {{ getTotalResponses(scope.row.id) }} {{ $t("questions.responses_label") }}
                    </span>
                  </div>
                </div>
                <div v-if="scope.row.id" class="ml-20">
                  <div
                    class="flex"
                    style="width: 577px; border-radius: 4px; overflow: hidden;"
                  >
                    <div
                      v-for="(item, index) in datamultichoices[scope.row.id]"
                      :key="index"
                      :style="`width: ${getMultipleChoicePercent(scope.row.id, index)}%`"
                    >
                      <div
                        v-if="getMultipleChoicePercent(scope.row.id, index) > 6"
                        :class="
                          `bar-results color-` + getMultipleChoiceColor(index)
                        "
                        :style="`width: 100%;`"
                      >
                        <span
                          >{{
                            getMultipleChoicePercent(scope.row.id, index)
                          }}%</span
                        >
                      </div>
                      <el-tooltip
                        v-else-if="
                          getMultipleChoicePercent(scope.row.id, index)
                        "
                        :content="`${getMultipleChoicePercent(
                          scope.row.id,
                          index
                        )}%`"
                        placement="top"
                      >
                        <div
                          :class="
                            `bar-results color-` + getMultipleChoiceColor(index)
                          "
                          :style="`width: 100%;`"
                        >
                          &nbsp;
                        </div>
                      </el-tooltip>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>
    </div>

    <!-- follow up question -->
    <el-dialog
    v-if="questionFormMounted"
    class="txtHeaderModal"
      width="720px"
      :visible.sync="follow_up_question"
      
    >
    <div class="flex-column">
      <div class="fs-20 fw-900">{{ $t("questions.add_follow_up_question") }}</div>
      <div class="fs-18 fw-900 mt-24 mb-29" style="margin-bottom:29px">{{titleName}}</div>
      <div>

        <el-select
          v-model="selectquestionFollewUp"
          filterable
          :placeholder="$t('questions.select_placeholder')"
          class="input-style"
          style="width:500px;"
        >
          <el-option
            class="text-dropdown"
            v-for="item in questionFollewUp"
            :key="item.key"
            :label="item.question_en"
            :value="item.key"
          ></el-option>
        </el-select>

      </div>
      <div class="mt-32" style="margin-bottom:40px">
        <el-button
          type="primary"
          @click="addFollowUpQuestion"
          class="btn-add bigger">
          {{ $t("questions.SUBMIT") }}</el-button>
        <el-button
          @click="addFollowUpQuestion"
          class="btn-cancel bigger">
          {{ $t("global.CANCEL") }}</el-button>
      </div>
    </div>
    </el-dialog>

  </div>
</template>

<script>
import format from "date-fns/format";
import endOfMonth from "date-fns/end_of_month";
import later from "later";
import draggable from "vuedraggable";
import firebase from "firebase/app";
import "firebase/firestore";
import IntensityCadencePicker from "@/components/intensity/IntensityCadencePicker.vue";
import { CADENCE_TARGETS, adminBiasToCadence, cadenceToAdminBias } from "@/store/intensity-mapping";

// This calendar is built around local month/day selections, not UTC boundaries.
later.date.localTime();

export default {
  name: "question",
  created() {
    this.$store.commit("authen/setActiveIndex", "4");
    this.setInitialTabFromRoute();
    this.initializeQuestionsView();
    this.$store.dispatch("intensity/getIntensityState");
  },
  components: {
    draggable: draggable,
    standard: require("./questions/standard").default,
    custom: () => import("./questions/custom.vue"),
    loadingEven: require("./even/loading").default,
    IntensityCadencePicker,
  },
  computed: {
    engonly() {
      return this.$store.getters["reward/engonly"];
    },
    user() {
      const user = this.$store.getters["authen/user"];
      return user;
    },
    optionsCategory() {
      return this.$store.getters["question/category"];
    },
    optionsScheduleOnetime() {
      return this.$store.getters["question/scheduleonetime"];
    },
    questions() {
      return this.$store.getters["question/questions"];
    },
    questionsAll() {
      return this.$store.getters["question/questionsall"];
    },
    questionsAllCustom() {
      return this.$store.getters["custom/questioncustomAll"];
    },
    loading() {
      return this.$store.getters["question/loading"];
    },
    loadingCalendar() {
      return this.$store.getters["question/loadingCalendar"];
    },
    loadingRowHeader() {
      return this.$store.getters["question/loadingRowHeader"];
    },
    datalist() {
      return this.$store.getters["question/datalist"];
    },
    optionTo() {
      // this.dynamicValidateForm.cascaderTo=this.optionTo.map((row) => [row.value]);
      // this.dynamicValidateForm.oldCascaderTo=this.optionTo.map((row) => [row.value]);
      return this.$store.getters["question/optionTo"];
    },
    recipientOptions() {
      const leadershipScoped = typeof this.teamleadership === "string" && this.teamleadership !== "";
      const leadershipOptions = Array.isArray(this.optionToleadership) ? this.optionToleadership : [];
      const defaultOptions = Array.isArray(this.optionTo) ? this.optionTo : [];
      const selectedBaseOptions = Array.isArray(this.option_To) ? this.option_To : [];
      const baseOptions = selectedBaseOptions && selectedBaseOptions.length
        ? this.option_To
        : (leadershipScoped ? leadershipOptions : defaultOptions);
      const allowedTeamIds = leadershipScoped
        ? leadershipOptions.map((option) => option.value)
        : [];
      const users = (this.allUsers || []).filter((user) => {
        return !leadershipScoped || allowedTeamIds.indexOf(user.team) !== -1;
      });
      const userOptions = users
        .filter((user) => user && user.email)
        .map((user) => {
          const firstName = user.firstname || "";
          const lastName = user.lastname || "";
          const fullName = `${firstName} ${lastName}`.trim();
          return {
            ...user,
            label: fullName || user.email,
            value: `user_${user.email}`,
            type: "user",
            toType: "user",
          };
        });

      return _.orderBy(_.uniqBy([...(baseOptions || []), ...userOptions], "value"), ["label"], ["asc"]);
    },
    allUsers() {
      return this.$store.getters["question/allUsers"];
    },
    cohortsUser() {
      return this.$store.getters["question/cohortsUser"];
    },
    _organization1level() {
      return this.$store.getters["question/organization1level"];
    },
    userTeam() {
      return this.$store.getters["question/userTeam"];
    },
    teamArr() {
      return this.$store.getters["question/teamArr"];
    },
    teamAll() {
      return this.$store.getters["question/teamAll"];
    },
    organizations() {
      return this.$store.getters["question/organizations"];
    },
    answerOpt() {
      return this.$store.getters["question/answerOpt"];
    },
    dateShow() {
      return this.$store.getters["question/dateShow"];
    },
    dateoptions() {
      return this.$store.getters["question/dateoptions"];
    },
    dataopenend() {
      return this.$store.getters["question/dataopenend"];
    },
    datamultichoices() {
      return this.$store.getters["question/datamultichoices"];
    },
    countmultichoices() {
      return this.$store.getters["question/countmultichoices"];
    },
    deletedTeam(){
      return this.$store.getters["question/deletedTeam"];
    },
    teamHeight(){
      return this.$store.getters["question/teamHeight"];
    },
    loadingFeedbackAI() {
      return this.$store.getters["question/loadingFeedbackAI"];
    },
    summaryDetail(){
      return this.$store.getters["question/summaryDetail"];
    },
    dataCompany(){
      return this.$store.getters["question/dataCompany"];
    },
    intensityCompanyState() {
      return this.$store.state.intensity && this.$store.state.intensity.companyState;
    },
    intensityAdminBias() {
      return this.$store.getters["intensity/adminBias"];
    },
    hasConfiguredIntensity() {
      return !!(this.intensityCompanyState && this.intensityCompanyState.id);
    },
    hasLoadedIntensityState() {
      return !!this.intensityCompanyState;
    },
    needsInitialIntensitySelection() {
      return this.hasLoadedIntensityState && !this.hasConfiguredIntensity;
    },
    currentIntensityCadence() {
      if (!this.hasConfiguredIntensity) return null;
      return adminBiasToCadence(this.intensityAdminBias);
    },
    currentIntensityCadenceLabel() {
      if (!this.hasLoadedIntensityState) return "Loading";
      if (this.needsInitialIntensitySelection) return "Not set";
      const cadence = this.currentIntensityCadence;
      return cadence.charAt(0).toUpperCase() + cadence.slice(1);
    },
    intensityLevelIndex() {
      const map = { light: 1, balanced: 2, intense: 3 };
      return map[this.currentIntensityCadence] || 0;
    },
    currentIntensityHeadline() {
      if (!this.hasLoadedIntensityState) {
        return "Loading intensity setting";
      }
      if (this.needsInitialIntensitySelection) {
        return "Choose a cadence to apply question caps";
      }
      const map = {
        light: '2–3 questions a day · 1 open-ended each week',
        balanced: '3–4 questions a day · 2 open-ended each week',
        intense: 'No caps — every question on its full schedule',
      };
      return map[this.currentIntensityCadence] || map.balanced;
    },
    intensityPanelHelperText() {
      if (!this.hasLoadedIntensityState) {
        return "Loading saved intensity before changes can be made.";
      }
      if (this.needsInitialIntensitySelection) {
        return "Choose an intensity to save this setting for the account.";
      }
      return "Changes apply immediately. You can update this any time.";
    },
    canAdjustIntensityFromHeader() {
      if (this.user && this.user.is_adviser) return true;
      const allowedRoles = ["admin", "hr", "superleader", "superadmin", "developer", "ghost", "advisor", "adviser"];
      const role = this.user && this.user.user_type
        ? String(this.user.user_type).toLowerCase()
        : "";
      return allowedRoles.includes(role);
    },
    headerIntensitySummary() {
      if (!this.hasLoadedIntensityState) return "Loading intensity setting";
      if (this.needsInitialIntensitySelection) return "Not set";
      const cadence = this.currentIntensityCadence;
      const targets = CADENCE_TARGETS[cadence] || {};
      const perDay = targets.questions_per_day
        ? `${targets.questions_per_day.min}-${targets.questions_per_day.max}/day`
        : "No cap/day";
      const openEnded = targets.open_ended_per_week === null || targets.open_ended_per_week === undefined
        ? "No cap open-ended"
        : `${targets.open_ended_per_week} open-ended/week`;
      return `${this.currentIntensityCadenceLabel} · ${perDay} · ${openEnded}`;
    },
    row_header: {
      get() {
        return this.$store.getters["question/row_header"];
      },
      set(value) {
        this.$store.commit("question/setRowHeader", value);
      },
    },
    questioncustomAll(){
      return this.$store.getters["custom/questioncustomAll"];
    },


    questionOrder: {
      get() {
        return this.$store.getters["question/questionOrder"];
      },
      set(value) {
        this.$store.commit("question/setquestionOrder", value);
      },
    },
    teamleadership(){
      
      return this.$store.getters["question/teamleadership"];
    },
    optionToleadership(){
      return this.$store.getters["question/optionToleadership"];
    },
    language() {
      return this.$store.getters["reward/language"];
    },
    getquestionLoading() {
      return this.$store.getters["custom/getquestionLoading"];
    },
    blurMode() {
      return this.$store.getters["demomode/blurMode"];
    },
    filteredDataOpenEnd() {
      if (this.selectedTeamFilter === 'all') {
        return this.dataopenend;
      }
      
      // Handle special 'organization' filter
      if (this.selectedTeamFilter === 'organization') {
        // Show all responses that don't have viable parent teams
        const teamIds = [...new Set(this.dataopenend
          .filter(item => !item.Summary && item.team)
          .map(item => item.team))];
        
        const organizationTeams = teamIds.filter(teamId => {
          const viableParent = this.findViableParentTeam(teamId);
          return viableParent && viableParent.teamId === 'organization';
        });
        
        return this.dataopenend.filter(item => {
          if (item.Summary) return true; // Always show summary
          return organizationTeams.includes(item.team);
        });
      }
      
      // Get selected team option to find child teams
      const selectedOption = this.availableTeamOptions.find(option => option.value === this.selectedTeamFilter);
      if (!selectedOption) return this.dataopenend;
      
      // Include the selected team and all its child teams (including those that don't meet threshold)
      const includeTeamIds = [this.selectedTeamFilter];
      if (selectedOption.childTeams) {
        includeTeamIds.push(...selectedOption.childTeams);
      }
      // Also include all descendants of the selected team
      includeTeamIds.push(...this.getAllDescendantTeams(this.selectedTeamFilter));
      
      return this.dataopenend.filter(item => {
        if (item.Summary) return true; // Always show summary
        return includeTeamIds.includes(item.team);
      });
    },
    availableTeamOptions() {
      const options = [{ value: 'all', label: 'All Teams', responseCount: this.dataopenend.filter(item => !item.Summary).length }];
      
      // Get unique teams from current dataset
      const teamIds = [...new Set(this.dataopenend
        .filter(item => !item.Summary && item.team)
        .map(item => item.team))];
      
      // Group teams by their viable parent (the closest ancestor that meets threshold)
      const viableTeams = new Map();
      
      teamIds.forEach(teamId => {
        const viableParent = this.findViableParentTeam(teamId);
        if (viableParent) {
          if (!viableTeams.has(viableParent.teamId)) {
            viableTeams.set(viableParent.teamId, {
              ...viableParent,
              childTeams: [],
              aggregatedResponses: 0
            });
          }
          
          const responseCount = this.getTeamResponseCount(teamId);
          viableTeams.get(viableParent.teamId).aggregatedResponses += responseCount;
          
          if (teamId !== viableParent.teamId) {
            viableTeams.get(viableParent.teamId).childTeams.push(teamId);
          }
        }
      });
      
      // Convert to options format
      viableTeams.forEach((teamData, teamId) => {
        // Only add teams that meet the threshold
        if (teamData.aggregatedResponses >= this.MIN_RESPONSES_FOR_TEAM_NAME) {
          options.push({
            value: teamId,
            label: teamData.label,
            responseCount: teamData.aggregatedResponses,
            memberCount: teamData.memberCount,
            meetsThreshold: true, // All teams in this list meet threshold
            hasChildren: teamData.childTeams.length > 0,
            depth: teamData.depth,
            childTeams: teamData.childTeams
          });
        }
      });
      
      // Filter by search query
      const filtered = options.filter(option => {
        if (option.value === 'all') return true;
        return option.label.toLowerCase().includes(this.teamSearchQuery.toLowerCase());
      });
      
      return filtered.sort((a, b) => {
        if (a.value === 'all') return -1;
        if (b.value === 'all') return 1;
        return a.label.localeCompare(b.label);
      });
    },
    selectedTeamInfo() {
      if (this.selectedTeamFilter === 'all') {
        return null;
      }
      return this.availableTeamOptions.find(option => option.value === this.selectedTeamFilter);
    },
    // Multi-language support
    isThaiLanguageSelected() {
      const selectedLanguage = localStorage.getItem('selectedLanguage') || 'en';
      return selectedLanguage === 'th';
    },
    
    isMalayLanguageSelected() {
      const selectedLanguage = localStorage.getItem('selectedLanguage') || 'en';
      return selectedLanguage === 'ms';
    },
    
    isChineseLanguageSelected() {
      const selectedLanguage = localStorage.getItem('selectedLanguage') || 'en';
      return selectedLanguage === 'zh-HK';
    },
    
    // Dynamic font class based on language selection
    questionFontClass() {
      const selectedLanguage = localStorage.getItem('selectedLanguage') || 'en';
      if (selectedLanguage === 'th') return 'font-kanit';
      if (selectedLanguage === 'zh-HK') return 'font-chinese';
      return 'font-lato';
    },
    
    localizedAnswerOptions() {
      const selectedLanguage = localStorage.getItem('selectedLanguage') || 'en';
      
      if (!this.currentQuestionData || !this.currentQuestionData.m_choice) {
        return this.answerOpt; // Fallback to store data
      }
      
      // Check for selected language first
      if (this.currentQuestionData.m_choice[selectedLanguage]) {
        // For Yes/No questions, check if we need to reverse the order
        // if (this.currentQuestionData.m_choice.weight && (this.currentQuestionData.m_choice.weight[0] === "0" || this.currentQuestionData.m_choice.weight[0] === 0)) {
        //   return [...this.currentQuestionData.m_choice[selectedLanguage]].reverse();
        // }
        return this.currentQuestionData.m_choice[selectedLanguage];
      }
      
      // Fallback to English
      if (this.currentQuestionData.m_choice.en) {
        // if (this.currentQuestionData.m_choice.weight && (this.currentQuestionData.m_choice.weight[0] === "0" || this.currentQuestionData.m_choice.weight[0] === 0)) {
        //   return [...this.currentQuestionData.m_choice.en].reverse();
        // }
        return this.currentQuestionData.m_choice.en;
      }
      
      return this.answerOpt; // Final fallback
    },
    localizedAnswerOptionsOther() {
      const selectedLanguage = localStorage.getItem('selectedLanguage') || 'en';
      
      if (!this.currentQuestionData || !this.currentQuestionData.m_choice) {
        return this.answerOpt; // Fallback to store data
      }
      
      // Check for selected language first
      if (this.currentQuestionData.m_choice[selectedLanguage]) {
        // For Yes/No questions, check if we need to reverse the order
        // This must not be a special question. // weihth 0,1,1,0
        if (this.currentQuestionData.key !== '-L1FmpFO-v8IZc_B5EB7' && this.currentQuestionData.m_choice.weight && (this.currentQuestionData.m_choice.weight[0] === "0" || this.currentQuestionData.m_choice.weight[0] === 0)) {
          return [...this.currentQuestionData.m_choice[selectedLanguage]].reverse();
        }
        return this.currentQuestionData.m_choice[selectedLanguage];
      }
      
      // Fallback to English
      if (this.currentQuestionData.m_choice.en) {
        // This must not be a special question. // weihth 0,1,1,0
        if (this.currentQuestionData.key !== '-L1FmpFO-v8IZc_B5EB7' && this.currentQuestionData.m_choice.weight && (this.currentQuestionData.m_choice.weight[0] === "0" || this.currentQuestionData.m_choice.weight[0] === 0)) {
          return [...this.currentQuestionData.m_choice.en].reverse();
        }
        return this.currentQuestionData.m_choice.en;
      }
      
      return this.answerOpt; // Final fallback
    },
    // Preview panel — normalize the form's question type into one of 5 buckets
    // that drive the per-type preview card layout in the Add/Edit Question modal.
    previewType() {
      var type = this.dynamicValidateForm && this.dynamicValidateForm.type;
      if (type === "Multiple Choice") return "yesno";
      if (type === "Multiple Choice(other)") return "multi";
      if (type === "Scale") return "scale";
      if (type === "Text Area Field") return "open";
      return "empty";
    },
    // Top option label for MC(other): pulls from the form's first English option,
    // falling back to a sensible placeholder so the preview always has a caption.
    previewTopOptionLabel() {
      if (this.previewType === "yesno") {
        var y = this.dynamicValidateForm && this.dynamicValidateForm.options_en && this.dynamicValidateForm.options_en[0];
        return (y && (y.value || y.placeholder)) || "yes";
      }
      if (this.previewType === "multi") {
        var m = this.dynamicValidateForm && this.dynamicValidateForm.options && this.dynamicValidateForm.options[0];
        return (m && (m.en || m.placeholder)) || "your top choice";
      }
      return "";
    },
    // Question text shown in the preview card title. Prefers the English draft;
    // falls back to the localized field.
    previewQuestionText() {
      var v = this.dynamicValidateForm;
      if (!v) return "";
      return v.question_en || (v.questions && v.questions.en) || "";
    },
  },
  watch: {
    row_header() {
      
      this.dynamicValidateForm.report_card_label = this.row_header
    },
    selectedDay() {
      this.addDataToTableOfModal();
    },
    async activeName() {
      if (this.activeName != "") {
        const tab = this.activeName || "";
        this.renderedTabs[tab] = true;
        await this.initializeTab(tab);
        const paramsTab = this.$router.currentRoute.params.tab;
        if (paramsTab != tab) {
          this.$router.push({ name: "questions", params: { tab } });
        }
      }
    },
    dialogOpenEnded(newVal) {
      if (newVal) {
        // Clear cache when dialog opens
        this._teamNameCache = {};
        // Reset team filter
        this.selectedTeamFilter = 'all';
        // Clear team search
        this.teamSearchQuery = '';
      }
    },
    dataopenend() {
      // Clear cache when data changes
      this._teamNameCache = {};
    },
    async user(newUser) {
      if (newUser && !this.viewReady) {
        await this.initializeQuestionsView();
      }
    },
    language(){
      if(this.language.length>0){

       
        for(const i in this.language){
        // console.log('language',this.language[i].code)
        if(!this.dynamicValidateForm.questions[`${this.language[i].code}`]){
          this.dynamicValidateForm.questions[`${this.language[i].code}`] = ''
        }
        if(this.dynamicValidateForm.less_txts&&!this.dynamicValidateForm.less_txts[`${this.language[i].code}`]){
          this.dynamicValidateForm.less_txts[`${this.language[i].code}`] = ''
        }
        if(this.dynamicValidateForm.max_txt&&!this.dynamicValidateForm.max_txts[`${this.language[i].code}`]){
          this.dynamicValidateForm.max_txts[`${this.language[i].code}`] = ''
        }
        if(this.dynamicValidateForm.options_YN&&!this.dynamicValidateForm.options_YN[`${this.language[i].code}`]&&this.language[i].code!='en'&&this.language[i].code!='th'){
          const arr=[
          {
            key: 1,
            value: "",
            placeholder: "Yes",
            question_id:'',
          },
          {
            key: 2,
            value: "",
            placeholder: "No",
            question_id:'',
          },
        ]
        //this.$set(this.dynamicValidateForm.options_YN, this.language[i].code, JSON.parse(JSON.stringify(arr)));
        //this.dynamicValidateForm.options_YN[`${this.language[i].code}`]=arr;
          this.dynamicValidateForm.options_YN[`${this.language[i].code}`]=JSON.parse(JSON.stringify(arr));
          //this.dynamicValidateForm[`options_${this.language[i].code}`]=JSON.parse(JSON.stringify(arr));
        }
        //console.log("Ya",this.dynamicValidateForm)
        }

      }
      
    },
 
  },
  data() {
    return {
      activeName: "standard",
      anchor: {
        standard: "standard",
        custom: "custom",
      },
      renderComponent: true,
      mode: "",
      dialogFormVisible: false,
      calendarModal: false,
      isRefreshingCalendar: false,
      isSavingQuestion: false,
      questionSaveSuccess: false,
      savedQuestionSummary: null,
      recentlyAddedQuestionId: null,
      isOpeningSavedQuestionCalendar: false,
      title_dialog: "",
      activeIndex: "en",
      formLabelWidth: "140px",
      status: "Y",
      recipientsModal: false,
      dialogOpenEnded: false,
      dialogYesNo: false,
      dialogScale: false,
      dialogMultipleOther: false,
      quest_id: null,
      quest_name: null,
      organization1level: [],
      follow_up_question:false,
      selectquestionFollewUp:'',
      questionFollewUp:[],
      indexOption:0,
      // optionTo: [
      //   {
      //     label: "All teams and cohorts",
      //     value: "asdfqwrqw213",
      //   },
      //   {
      //     label: "Executive Board",
      //     value: "asd2qwr2q1w13",
      //   },
      //   {
      //     label: "Purchasing & Sourcing BU",
      //     value: "as3dfqw2rq3213",
      //   },
      //   {
      //     label: "Bulky Logistics Squad",
      //     value: "sdfqqwrdsqw213",
      //   },
      //   {
      //     label: "Happily Culture Builders",
      //     value: "a1d43q2wr2w213",
      //   },
      //   {
      //     label: "Super Outer",
      //     value: "a1d4fq2wq2w2113",
      //   },
      //   {
      //     label: "Superstar",
      //     value: "as1d3qwrqw113",
      //   },
      //   {
      //     label: "0 to 6mo",
      //     value: "as143q2w113",
      //   },
      //   {
      //     label: "10+ yrs",
      //     value: "as1d43rq2w1133",
      //   },
      //   {
      //     label: "2 to 5yrs",
      //     value: "as1d43rq2w11232",
      //   },
      //   {
      //     label: "5 to 10yrs",
      //     value: "as1d43rq2w11131",
      //   },
      // ],
      //cascaderTo: [["asdfqwrqw213"]], // add to obj : dynamicValidateForm
      //showResultsInDaily: true, // add to obj : dynamicValidateForm
      dynamicValidateForm: {
        cascaderTo: [["all_teams_and_cohorts"]],
        oldCascaderTo: [["all_teams_and_cohorts"]],
        recipients: [],
        showResultsInDaily: true,
        category: "",
        type: "",
        questions: {},
        question_en: "",
        question_th: "",
        coins: "1",
        frequency: "",
        dayofweek: "monday",
        schedule: "",
        feelingY: "Positive",
        feelingN: "Negative",
        powerSkill: null,
        options_YN:{},
        options_en: [
          {
            key: 1,
            value: "Yes",
            weight: "1",
            placeholder: "Yes",
            question_id:'',
          },
          {
            key: 2,
            value: "No",
            weight: "0",
            placeholder: "No",
            question_id:'',
          },
        ],
        options_th: [
          {
            key: 1,
            value: "ใช่",
            placeholder: "Yes",
            question_id:'',
          },
          {
            key: 2,
            value: "ไม่ใช่",
            placeholder: "No",
            question_id:'',
          },
        ],
        options: [
          {
            key: this.$utils("mockingID", 15),
            th: "",
            en: "",
            feeling: "Positive",
            question_id:'',
          },
          {
            key: this.$utils("mockingID", 15),
            th: "",
            en: "",
            feeling: "Neutral",
            question_id:'',
          },
          {
            key: this.$utils("mockingID", 15),
            th: "",
            en: "",
            feeling: "Negative",
            question_id:'',
          },
        ],
        less_txts: {},
        less_txt: "",
        less_txt_en: "",
        max_txts: {},
        max_txt: "",
        max_txt_en: "",
        max: 10,
        min: 0,
        rolling:false,
        report_card: false,
        report_card_label: "",
        culture_index: false,
        status: "Y",
      },
      options_quarterly: [],
      options_biannually: [],
      options_yearly: [],
      calendarShowing: {
        day: 1, // 1-31
        dayLabel: "Mon",
        month: 1, // 1-12
        monthLabel: "January",
        year: "2010",
        firstDayOfWeek: 0, // 1 - 7 (1 = Mon)
        lastDayOfPastMonth: 30,
        lastDayOfMonth: 30,
      },
      tableSelectedDay: [],
      selectedDay: null,
      calendarData: [],
      questionPreviewText: "",
      questionsIntensityPopoverVisible: false,
      savingHeaderIntensity: false,
      syncingIntensityBlocks: false,
      intensityBlockSyncSignature: "",
      skipIntensityBlockSyncOnce: false,
      // Undo state for the Check-in Intensity change.
      // Captured at the moment the admin taps a new cadence — holds the
      // prior cadence and a snapshot of each question's block_status so we
      // can restore the exact prior question state if they click Undo
      // before the 8-second window expires.
      pendingIntensityRevert: null, // { cadence, questions: [{key, block_status}], previousLabel } | null
      pendingIntensityRevertTimer: null,
      pendingIntensityRevertExpiresAt: 0,
      undoingIntensityChange: false,
      questionsPerMonth: {},
      dialogQuestionForm: false,
      refreshInput: true,
      propsCofig: { multiple: true },
      firstrun: true,
      disabledRowHeader: true,
      titleName:'',
      option_To:[],
      questions_text:'',
      // Anonymity thresholds - adjust these values as needed
      MIN_RESPONSES_FOR_TEAM_NAME: 5, // Minimum responses required for team visibility
      MIN_MEMBERS_FOR_TEAM_NAME: 5, // Minimum members (when member check is enabled)
      USE_MEMBER_COUNT_CHECK: false, // Set to true when member counts are available
      _teamNameCache: {},
      // Team filtering for modals
      selectedTeamFilter: 'all',
      teamFilterOptions: [],
      teamSearchQuery: '',
      // Store current question data for localization
      currentQuestionData: null,
      renderedTabs: {
        standard: false,
        custom: false,
      },
      questionFormMounted: false,
      calendarModalMounted: false,
      parentDetailDialogsMounted: false,
      tabReady: {
        standard: false,
        custom: false,
      },
      viewReady: false,
      audienceReady: false,
      formReady: false,
      detailReady: false,
      calendarReady: false,
      viewInitPromise: null,
      audiencePromise: null,
      formPromise: null,
      detailPromise: null,
      calendarPromise: null,
      backgroundWarmupScheduled: false,
      backgroundWarmupCompleted: false,
      backgroundWarmupIdleHandle: null,
      backgroundWarmupTimeoutHandle: null,
    };
  },
  mounted() {
    this.getDateData(new Date());
    const params = this.$router.currentRoute.params.tab;
    if (this.$route.query.welcome == "true") {
      this.$router.push({
        name: "questions",
        params: { tab: "standard" },
        query: { welcome: "false" },
      });
    }
    if (params && this.anchor[params] && !this.$route.query.welcome) {
      this.activeName = params;
    } else if (!this.$route.query.welcome) {
      const tab = this.activeName || "";
      if (params != tab) {
        this.$router.push({ name: "questions", params: { tab: tab } });
      }
    }
    this.$root.$on('changeTab', this.handleTabChange);

    // Listen for language changes
    this.$root.$on('languageChanged', this.handleLanguageChange);

    // Deep link from the Programs editor: open the composer ready to
    // author a Program-only question.
    if (this.$route.query.compose === 'program') {
      this.openProgramQuestionComposer();
    }
  },
  
  beforeDestroy() {
    // Clean up event listener
    this.$root.$off('changeTab', this.handleTabChange);
    this.$root.$off('languageChanged', this.handleLanguageChange);
    // Cancel the intensity-undo timer if the user navigates away while it's armed.
    if (this.pendingIntensityRevertTimer) {
      clearTimeout(this.pendingIntensityRevertTimer);
      this.pendingIntensityRevertTimer = null;
    }
    if (
      typeof window !== "undefined" &&
      typeof window.cancelIdleCallback === "function" &&
      this.backgroundWarmupIdleHandle !== null
    ) {
      window.cancelIdleCallback(this.backgroundWarmupIdleHandle);
    }
    if (this.backgroundWarmupTimeoutHandle !== null) {
      clearTimeout(this.backgroundWarmupTimeoutHandle);
    }
  },

  methods: {
    shouldRenderTab(tab) {
      return !!this.renderedTabs[tab];
    },
    intensityCadenceSummaryFor(cadence) {
      const targets = CADENCE_TARGETS[cadence];
      if (!targets) return "";
      const questionsPerDay = targets.questions_per_day
        ? `${targets.questions_per_day.min}-${targets.questions_per_day.max}`
        : "No cap";
      const openEnded = targets.open_ended_per_week === null
        ? "No cap"
        : `${targets.open_ended_per_week}`;
      return `Questions/day: ${questionsPerDay} | Open-ended/week: ${openEnded}`;
    },
    async refreshQuestionsAfterIntensityChange() {
      await Promise.all([
        this.$store.dispatch("intensity/getIntensityState"),
        this.$store.dispatch("question/getQuestions", { user: this.user, forceRefresh: true }),
        this.$store.dispatch("custom/getQuestionsCustom", { user: this.user, forceRefresh: true }),
      ]);

      if (this.calendarData && this.calendarData.length > 0) {
        this.getQuestionPerMonth();
        this.addDataToArr({ syncIntensityBlocks: false });
      }
    },
    async onHeaderCadenceChange(cadence) {
      if (!this.canAdjustIntensityFromHeader || !this.hasLoadedIntensityState || !cadence) return;
      if (cadence === this.currentIntensityCadence) return;

      // Snapshot the prior state *before* we dispatch the change so the
      // Undo button (rendered in the panel footer for ~8s) can restore
      // the exact previous block_status of loaded standard/custom questions. See
      // `bulkRestoreBlockStatus` in src/store/question.ts.
      const previousCadence = this.currentIntensityCadence;
      const previousLabel = this.currentIntensityCadenceLabel;
      const standardQuestions = (this.$store.state.question && this.$store.state.question.questionsall) || [];
      const customQuestions = (this.$store.state.custom && this.$store.state.custom.questionsall) || [];
      const sourceQuestions = standardQuestions.concat(customQuestions);
      const questionsSnapshot = sourceQuestions
        .filter(q => q && q.key)
        .map(q => ({ key: q.key, block_status: q.block_status !== false }));

      this.clearPendingIntensityRevert(); // drop any older snapshot
      this.savingHeaderIntensity = true;
      try {
        await this.$store.dispatch("intensity/setAdminBias", cadenceToAdminBias(cadence));
        await this.refreshQuestionsAfterIntensityChange();
        // Previous client-side intensity filtering/block_question sync.
        // Kept for reference while the backend /signup/sync-intensity endpoint
        // owns this work after intensity_state is saved.
        // await this.ensureCalendarReady({
        //   forceRefresh: true,
        //   showLoader: false,
        // });
        // this.getQuestionPerMonth();
        // this.addDataToArr();

        // Arm the undo window once the change has committed successfully.
        if (previousCadence && previousCadence !== cadence) {
          this.armPendingIntensityRevert({
            cadence: previousCadence,
            previousLabel,
            questions: questionsSnapshot,
          });
        }
      } catch (error) {
        this.$message({ type: "error", message: "Unable to update intensity" });
      } finally {
        this.savingHeaderIntensity = false;
      }
    },
    /**
     * Arm the Undo affordance — stores the snapshot and starts an 8s timer
     * that auto-dismisses the inline undo row inside the intensity panel.
     */
    armPendingIntensityRevert(snapshot) {
      const TTL_MS = 8000;
      this.pendingIntensityRevert = snapshot;
      this.pendingIntensityRevertExpiresAt = Date.now() + TTL_MS;
      this.pendingIntensityRevertTimer = setTimeout(() => {
        this.clearPendingIntensityRevert();
      }, TTL_MS);
    },
    clearPendingIntensityRevert() {
      if (this.pendingIntensityRevertTimer) {
        clearTimeout(this.pendingIntensityRevertTimer);
        this.pendingIntensityRevertTimer = null;
      }
      this.pendingIntensityRevert = null;
      this.pendingIntensityRevertExpiresAt = 0;
    },
    /**
     * Restore the cadence + question state captured at the last intensity
     * change. Two-step: revert cadence (re-runs /signup/sync-intensity on the
     * backend), then patch any deltas via the bulk Firestore writeBatch in
     * `question/bulkRestoreBlockStatus`.
     */
    async onUndoIntensityChange() {
      const snapshot = this.pendingIntensityRevert;
      if (!snapshot || this.undoingIntensityChange) return;

      // Stop the auto-dismiss timer — we're handling it now.
      if (this.pendingIntensityRevertTimer) {
        clearTimeout(this.pendingIntensityRevertTimer);
        this.pendingIntensityRevertTimer = null;
      }
      this.undoingIntensityChange = true;
      try {
        await this.$store.dispatch(
          "intensity/setAdminBias",
          cadenceToAdminBias(snapshot.cadence),
        );
        await this.$store.dispatch(
          "question/bulkRestoreBlockStatus",
          snapshot.questions,
        );
        await this.refreshQuestionsAfterIntensityChange();
        this.pendingIntensityRevert = null;
        this.pendingIntensityRevertExpiresAt = 0;
        this.$message({
          type: "success",
          message: `Reverted to ${snapshot.previousLabel}`,
          duration: 2000,
        });
      } catch (error) {
        this.$message({
          type: "error",
          message: "Unable to undo intensity change",
        });
      } finally {
        this.undoingIntensityChange = false;
      }
    },
    setInitialTabFromRoute() {
      if (this.$route.query.welcome == "true") {
        this.activeName = "standard";
        this.renderedTabs.standard = true;
        return;
      }

      const params = this.$router.currentRoute.params.tab;
      if (params && this.anchor[params]) {
        this.activeName = params;
        this.renderedTabs[params] = true;
        return;
      }

      this.renderedTabs.standard = true;
    },
    async initializeQuestionsView() {
      if (!this.user) {
        return;
      }

      if (this.viewInitPromise) {
        return this.viewInitPromise;
      }

      this.viewInitPromise = (async () => {
        await this.initializeTab(this.activeName || "standard");
        this.viewReady = true;
        this.$nextTick(() => {
          this.scheduleBackgroundWarmup();
        });
      })();

      try {
        await this.viewInitPromise;
      } finally {
        this.viewInitPromise = null;
      }
    },
    async initializeTab(tab, { forceRefresh = false } = {}) {
      if (!this.user) {
        return;
      }

      if (this.tabReady[tab] && !forceRefresh) {
        return;
      }

      if (tab === "custom") {
        await Promise.all([
          this.$store.dispatch("question/getCategoryOptions"),
          this.ensureQuestionAudienceReady({ forceRefresh }),
        ]);
        await this.$store.dispatch("custom/getQuestionsCustom", {
          user: this.user,
          forceRefresh,
        });
        this.tabReady.custom = true;
        return;
      }

      await Promise.all([
        this.$store.dispatch("question/getCategoryOptions"),
        this.$store.dispatch("question/getQuestions", {
          user: this.user,
          forceRefresh,
        }),
      ]);
      this.tabReady.standard = true;
    },
    scheduleBackgroundWarmup() {
      if (
        this.backgroundWarmupScheduled ||
        this.backgroundWarmupCompleted ||
        !this.user
      ) {
        return;
      }

      const runWarmup = () => {
        this.backgroundWarmupIdleHandle = null;
        this.backgroundWarmupTimeoutHandle = null;
        this.runBackgroundWarmup();
      };

      this.backgroundWarmupScheduled = true;

      if (
        typeof window !== "undefined" &&
        typeof window.requestIdleCallback === "function"
      ) {
        this.backgroundWarmupIdleHandle = window.requestIdleCallback(
          runWarmup,
          { timeout: 1200 }
        );
        return;
      }

      this.backgroundWarmupTimeoutHandle = window.setTimeout(runWarmup, 250);
    },
    async runBackgroundWarmup() {
      if (this.backgroundWarmupCompleted || !this.user) {
        return;
      }

      this.backgroundWarmupCompleted = true;

      const warmups =
        this.activeName === "custom"
          ? [
              () => this.ensureQuestionFormReady(),
              () => this.ensureQuestionDetailReady(),
            ]
          : [
              () => this.ensureQuestionDetailReady(),
              () => this.ensureQuestionFormReady(),
            ];

      for (const warmup of warmups) {
        try {
          await warmup();
        } catch (error) {
          console.error("Error warming questions background data:", error);
        }
      }
    },
    async ensureQuestionAudienceReady({ forceRefresh = false } = {}) {
      if (this.audienceReady && !forceRefresh) {
        return;
      }

      if (this.audiencePromise && !forceRefresh) {
        return this.audiencePromise;
      }

      this.audiencePromise = (async () => {
        await this.$store.dispatch("question/getChildren", { forceRefresh });
        await this.$store.dispatch("question/getTeamAndCohort", { forceRefresh });
        this.audienceReady = true;
      })();

      try {
        await this.audiencePromise;
      } finally {
        this.audiencePromise = null;
      }
    },
    async ensureQuestionFormReady({ forceRefresh = false } = {}) {
      if (this.formReady && !forceRefresh) {
        return;
      }

      if (this.formPromise && !forceRefresh) {
        return this.formPromise;
      }

      this.formPromise = (async () => {
        await this.ensureQuestionAudienceReady({ forceRefresh });
        await this.$store.dispatch("question/getUser", { forceRefresh });
        await this.$store.dispatch("reward/checkEngOnly");
        await Promise.all([
          this.$store.dispatch("question/getDataCompany", { forceRefresh }),
          this.$store.dispatch("question/getCategoryOptions"),
        ]);
        this.$store.dispatch("question/getScheduleOptions", {
          end_row: "",
        });
        this.formReady = true;
      })();

      try {
        await this.formPromise;
      } finally {
        this.formPromise = null;
      }
    },
    async ensureQuestionDetailReady({ forceRefresh = false } = {}) {
      if (this.detailReady && !forceRefresh) {
        return;
      }

      if (this.detailPromise && !forceRefresh) {
        return this.detailPromise;
      }

      this.detailPromise = (async () => {
        await this.ensureQuestionAudienceReady({ forceRefresh });
        await Promise.all([
          this.$store.dispatch("question/getUser", { forceRefresh }),
          this.$store.dispatch("question/getTeamHeight", this.user),
          this.$store.dispatch("question/getDataOrganize", {
            user: this.user,
            forceRefresh,
          }),
        ]);
        this.detailReady = true;
      })();

      try {
        await this.detailPromise;
      } finally {
        this.detailPromise = null;
      }
    },
    async ensureCalendarReady({ forceRefresh = false, showLoader = true } = {}) {
      if (this.calendarReady && !forceRefresh) {
        return;
      }

      if (this.calendarPromise && !forceRefresh) {
        return this.calendarPromise;
      }

      this.calendarPromise = (async () => {
        if (showLoader) {
          this.$store.commit("question/setLoadingCalendar", true);
        }

        try {
          await this.ensureQuestionAudienceReady({ forceRefresh });
          await Promise.all([
            this.$store.dispatch("question/getQuestions", {
              user: this.user,
              forceRefresh,
            }),
            this.$store.dispatch("custom/getQuestionsCustom", {
              user: this.user,
              forceRefresh,
            }),
            this.$store.dispatch("question/getQuestionsOrder", {
              forceRefresh,
            }),
          ]);
          this.tabReady.standard = true;
          this.tabReady.custom = true;
          this.calendarReady = true;
        } finally {
          if (showLoader) {
            this.$store.commit("question/setLoadingCalendar", false);
          }
        }
      })();

      try {
        await this.calendarPromise;
      } finally {
        this.calendarPromise = null;
      }
    },
    handleTabChange(item) {
      if (item.path == "questions") {
        this.activeName = item.tab;
      }
    },
    getActiveOptions() {
      // console.log('getActiveOptions',this.activeIndex,this.dynamicValidateForm )
      
      //   return this.activeIndex === 'en' || this.activeIndex === 'th'
      // ? this.dynamicValidateForm[`options_${this.activeIndex}`]
      // : this.dynamicValidateForm.options_YN[this.activeIndex] 
      if (this.activeIndex === 'en' || this.activeIndex === 'th') {
      return this.dynamicValidateForm[`options_${this.activeIndex}`] || [];
    }
    if (!this.dynamicValidateForm.options_YN[this.activeIndex]) {
        this.$set(this.dynamicValidateForm.options_YN, this.activeIndex, [
          { key: 1, value: '', placeholder: 'Yes', question_id: '' },
          { key: 2, value: '', placeholder: 'No', question_id: '' },
        ]);
      }
      return this.dynamicValidateForm.options_YN[this.activeIndex];
    },
    
    isReadonly() {
      return this.activeIndex === 'en' || this.activeIndex === 'th';
    },
    updateOptionValue(index, value) {
      // console.log("updateOptionValue",this.dynamicValidateForm.options_YN[this.activeIndex][index].value)
      this.dynamicValidateForm.options_YN[this.activeIndex][index].value=value
      
    },
    truncateText(text) {
      let length = this.maxLength;
      return text.length > length ? text.slice(0, length) + "..." : text;
    },
    
    // Badge styling methods
    getQuestionTypeBadgeClass(type) {
      const typeMap = {
        'Multiple Choice': 'badge-multiple-choice',
        'Multiple Choice(other)': 'badge-multiple-choice',
        'Yes/No': 'badge-yes-no',
        'Scale': 'badge-scale',
        'Text Area Field': 'badge-text-area',
        'Follow-up': 'badge-follow-up'
      };
      return typeMap[type] || 'badge-default';
    },
    
    getFrequencyTagClass(frequency) {
      if (!frequency) return '';
      const freq = frequency.toLowerCase();
      if (freq.includes('bi-annual') || freq.includes('bi-weekly')) return 'tag-biannual';
      if (freq.includes('quarterly') || freq === 'quarterly') return 'tag-biannual';
      if (freq.includes('month') || freq === 'monthly') return 'tag-biannual';
      if (freq.includes('year') || freq === 'yearly') return 'tag-yearly';
      if (freq.includes('week') || freq === 'weekly') return 'tag-yearly';
      if (freq.includes('daily')) return 'tag-yearly';
      if (freq.includes('follow')) return 'tag-follow-up';
      if (freq.includes('one-time')) return 'tag-default';
      return 'tag-default';
    },
    
    async openModal(elm) {
      this.parentDetailDialogsMounted = true;
      this.quest_id = elm.key;
      this.quest_name = elm.question_en;
      this.currentQuestionData = elm; // Store current question data for localization
      let splitDate = elm.date.split("/");
      let date = splitDate[2]+'-'+splitDate[1]+'-'+splitDate[0];
      await this.ensureQuestionDetailReady();
      this.$store.commit("question/setLoading", true);
      if (elm.type === "Multiple Choice") {
        if (elm.custom_type === "multiple_choice") {
          this.$store.commit("question/setCountMultiChoices", 0);
          this.$store.commit("question/setDataMultiChoices", []);
          this.dialogMultipleOther = true;
          this.$store.dispatch("question/getDateDetail", {
            quest_id: elm.key,
            type: elm.type,
            custom_type: elm.custom_type,
            date: date,
          });
        } else {
          if (elm.m_choice.weight[0] === "0" || elm.m_choice.weight[0] === 0) {
            this.yes = false;
          } else {
            this.yes = true;
          }
          this.$store.commit("question/setDataMultiChoices", []);
          this.$store.commit("question/setCountMultiChoices", 0);
          this.dialogYesNo = true;
          this.$store.dispatch("question/getDateDetail", {
            quest_id: elm.key,
            type: elm.type,
            date: date,
          });
        }
      } else if (elm.type === "Scale") {
        this.$store.commit("question/setCountMultiChoices", 0);
        this.$store.commit("question/setDataMultiChoices", []);
        this.dialogScale = true;
        this.$store.dispatch("question/getDateDetail", {
          quest_id: elm.key,
          type: elm.type,
          date: date,
        });
      } else {
        this.$store.commit("question/setDataOpenEnd", []);
        this.dialogOpenEnded = true;
        this.$store.dispatch("question/getDateDetail", {
          quest_id: elm.key,
          type: elm.type,
          date: date,
        });
      }
    },
    async downloadExcel_Open_ended() {
      var date_data = format(this.dateShow, "YYYYMMDD");
      var question = "";

      this.quest_name
        .trim()
        .split(" ")
        .map((value, i) => {
          if (i < 5) {
            question = question + "_" + value;
          }
        });
      this.exportActivityStatus = true;
      this.dialogExport = false;
      const Excel = require("exceljs");
      var wb = new Excel.Workbook();
      var ws = wb.addWorksheet("mySheet");
      /*TITLE*/
      ws.mergeCells("B2", "C2", "D2", "E2");
      ws.getRow(2).height = 25;
      ws.getCell("B2").font = { name: "Arial", size: 18, bold: true };
      ws.getCell("B2").alignment = { vertical: "middle" };

      ws.getCell("B2").value = "QUESTION DETAILS";
      ws.getCell("B3").value = this.quest_name;
      ws.getCell("B4").value = "Open-ended";
      ws.getCell("B6").value = this.dateShow;

      // Use filtered data for count
      const dataToExport = this.filteredDataOpenEnd.filter(item => !item.Summary);
      let countMessage = dataToExport.length;
      let responses = "response";
      if (countMessage > 1) {
        responses = "responses";
      }
      
      // Add team filter info if applied
      let filterInfo = countMessage + " " + responses;
      if (this.selectedTeamFilter !== 'all') {
        const teamInfo = this.selectedTeamInfo;
        filterInfo += ` (filtered by: ${teamInfo ? teamInfo.label : 'Unknown Team'})`;
      }
      
      ws.getCell("B7").value = filterInfo;
      ws.getCell("B8").value = "";
      ws.getColumn("A").width = 5;
      ws.getColumn("B").width = 25; // Team name column
      ws.getColumn("C").width = 80; // Response column
      
      // Add headers
      ws.getCell("B9").value = "Team";
      ws.getCell("C9").value = "Response";
      ws.getCell("B9").font = { bold: true };
      ws.getCell("C9").font = { bold: true };
      
      // Add data with team names
      _.forEach(dataToExport, (item, key) => {
        const teamInfo = this.getTeamNameInfo(item.team);
        let _tmp = ["", teamInfo.name, item.answer || item];
        ws.addRow(_tmp);
      });
      wb.xlsx
        .writeBuffer({
          base64: true,
        })
        .then(function (xls64) {
          // build anchor tag and attach file (works in chrome)
          var a = document.createElement("a");
          var data = new Blob([xls64], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          var url = URL.createObjectURL(data);
          a.href = url;
          a.download = date_data + "_opened-ended" + question + ".xlsx";
          document.body.appendChild(a);
          a.click();
          /*save logs*/
          this.$store.dispatch(
            "authen/addLogs",
            {
              company: this.user.company,
              createdate: format(new Date(), "YYYY-MM-DD HH:mm:ss"),
              table: "questions",
              status: "downloaded",
              user: this.user.email,
            },
            { root: true }
          );
        })
        .catch(function (error) {
          console.log(error.message);
        });
      this.exportActivityStatus = false;
    },
    async downloadExcel_YesorNO() {
     
      var team = [];
      var level0 = [];
      var data = [];
      this.teamAll.map((value) => {
        let total = 0;
        let resY = 0;
        let resN = 0;
        let parent_name =
          this.teamArr[value.parent] !== undefined
            ? this.teamArr[value.parent].name
            : "noun";
        if (this.teamArr[value.parent] === undefined) {
          level0.push(value);
        }
        if (this.datamultichoices[value.key]) {
          total =
            this.datamultichoices[value.key][0] +
            this.datamultichoices[value.key][1];
          resY = this.datamultichoices[value.key][0];
          resN = this.datamultichoices[value.key][1];
        }
        team.push({
          ...value,
          parent_name: parent_name,
          percentYes: this.getYesPercent(value.key),
          percentNo: this.getNoPercent(value.key),
          resY: resY,
          resN: resN,
          total_res: total,
        });
      });
      if(Object.keys(this.deletedTeam).length > 0){
        let index = 1;
        for(const keyId in this.deletedTeam){
          let total = 0;
          let resY = 0;
          let resN = 0;
          let parent_name =
            this.teamArr[this.teamHeight] !== undefined
              ? this.teamArr[this.teamHeight].name
              : "noun";
          if (this.datamultichoices[keyId]) {
            total =
              this.datamultichoices[keyId][0] +
              this.datamultichoices[keyId][1];
            resY = this.datamultichoices[keyId][0];
            resN = this.datamultichoices[keyId][1];
          }
          team.push({
            key: keyId,
            name: 'Deleted Team ' + index,
            parent: this.teamHeight,
            parent_name: parent_name,
            percentYes: this.getYesPercent(keyId),
            percentNo: this.getNoPercent(keyId),
            resY: resY,
            resN: resN,
            total_res: total,
          });
          index++
        }
      }
      const organizations = this.organizations;
      this.organization1level = {};
      const organization1level = this.organization1level;
      
      const teams = organizations[level0[0].key];
      if (teams) {
        organization1level[teams.team_id] = true;
        _.forEach(teams.parent, (value) => {
          organization1level[value.team_id] = true;
          if (value.parent) {
            this.getTeamOfParent(value.parent);
            // dispatch('getTeamOfParent', value.parent)
          }
        });
      } else {
        organization1level[level0[0].key] = true;
      }
      if(Object.keys(this.deletedTeam).length > 0){
        for(const keyId2 in this.deletedTeam){
          organization1level[keyId2] = true;
        }
      }
      _.forEach(organization1level, (value, team_id) => {
        let _temp = _.find(team, ["key", team_id]);
        data.push(_temp);
      });
      var date_data = format(this.dateShow, "YYYYMMDD");
      var question = "";
      this.quest_name
        .trim()
        .split(" ")
        .map((value, i) => {
          if (i < 5) {
            question = question + "_" + value;
          }
        });
      this.exportActivityStatus = true;
      this.dialogExport = false;
      const Excel = require("exceljs");
      var wb = new Excel.Workbook();
      var ws = wb.addWorksheet("mySheet");
      /*TITLE*/
      ws.mergeCells("B2", "C2", "D2", "E2");
      ws.getRow(2).height = 25;
      ws.getCell("B2").font = { name: "Arial", size: 18, bold: true };
      ws.getCell("B2").alignment = { vertical: "middle" };

      ws.getCell("B2").value = "QUESTION DETAILS";
      ws.getCell("B3").value = this.quest_name;
      ws.getCell("B4").value = "Yes/No";
      ws.getCell("B6").value = this.dateShow;

      const countMessage = this.dataopenend.length;
      let responses = "response";
      if (countMessage > 1) {
        responses = "responses";
      }
      ws.getColumn("A").width = 5;
      let header = [
        "",
        "Team Name",
        "Parent Team",
        "Option",
        "Value",
        "Responses",
        "Total Responses",
      ];
      ws.getRow(7).values = header;
      //   /*Data*/
      _.forEach(data, (item, key) => {
        let _tmp = [
          "",
          item.name,
          item.parent_name,
          "Yes",
          item.percentYes,
          item.resY,
          item.total_res,
        ];
        ws.addRow(_tmp);
        let _tmp1 = ["", "", "", "No", item.percentNo, item.resN];
        ws.addRow(_tmp1);
      });
      wb.xlsx
        .writeBuffer({
          base64: true,
        })
        .then(function (xls64) {
          // build anchor tag and attach file (works in chrome)
          var a = document.createElement("a");
          var data = new Blob([xls64], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          var url = URL.createObjectURL(data);
          a.href = url;
          a.download = date_data + "_Yes-No" + question + ".xlsx";
          document.body.appendChild(a);
          a.click();
          /*save logs*/
          this.$store.dispatch(
            "authen/addLogs",
            {
              company: this.user.company,
              createdate: format(new Date(), "YYYY-MM-DD HH:mm:ss"),
              table: "questions",
              status: "downloaded",
              user: this.user.email,
            },
            { root: true }
          );
        })
        .catch(function (error) {
          console.log(error.message);
        });
      this.exportActivityStatus = false;
    },
    async downloadExcel_Scale() {
      var team = [];
      var level0 = [];
      var data = [];
      this.teamAll.map((value) => {
        let total = 0;
        let avg = {};
        let parent_name =
          this.teamArr[value.parent] !== undefined
            ? this.teamArr[value.parent].name
            : "noun";
        if (this.teamArr[value.parent] === undefined) {
          level0.push(value);
        }
        if (this.datamultichoices[value.key]) {
          for (
            var i = 0;
            i < this.datamultichoices[value.key]["range"].length;
            i++
          ) {
            total += this.datamultichoices[value.key]["range"][i];
          }
          avg = this.datamultichoices[value.key].avg;
        }
        team.push({
          ...value,
          parent_name: parent_name,
          total_res: total,
          avg: avg,
          range0to6: this.getScalePercent(value.key, 0),
          range7to8: this.getScalePercent(value.key, 1),
          range9to10: this.getScalePercent(value.key, 2),
        });
      });
      if(Object.keys(this.deletedTeam).length > 0){
        let index = 1
        for(const keyId in this.deletedTeam){
          let total = 0;
          let avg = {};
          let parent_name =
            this.teamArr[this.teamHeight] !== undefined
              ? this.teamArr[this.teamHeight].name
              : "noun";
          if (this.datamultichoices[keyId]) {
            for (
              var i = 0;
              i < this.datamultichoices[keyId]["range"].length;
              i++
            ) {
              total += this.datamultichoices[keyId]["range"][i];
            }
            avg = this.datamultichoices[keyId].avg;
          }
          team.push({
            key: keyId,
            name: 'Deleted Team ' + index,
            parent: this.teamHeight,
            parent_name: parent_name,
            total_res: total,
            avg: avg,
            range0to6: this.getScalePercent(keyId, 0),
            range7to8: this.getScalePercent(keyId, 1),
            range9to10: this.getScalePercent(keyId, 2),
          });
          index++
        }
      }
      const organizations = this.organizations;
      this.organization1level = {};
      const organization1level = this.organization1level;
      const teams = organizations[level0[0].key];
      if (teams) {
        organization1level[teams.team_id] = true;
        _.forEach(teams.parent, (value) => {
          organization1level[value.team_id] = true;
          if (value.parent) {
            this.getTeamOfParent(value.parent);
          }
        });
      } else {
        organization1level[level0[0].key] = true;
      }
      if(Object.keys(this.deletedTeam).length > 0){
        for(const keyId2 in this.deletedTeam){
          organization1level[keyId2] = true;
        }
      }
      _.forEach(organization1level, (value, team_id) => {
        let _temp = _.find(team, ["key", team_id]);
        data.push(_temp);
      });
      var date_data = format(this.dateShow, "YYYYMMDD");
      var question = "";
      this.quest_name
        .trim()
        .split(" ")
        .map((value, i) => {
          if (i < 5) {
            question = question + "_" + value;
          }
        });
      this.exportActivityStatus = true;
      this.dialogExport = false;
      const Excel = require("exceljs");
      var wb = new Excel.Workbook();
      var ws = wb.addWorksheet("mySheet");
      /*TITLE*/
      ws.mergeCells("B2", "C2", "D2", "E2");
      ws.getRow(2).height = 25;
      ws.getCell("B2").font = { name: "Arial", size: 18, bold: true };
      ws.getCell("B2").alignment = { vertical: "middle" };

      ws.getCell("B2").value = "QUESTION DETAILS";
      ws.getCell("B3").value = this.quest_name;
      ws.getCell("B4").value = "Multiple Choice";
      ws.getCell("B6").value = this.dateShow;

      const countMessage = this.dataopenend.length;
      let responses = "response";
      if (countMessage > 1) {
        responses = "responses";
      }
      ws.getColumn("A").width = 5;
      let header = [
        "",
        "Team Name",
        "Parent Team",
        "Average Rating",
        "0-6 (%)",
        "7-8 (%)",
        "	9-10 (%)",
        "Total Responses",
      ];
      ws.getRow(7).values = header;
      //   /*Data*/
      _.forEach(data, (item, key) => {
        let _tmp = [
          "",
          item.name,
          item.parent_name,
          item.avg,
          item.range0to6,
          item.range7to8,
          item.range9to10,
          item.total_res,
        ];
        ws.addRow(_tmp);
      });
      wb.xlsx
        .writeBuffer({
          base64: true,
        })
        .then(function (xls64) {
          // build anchor tag and attach file (works in chrome)
          var a = document.createElement("a");
          var data = new Blob([xls64], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          var url = URL.createObjectURL(data);
          a.href = url;
          a.download = date_data + "_Scale" + question + ".xlsx";
          document.body.appendChild(a);
          a.click();
          /*save logs*/
          this.$store.dispatch(
            "authen/addLogs",
            {
              company: this.user.company,
              createdate: format(new Date(), "YYYY-MM-DD HH:mm:ss"),
              table: "questions",
              status: "downloaded",
              user: this.user.email,
            },
            { root: true }
          );
        })
        .catch(function (error) {
          console.log(error.message);
        });
      this.exportActivityStatus = false;
    },
    async downloadExcel_MultipleChoice() {
      var team = [];
      var level0 = [];
      var data = [];
      this.teamAll.map((value) => {
        let total = 0;
        let option = {};
        let parent_name =
          this.teamArr[value.parent] !== undefined
            ? this.teamArr[value.parent].name
            : "noun";
        if (this.teamArr[value.parent] === undefined) {
          level0.push(value);
        }
        if (this.datamultichoices[value.key]) {
          for (var i = 0; i < this.datamultichoices[value.key].length; i++) {
            total += this.datamultichoices[value.key][i];
            option[`precent${i+1}`] = this.getMultipleChoicePercent(value.key,this.answerOpt.length - i - 1);
            option[`res${i+1}`] = this.datamultichoices[value.key][this.answerOpt.length - i - 1];
            option[`options${i+1}`] = this.answerOpt[i];
            option['optionCount'] = option['optionCount']?option['optionCount']+1:1
          }
        }
        team.push({
          ...value,
          parent_name: parent_name,
          total_res: total,
          ...option,
        });
      });
      if(Object.keys(this.deletedTeam).length > 0){
        let index = 1
        for(const keyId in this.deletedTeam){
          this.teamAll.map((value) => {
            let total = 0;
            let option = {};
            let parent_name =
              this.teamArr[this.teamHeight] !== undefined
                ? this.teamArr[this.teamHeight].name
                : "noun";
            if (this.datamultichoices[keyId]) {
              for (var i = 0; i < this.datamultichoices[keyId].length; i++) {
                total += this.datamultichoices[keyId][i];
                option[`precent${i+1}`] = this.getMultipleChoicePercent(keyId,this.answerOpt.length - i - 1);
                option[`res${i+1}`] = this.datamultichoices[keyId][this.answerOpt.length - i - 1];
                option[`options${i+1}`] = this.answerOpt[i];
                option['optionCount'] = option['optionCount']?option['optionCount']+1:1
              }
            }
            team.push({
              key: keyId,
              name: 'Deleted Team ' + index,
              parent: this.teamHeight,
              parent_name: parent_name,
              total_res: total,
              ...option,
            });
          });
          index++
        }
      }
      const organizations = this.organizations;
      this.organization1level = {};
      const organization1level = this.organization1level;
      const teams = organizations[level0[0].key];
      if (teams) {
        organization1level[teams.team_id] = true;
        _.forEach(teams.parent, (value) => {
          organization1level[value.team_id] = true;
          if (value.parent) {
            this.getTeamOfParent(value.parent);
          }
        });
      } else {
        organization1level[level0[0].key] = true;
      }
      if(Object.keys(this.deletedTeam).length > 0){
        for(const keyId2 in this.deletedTeam){
          organization1level[keyId2] = true;
        }
      }
      _.forEach(organization1level, (value, team_id) => {
        let _temp = _.find(team, ["key", team_id]);
        data.push(_temp);
      });
      var date_data = format(this.dateShow, "YYYYMMDD");
      var question = "";
      this.quest_name
        .trim()
        .split(" ")
        .map((value, i) => {
          if (i < 5) {
            question = question + "_" + value;
          }
        });
      this.exportActivityStatus = true;
      this.dialogExport = false;
      const Excel = require("exceljs");
      var wb = new Excel.Workbook();
      var ws = wb.addWorksheet("mySheet");
      /*TITLE*/
      ws.mergeCells("B2", "C2", "D2", "E2");
      ws.getRow(2).height = 25;
      ws.getCell("B2").font = { name: "Arial", size: 18, bold: true };
      ws.getCell("B2").alignment = { vertical: "middle" };

      ws.getCell("B2").value = "QUESTION DETAILS";
      ws.getCell("B3").value = this.quest_name;
      ws.getCell("B4").value = "Multiple Choice";
      ws.getCell("B6").value = this.dateShow;

      const countMessage = this.dataopenend.length;
      let responses = "response";
      if (countMessage > 1) {
        responses = "responses";
      }
      ws.getColumn("A").width = 5;
      let header = [
        "",
        "Team Name",
        "Parent Team",
        "Option",
        "Value",
        "Responses",
        "Total Responses",
      ];
      ws.getRow(7).values = header;
      //   /*Data*/
      _.forEach(data, (item, key) => {
        if(item.optionCount && item.optionCount > 0){
          for (var i = 0; i < item.optionCount; i++) {
            if(i == 0){
              let _tmp = [
                "",
                item.name,
                item.parent_name,
                item.options1,
                item.percent1,
                item.res1,
                item.total_res,
              ];
              ws.addRow(_tmp);
            }else{
              let _tmp1 = ['', '', '', item[`options${i+1}`], item[`options${i+1}`], item[`res${i+1}`]];
              ws.addRow(_tmp1);
            }
          }
        }else{
          let _tmp = [
            "",
            item.name,
            item.parent_name,
            item.options1,
            item.percent1,
            item.res1,
            item.total_res,
          ];
          ws.addRow(_tmp);
          ws.addRow();
        }
      });
      wb.xlsx
        .writeBuffer({
          base64: true,
        })
        .then(function (xls64) {
          // build anchor tag and attach file (works in chrome)
          var a = document.createElement("a");
          var data = new Blob([xls64], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          var url = URL.createObjectURL(data);
          a.href = url;
          a.download = date_data + "_Multiple-Choice" + question + ".xlsx";
          document.body.appendChild(a);
          a.click();
          /*save logs*/
          this.$store.dispatch(
            "authen/addLogs",
            {
              company: this.user.company,
              createdate: format(new Date(), "YYYY-MM-DD HH:mm:ss"),
              table: "questions",
              status: "downloaded",
              user: this.user.email,
            },
            { root: true }
          );
        })
        .catch(function (error) {
          console.log(error.message);
        });
      this.exportActivityStatus = false;
    },
    renderDropdown() {
      if (this.dateoptions.length > 1) {
        return (
          <i class="el-icon-arrow-down txtDropdown__caret"></i>
        );
      }
    },
    renderHeaderOpenEnded(creatElement, { column }) {
      let components = [];
      for (var i = 0; i < this.dateoptions.length; i++) {
        components.push(
          <el-dropdown-item command={this.dateoptions[i].date}>
            {this.dateoptions[i]["show"]}
          </el-dropdown-item>
        );
      }
      let countMessage = this.dataopenend.length;
      if (countMessage && countMessage > 0 && this.summaryDetail.Summary) {
        countMessage = countMessage - 1
      }
      
      // Use filtered data for count when team filter is applied
      if (this.selectedTeamFilter !== 'all') {
        countMessage = this.filteredDataOpenEnd.filter(item => !item.Summary).length;
      }
      
      let responses = "response";
      if (countMessage > 1) {
        responses = "responses";
      }
      return (
        <div class="headerOpenEnded">
          <el-dropdown trigger="click" onCommand={this.handleDateCommand}>
            <span class="txtDropdown">
              {this.dateShow}
              {this.renderDropdown()}
            </span>

            <el-dropdown-menu slot="dropdown" class="question-date-popper">
              {components}
            </el-dropdown-menu>
          </el-dropdown>

          <span class="txtTotalResponse">
            {countMessage} {responses}
            {this.selectedTeamFilter !== 'all' && <span class="filtered-indicator"> (filtered)</span>}
          </span>
        </div>
      );
    },
    renderHeaderYesNo(creatElement, { column }) {
      let components = [];
      for (var i = 0; i < this.dateoptions.length; i++) {
        components.push(
          <el-dropdown-item command={this.dateoptions[i].date}>
            {this.dateoptions[i]["show"]}
          </el-dropdown-item>
        );
      }
      const countMessage = this.countmultichoices;
      let responses = "response";
      if (countMessage > 1) {
        responses = "responses";
      }
      let classForNo = !this.yes ? "box color-4 mr-8" : "box color-2 mr-8";
      let classForYes = this.yes ? "box color-4 mr-8" : "box color-2 mr-8";
      return (
        <div>
          <div class="flex-between mt-10 mb-2">
            <el-dropdown trigger="click" onCommand={this.handleDateCommand}>
              <span class="txtDropdown">
                {this.dateShow}
                {this.renderDropdown()}
              </span>
              <el-dropdown-menu slot="dropdown" class="question-date-popper">
                {components}
              </el-dropdown-menu>
            </el-dropdown>
            <span class="txtTotalResponse">
              {this.$utils("addCommasInNumber", countMessage)} {responses}
            </span>
          </div>
          <div>
            <div class="right flex-reverse fs-12 mt-10">
              <div class="flex ml-20">
                <div class={classForYes}></div>
                <div>{this.localizedAnswerOptions[0] || 'Yes'}</div>
              </div>
              <div class="flex ml-20">
                <div class={classForNo}></div>
                <div>{this.localizedAnswerOptions[1] || 'No'}</div>
              </div>
            </div>
          </div>
        </div>
      );
    },
    getMultipleChoiceColor(index) {
      if (this.answerOpt.length > 0) {
        let orderColor = [];
        if (this.answerOpt.length == 2) {
          orderColor = [2, 4];
        } else if (this.answerOpt.length == 3) {
          orderColor = [2, 3, 4];
        } else if (this.answerOpt.length == 4) {
          orderColor = [2, 3, 4, 5];
        } else {
          let no = 1;
          for (var i = 0; i < this.answerOpt.length; i++) {
            orderColor.push(no);
            no = no + 1;
          }
        }
        return orderColor[index];
      }
    },
    getScaleColor(index) {
      let orderColor = [2, 3, 4];
      return orderColor[index];
    },
    renderHeaderScale(creatElement, { column }) {
      let components = [];
      for (var i = 0; i < this.dateoptions.length; i++) {
        components.push(
          <el-dropdown-item command={this.dateoptions[i].date}>
            {this.dateoptions[i]["show"]}
          </el-dropdown-item>
        );
      }
      const countMessage = this.countmultichoices;
      let responses = "response";
      if (countMessage > 1) {
        responses = "responses";
      }
      return (
        <div>
          <div class="flex-between mt-10 mb-2">
            <el-dropdown trigger="click" onCommand={this.handleDateCommand}>
              <span class="txtDropdown">
                {this.dateShow}
                {this.renderDropdown()}
              </span>
              <el-dropdown-menu slot="dropdown" class="question-date-popper">
                {components}
              </el-dropdown-menu>
            </el-dropdown>
            <span class="txtTotalResponse">
              {this.$utils("addCommasInNumber", countMessage)} {responses}
            </span>
          </div>
          <div>
            <div class="right flex-reverse fs-11 mt-10">
              <div class="flex ml-20">
                <div class="box color-4 mr-8"></div>
                <div class="fc-000000 opa-06">9-10</div>
              </div>
              <div class="flex ml-20">
                <div class="box color-3 mr-8"></div>
                <div class="fc-000000 opa-06">7-8</div>
              </div>
              <div class="flex ml-20">
                <div class="box color-2 mr-8"></div>
                <div class="fc-000000 opa-06">0-6</div>
              </div>
            </div>
          </div>
        </div>
      );
    },
    renderHeaderMultipleOther(creatElement, { column }) {
      let components = [];
      for (var i = 0; i < this.dateoptions.length; i++) {
        components.push(
          <el-dropdown-item command={this.dateoptions[i].date}>
            {this.dateoptions[i]["show"]}
          </el-dropdown-item>
        );
      }
      const countMessage = this.countmultichoices;
      let responses = "response";
      if (countMessage > 1) {
        responses = "responses";
      }
      let components_answer = [];
      if (this.localizedAnswerOptionsOther.length > 0) {
        let orderColor = [];
        if (this.localizedAnswerOptionsOther.length == 2) {
          orderColor = [4, 2];
        } else if (this.localizedAnswerOptionsOther.length == 3) {
          orderColor = [4, 3, 2];
        } else if (this.localizedAnswerOptionsOther.length == 4) {
          orderColor = [5, 4, 3, 2];
        } else {
          let no = this.localizedAnswerOptionsOther.length;
          for (var i = this.localizedAnswerOptionsOther.length; i > 0; i--) {
            orderColor.push(no);
            no = no - 1;
          }
        }
        for (var i = 0; i < this.localizedAnswerOptionsOther.length; i++) {
          components_answer.push(
            <div class="flex ml-20">
              <div class={"box color-" + orderColor[i] + " mr-8"}></div>
              <div>{this.localizedAnswerOptionsOther[i]}</div>
            </div>
          );
        }
      }
      return (
        <div>
          <div class="flex-between mt-10 mb-2">
            <el-dropdown trigger="click" onCommand={this.handleDateCommand}>
              <span class="txtDropdown">
                {this.dateShow}
                {this.renderDropdown()}
              </span>
              <el-dropdown-menu slot="dropdown" class="question-date-popper">
                {components}
              </el-dropdown-menu>
            </el-dropdown>
            <span class="txtTotalResponse">
              {this.$utils("addCommasInNumber", countMessage)} {responses}
            </span>
          </div>
          <div>
            <div class="right flex-reverse fs-12 mt-10">
              {components_answer}
            </div>
          </div>
        </div>
      );
    },
    handleDateCommand(date) {
      if (date) {
        for (const x in this.dateoptions) {
          if (this.dateoptions[x].date === date) {
            this.$store.commit(
              "question/setDateShow",
              this.dateoptions[x]["show"]
            );
          }
        }
      }
      this.$store.commit("question/setLoading", true);
      if (this.dialogYesNo) {
        this.$store.dispatch("question/getDataMultiChoices", {
          date,
          quest_id: this.quest_id,
        });
      } else if (this.dialogMultipleOther) {
        this.$store.dispatch("question/getDataMultiChoicesOther", {
          date,
          quest_id: this.quest_id,
        });
      } else if (this.dialogScale) {
        this.$store.dispatch("question/getDataScale", {
          date,
          quest_id: this.quest_id,
        });
      } else {
        this.$store.dispatch("question/getDataOpenEnd", {
          date,
          quest_id: this.quest_id,
        });
      }
    },
    getScalePercent(id, index) {
      if (this.datamultichoices[id]) {
        let total = 0;
        for (var i = 0; i < this.datamultichoices[id]["range"].length; i++) {
          total += this.datamultichoices[id]["range"][i];
        }
        if (total !== 0) {
          let results = Math.round(
            (this.datamultichoices[id]["range"][index] * 100) / total
          );
          return results;
        } else {
          return 0;
        }
      }
    },
    getColorPercentForScale(value) {
      if (value) {
        return `width: ${Math.round((537 / 100) * value)}px;`;
      } else {
        return;
      }
    },
    getYesPercent(id) {
      if (this.datamultichoices[id]) {
        const total =
          this.datamultichoices[id][0] + this.datamultichoices[id][1];
        if (total !== 0)
          return Math.round((this.datamultichoices[id][0] * 100) / total);
        else return 0;
      }
    },
    getNoPercent(id) {
      if (this.datamultichoices[id]) {
        const total =
          this.datamultichoices[id][0] + this.datamultichoices[id][1];
        if (total !== 0)
          return Math.round((this.datamultichoices[id][1] * 100) / total);
        else return 0;
      }
    },
    getMultipleChoicePercent(id, index) {
      if (this.datamultichoices[id]) {
        let total = 0;
        if (this.answerOpt.length > 0) {
          for (var i = 0; i < this.datamultichoices[id].length; i++) {
            total += this.datamultichoices[id][i];
          }
          if (total !== 0) {
            let results = Math.round(
              (this.datamultichoices[id][index] * 100) / total
            );
            return results;
          } else {
            return 0;
          }
        }
      }
    },
    getColorPercent(value) {
      if (value) {
        return `width: ${Math.round((577 / 100) * value)}px;`;
      } else {
        return;
      }
    },
    getTotalResponses(id) {
      if (!this.datamultichoices[id]) return 0;

      // For Scale type (has 'range' property)
      if (this.datamultichoices[id].range) {
        return this.datamultichoices[id].range.reduce((sum, current) => sum + current, 0);
      }

      // For Yes/No type (array with 2 values)
      if (Array.isArray(this.datamultichoices[id]) && this.datamultichoices[id].length === 2) {
        return this.datamultichoices[id][0] + this.datamultichoices[id][1];
      }

      // For Multiple Choice (array with multiple values)
      if (Array.isArray(this.datamultichoices[id])) {
        return this.datamultichoices[id].reduce((sum, current) => sum + current, 0);
      }

      return 0;
    },
    async getTeamOfParent(item) {
      const organization1level = this.organization1level;
      _.forEach(item, (value) => {
        organization1level[value.team_id] = true;
        if (value.parent) {
          this.getTeamOfParent(value.parent);
          // dispatch('getTeamOfParent', value.parent)
        }
      });
    },
    openRecipientsDialog() {
      this.recipientsModal = true;
    },
    normalizeEmail(value) {
      return String(value || "").trim().toLowerCase();
    },
    extractEmailsFromText(text) {
      const matches = String(text || "").match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) || [];
      return _.uniq(matches.map((email) => this.normalizeEmail(email)));
    },
    findRecipientUserByEmail(email) {
      const normalizedEmail = this.normalizeEmail(email);
      return _.find(this.allUsers || [], (user) => {
        return this.normalizeEmail(user && user.email) === normalizedEmail;
      });
    },
    selectRecipientsByEmailText(text) {
      const emails = this.extractEmailsFromText(text);
      if (!emails.length) {
        return { selected: 0, missing: [] };
      }

      const matchedUsers = [];
      const missing = [];
      emails.forEach((email) => {
        const user = this.findRecipientUserByEmail(email);
        if (user && user.email) {
          matchedUsers.push(user);
        } else {
          missing.push(email);
        }
      });

      const uniqueUsers = _.uniqBy(matchedUsers, (user) => this.normalizeEmail(user.email));
      if (!uniqueUsers.length) {
        return { selected: 0, missing };
      }

      const existingSelection = (this.dynamicValidateForm.cascaderTo || []).filter((item) => {
        return item && item[0] && item[0] !== "all_teams_and_cohorts";
      });
      const userSelection = uniqueUsers.map((user) => [`user_${user.email}`]);
      this.dynamicValidateForm.cascaderTo = _.uniqBy(
        [...existingSelection, ...userSelection],
        (item) => item && item[0]
      );
      this.changeDataToCascader(this.dynamicValidateForm.cascaderTo);
      this.clearRecipientFilterInput();

      return { selected: uniqueUsers.length, missing };
    },
    handleRecipientPaste(event) {
      const text = event && event.clipboardData
        ? event.clipboardData.getData("text")
        : "";
      const result = this.selectRecipientsByEmailText(text);
      if (!result.selected) {
        return;
      }

      event.preventDefault();
      if (result.missing.length) {
        this.$message({
          showClose: true,
          message: `${result.selected} recipients selected. ${result.missing.length} email${result.missing.length === 1 ? "" : "s"} not found.`,
          type: "warning",
        });
      }
    },
    clearRecipientFilterInput() {
      this.$nextTick(() => {
        const cascader = this.$refs.refTo;
        if (!cascader) return;
        cascader.inputValue = "";
        cascader.presentText = "";
        cascader.dropDownVisible = false;
      });
    },
    changeDataToCascader(value) {
      let recipients = [];
      let cascaderTo = [];
      let oldcascaderTo = [];
      let diffcascaderTo = {};
      let diffoldcascaderTo = {};
      
      let team = [];
      let alluser = _.find(
        this.dynamicValidateForm.cascaderTo,
        function (value) {
          return value[0] == "all_teams_and_cohorts";
        }
      );

      let oldalluser = _.find(
        this.dynamicValidateForm.oldCascaderTo,
        function (value) {
          return value[0] == "all_teams_and_cohorts";
        }
      );

      if (alluser) {
        this.dynamicValidateForm.oldCascaderTo =
          this.dynamicValidateForm.cascaderTo;
        this.dynamicValidateForm.recipients = this.allUsers;
      } else {
        if (this.dynamicValidateForm.cascaderTo) {
          this.dynamicValidateForm.cascaderTo.map((value) => {
            
            cascaderTo.push(value[0]);
          });
        }
        if (this.dynamicValidateForm.oldCascaderTo) {
          this.dynamicValidateForm.oldCascaderTo.map((value) => {
            oldcascaderTo.push(value[0]);
          });
        }

        let diff = _.xor(cascaderTo, oldcascaderTo);

        if (diff) {
          diff.map((data) => {
            if (data) {
              let diffolddata = _.filter(
                this.dynamicValidateForm.oldCascaderTo,
                function (value) {
                  return value[0] == data;
                }
              );
              if (diffolddata[0]) {
                diffoldcascaderTo[diffolddata[0]] = true;
              }
            }
          });
        }

        //cohorts
        if (this.dynamicValidateForm.cascaderTo) {
          this.dynamicValidateForm.cascaderTo.map((value) => {
            const selectedValue = value && value[0];
            if (!selectedValue) {
              return;
            }
            if (selectedValue.indexOf("user_") === 0) {
              const email = selectedValue.replace(/^user_/, "");
              const user = this.findRecipientUserByEmail(email);
              if (user) {
                recipients.push(user);
              }
              return;
            }
            if (
              this.cohortsUser[selectedValue] &&
              this.cohortsUser[selectedValue].users
            ) {
              team.push(selectedValue);
              for (let data in this.cohortsUser[selectedValue].users) {
                let user = _.find(this.allUsers, function (v) {
                  return v.key == data;
                });
                if (user) {
                  recipients.push(user);
                }
              }
              //cohorts=====================
            } else {
              if (this._organization1level[selectedValue]) {
                for (let teamId in this._organization1level[selectedValue]) {
                  if (!diffoldcascaderTo[teamId]) {
                    if (this.userTeam[teamId]) {
                      recipients.push(...this.userTeam[teamId]);
                    }
                    let uniq = _.find(team, function (value) {
                      return value == teamId;
                    });
                    if (!uniq) {
                      team.push(teamId);
                    }
                  }
                }
              } else {
                if (this.userTeam[selectedValue]) {
                  recipients.push(...this.userTeam[selectedValue]);
                }
                let uniq = _.find(team, function (_value) {
                  return _value == selectedValue;
                });
                if (!uniq) {
                  team.push(selectedValue);
                }
              }
            }
          });
        }

        recipients = _.uniqBy(recipients, "email");
        this.dynamicValidateForm.recipients = recipients;

        this.dynamicValidateForm.oldCascaderTo =
          this.dynamicValidateForm.cascaderTo;
      }
    },
    increase(index) {
      return index + 1;
    },
    onChangeQuestionsType(value) {
      if(this.dynamicValidateForm.report_card == true){
        document.getElementById("report_card").click();
      }

      if(value == 'Text Area Field'){
        this.disabledRowHeader = true
      }else{
        this.disabledRowHeader = false
      }
    },
    onChangeY(value) {
      if (value === "Negative") {
        this.dynamicValidateForm.feelingN = "Positive";
      } else if (value === "Neutral") {
        this.dynamicValidateForm.feelingN = "Neutral";
      } else {
        this.dynamicValidateForm.feelingN = "Negative";
      }
    },
    onChangeN(value) {
      if (value === "Negative") {
        this.dynamicValidateForm.feelingY = "Positive";
      } else if (value === "Neutral") {
        this.dynamicValidateForm.feelingY = "Neutral";
      } else {
        this.dynamicValidateForm.feelingY = "Negative";
      }
    },
    async addQuestion() {
      //console.log('addQuestion')
      this.questionFormMounted = true;
      await this.ensureQuestionFormReady();
      this.title_dialog = this.$t("questions.add_a_question");
      this.mode = "";
      this.cancelAdd();
      this.changeDataToCascader();
    },
    async openProgramQuestionComposer() {
      await this.addQuestion();
      // Seed the frequency so the question is parked off the public
      // schedule and shows up in the Programs library picker.
      this.dynamicValidateForm.frequency = "Program-only";
      // Drop the param so a later refresh doesn't reopen the composer.
      if (this.$route.query.compose) {
        this.$router.replace({ query: {} }).catch(() => {});
      }
    },
    cancelAdd() {
      this.clearQuestionSuccessState();
      this.isSavingQuestion = false;
      this.activeIndex = "en";
      this.dialogFormVisible = true;
      this.option_To=this.teamleadership!=''?this.optionToleadership:this.optionTo
      this.questions_text=''
      this.dynamicValidateForm = {
        to: [],
        cascaderTo:this.teamleadership!=''?[this.teamleadership]:[["all_teams_and_cohorts"]],
        oldCascaderTo:this.teamleadership!=''?[this.teamleadership]:[["all_teams_and_cohorts"]],
        showResultsInDaily: true,
        category: "",
        type: "",
        questions:{},
        question_en: "",
        question_th: "",
        coins: "1",
        frequency: "",
        dayofweek: "monday",
        schedule: "",
        status: "Y",
        feelingY: "Positive",
        feelingN: "Negative",
        options_YN:{},
        options_en: [
          {
            key: 1,
            value: "Yes",
            weight: "1",
            placeholder: "Yes",
            question_id:''
          },
          {
            key: 2,
            value: "No",
            weight: "0",
            placeholder: "No",
            question_id:''
          },
        ],
        options_th: [
          {
            key: 1,
            value: "ใช่",
            placeholder: "Yes",
            question_id:''
          },
          {
            key: 2,
            value: "ไม่ใช่",
            placeholder: "No",
            question_id:''
          },
        ],
        options: [
          {
            key: this.$utils("mockingID", 15),
            th: "",
            en: "",
            feeling: "Positive",
            question_id:''
          },
          {
            key: this.$utils("mockingID", 15),
            th: "",
            en: "",
            feeling: "Neutral",
            question_id:''
          },
          {
            key: this.$utils("mockingID", 15),
            th: "",
            en: "",
            feeling: "Negative",
            question_id:''
          },
        ],
        less_txt: "",
        less_txts: {},
        less_txt_en: "",
        max_txts:{},
        max_txt: "",
        max_txt_en: "",
        max: 10,
        min: 0,
        rolling:false,
        report_card: false,
        report_card_label: "",
        culture_index: false
      };
      if(this.language.length>0){
        for(const i in this.language){
     
          if(this.dynamicValidateForm.options_YN&&!this.dynamicValidateForm.options_YN[`${this.language[i].code}`]&&this.language[i].code!='en'&&this.language[i].code!='th'){
          const arr=[
          {
            key: 1,
            value: "",
            placeholder: "Yes",
            question_id:'',
          },
          {
            key: 2,
            value: "",
            placeholder: "No",
            question_id:'',
          },
        ]
        //this.dynamicValidateForm.options_YN[`${this.language[i].code}`]=JSON.parse(JSON.stringify(arr));
        this.dynamicValidateForm[`options_${this.language[i].code}`]=JSON.parse(JSON.stringify(arr));
        }
      }

      }

      this.disabledRowHeader = true
      this.changeDataToCascader();
    },
    retrunTypName(keyDimation) {
      const dimension = _.filter(this.optionsCategory, {
        key: keyDimation,
      }).map((v) => v.category);
      const imageList = [
        {
          imageName: "email-header-ambassardorship",
          name: "Ambassadorship",
        },
        {
          imageName: "email-header-companyalignment",
          name: "Company Alignment",
        },
        {
          imageName: "email-header-feedback",
          name: "Feedback",
        },
        {
          imageName: "email-header-happiness",
          name: "Happiness",
        },
        {
          imageName: "email-header-introspection",
          name: "Introspection",
        },
        {
          imageName: "email-header-recognition",
          name: "Recognition",
        },
        {
          imageName: "email-header-relationshipwithcolleagues",
          name: "Relationship with Colleagues",
        },
        {
          imageName: "email-header-relationshipmanager",
          name: "Relationship with Manager",
        },
        {
          imageName: "email-header-satisfaction",
          name: "Satisfaction",
        },
        {
          imageName: "email-header-wellness",
          name: "Wellness",
        },
        {
          imageName: "email-header-personalgrowth",
          name: "Personal Growth",
        },
      ];
      const pic = _.filter(imageList, { name: dimension[0] }).map(
        (v) => v.imageName
      );
      const images = require.context(
        "@/assets/images/questions/",
        false,
        /\.png$/
      );
      return images("./" + pic[0] + ".png");
    },
    async saveQuestion() {
      var option_other = [];
      if(this.dynamicValidateForm.report_card == true) {
        if(!this.dynamicValidateForm.report_card_label.replace(/ /g, "")){
          this.$message({
            showClose: true,
            message: this.$t("questions.toast_include_row_header"),
            type: "error",
          });
          return false;
        }
      }
      if (this.dynamicValidateForm.type === "Multiple Choice(other)") {

        for(const i in this.dynamicValidateForm.options){
          // console.log('i',i)
          // console.log('this.dynamicValidateForm.options[i]',this.dynamicValidateForm.options[i])
          for(const j in this.language){
            if(this.dynamicValidateForm.options[i][this.language[j].code]){
              if(!this.dynamicValidateForm.options[i][this.language[j].code].replace(/ /g, "")){
                this.$message({
                  showClose: true,
                  message: `${this.$t("questions.toast_input_option_prefix")} ${this.language[j].text}`,
                  type: "error",
                });
                return false;
              }
            }else{
              this.$message({
                showClose: true,
                message: `${this.$t("questions.toast_input_option_prefix")} ${this.language[j].text}`,
                type: "error",
              });
              return false;
            }

          }
        }



        // option_other = _.filter(
        //   this.dynamicValidateForm.options,
        //   function (currentObject) {
        //     return (
        //       !currentObject.en.replace(/ /g, "") ||
        //       !currentObject.th.replace(/ /g, "")
        //     );
        //   }
        // );
        // console.log('option_other',option_other)
      }
      if(this.dynamicValidateForm.questions){
        for(const i in this.language){

        if ( !this.dynamicValidateForm.questions[this.language[i].code] ||
        !this.dynamicValidateForm.questions[this.language[i].code].trim()) {
          this.$message({
            showClose: true,
            message: `${this.$t("questions.toast_include_version_prefix")} ${this.language[i].text} ${this.$t("questions.toast_include_version_suffix")}`,
            type: "error",
          });
          return false;
        }
        }

      }
  
      if (!this.dynamicValidateForm.category.replace(/ /g, "")) {
        this.$message({
          showClose: true,
          message: this.$t("questions.toast_select_dimension"),
          type: "error",
        });
        return false;
      } else if (!this.dynamicValidateForm.type.replace(/ /g, "")) {
        this.$message({
          showClose: true,
          message: this.$t("questions.toast_select_question_format"),
          type: "error",
        });
        return false;
      }
      // else if (!this.dynamicValidateForm.questions.en.replace(/ /g, "")) {
      //   this.$message({
      //     showClose: true,
      //     message: "Please input question",
      //     type: "error",
      //   });
      //   return false;
      // } else if (!this.engonly &&(!this.dynamicValidateForm.question_th.replace(/ /g, ""))) {
      //   this.$message({
      //     showClose: true,
      //     message: "Please include a Thai version for your question",
      //     type: "error",
      //   });
      //   return false;
      // } 
      else if (!this.dynamicValidateForm.coins.replace(/ /g, "")) {
        this.$message({
          showClose: true,
          message: this.$t("questions.toast_input_coins"),
          type: "error",
        });
        return false;
      } else if (!this.dynamicValidateForm.frequency.replace(/ /g, "")) {
        this.$message({
          showClose: true,
          message: this.$t("questions.toast_select_frequency"),
          type: "error",
        });
        return false;
      } else if (!this.dynamicValidateForm.dayofweek.replace(/ /g, "")&&!this.dynamicValidateForm.frequency=='Follow-up') {
        this.$message({
          showClose: true,
          message: this.$t("questions.toast_select_day_of_week"),
          type: "error",
        });
        return false;
      } else if (!this.dynamicValidateForm.schedule.replace(/ /g, "")&&!this.dynamicValidateForm.frequency=='Follow-up') {
        this.$message({
          showClose: true,
          message: this.$t("questions.toast_select_schedule"),
          type: "error",
        });
        return false;
      } else if (this.dynamicValidateForm.type === "Multiple Choice") {
        if (
          (this.dynamicValidateForm.feelingY == "Positive" &&
            this.dynamicValidateForm.feelingN == "Negative") ||
          (this.dynamicValidateForm.feelingY == "Negative" &&
            this.dynamicValidateForm.feelingN == "Positive") ||
          (this.dynamicValidateForm.feelingY == "Neutral" &&
            this.dynamicValidateForm.feelingN == "Neutral")
        ) {
          return this.createQuestionFromForm();
        } else {
          this.$message({
            showClose: true,
            message: this.$t("questions.toast_yes_no_assignment_required"),
            type: "error",
          });
          return false;
        }
      } else if (this.dynamicValidateForm.type === "Multiple Choice(other)") {
        var negative = _.filter(
          this.dynamicValidateForm.options,
          function (currentObject) {
            return currentObject.feeling === "Negative";
          }
        );
        var positive = _.filter(
          this.dynamicValidateForm.options,
          function (currentObject) {
            return currentObject.feeling === "Positive";
          }
        );
        var neutral = _.filter(
          this.dynamicValidateForm.options,
          function (currentObject) {
            return currentObject.feeling === "Neutral";
          }
        );
        var feelingisEmtry = _.filter(
          this.dynamicValidateForm.options,
          function (currentObject) {
            return !currentObject.feeling.replace(/ /g, "");
          }
        );
        if (feelingisEmtry.length > 0) {
          this.$message({
            showClose: true,
            message: this.$t("questions.toast_multi_choice_assignment_required"),
            type: "error",
          });
          return false;
        } else {
          if (
            neutral.length === this.dynamicValidateForm.options.length &&
            neutral.length > 0
          ) {
            return this.createQuestionFromForm();
          } else if (positive.length > 0 && negative.length > 0) {
            return this.createQuestionFromForm();
          } else {
            this.$message({
              showClose: true,
              message: this.$t("questions.toast_multi_choice_assignment_required"),
              type: "error",
            });
            return false;
          }
        }
      } else if (this.dynamicValidateForm.type === "Scale") {
        // if (
        //   !this.engonly &&
        //   (!this.dynamicValidateForm.less_txt.replace(/ /g, "") ||
        //     !this.dynamicValidateForm.less_txt_en.replace(/ /g, "") ||
        //     !this.dynamicValidateForm.max_txt.replace(/ /g, "") ||
        //     !this.dynamicValidateForm.max_txt_en.replace(/ /g, ""))
        // ) {
        //   this.$message({
        //     showClose: true,
        //     message: "Please include a Thai version for your question",
        //     type: "error",
        //   });
        //   return false;
        // }
        for(const i in this.language){
          
          if (!this.dynamicValidateForm.less_txts[this.language[i].code] || 
          !this.dynamicValidateForm.less_txts[this.language[i].code].trim()||
          !this.dynamicValidateForm.max_txts[this.language[i].code] || 
          !this.dynamicValidateForm.max_txts[this.language[i].code].trim()){
            this.$message({
            showClose: true,
            message: this.$t("questions.toast_input_min_max_label"),
            type: "error",
            });
            return false;
          }
        }
        return this.createQuestionFromForm();


        // if (!this.dynamicValidateForm.less_txts.en.replace(/ /g, "") ||
        //     !this.dynamicValidateForm.max_txts.en.replace(/ /g, "")
        // ) {
        //   this.$message({
        //     showClose: true,
        //     message: "Please input min/max Label",
        //     type: "error",
        //   });
        //   return false;
        // } else if (!this.engonly && (!this.dynamicValidateForm.less_txt.replace(/ /g, "") ||
        //     !this.dynamicValidateForm.max_txt.replace(/ /g, ""))){
        //   this.$message({
        //     showClose: true,
        //     message: "Please input min/max Label in Thai version",
        //     type: "error",
        //   });
        //   return false;
        // } else {
        //   this.$store.dispatch("custom/CreateUpdateQuestion", {
        //     form: this.dynamicValidateForm,
        //   });
        //   this.cancelAdd();
        //   this.$message({
        //     type: "success",
        //     message: "Your custom question has been created",
        //   });
        // }
      } else {
        return this.createQuestionFromForm();
      }
    },
    async createQuestionFromForm() {
      if (this.isSavingQuestion) {
        return false;
      }

      const formSnapshot = _.cloneDeep(this.dynamicValidateForm);
      this.isSavingQuestion = true;

      try {
        const result = await this.$store.dispatch("custom/CreateUpdateQuestion", {
          form: this.dynamicValidateForm,
        });
        this.recentlyAddedQuestionId = result && result.id ? result.id : null;
        this.savedQuestionSummary = this.buildSavedQuestionSummary(result, formSnapshot);
        this.questionSaveSuccess = true;
        this.refreshCalendarCacheAfterSave();
        return true;
      } catch (error) {
        console.error("Error creating custom question:", error);
        this.$message({
          showClose: true,
          message: this.$t("questions.toast_custom_question_create_failed"),
          type: "error",
        });
        return false;
      } finally {
        this.isSavingQuestion = false;
      }
    },
    buildSavedQuestionSummary(result, formSnapshot) {
      const savedQuestion = result && result.question ? result.question : {};
      const questionText =
        (formSnapshot.questions && formSnapshot.questions.en) ||
        savedQuestion.question_en ||
        savedQuestion.question ||
        "";
      const isProgramOnly =
        formSnapshot.frequency === "Program-only" ||
        savedQuestion.repeats === "Program-only";
      const isFollowUp =
        !isProgramOnly &&
        (formSnapshot.frequency === "Follow-up" || savedQuestion.parent === "Y");
      const isParked = isFollowUp || isProgramOnly;

      return {
        id: result && result.id ? result.id : null,
        question: questionText,
        typeName: this.getQuestionTypeLabelFromForm(formSnapshot),
        frequencyLabel: this.getFrequencyLabelFromForm(formSnapshot),
        scheduleLabel: isParked ? "" : this.getScheduleLabelFromForm(formSnapshot),
        isFollowUp,
        isProgramOnly,
        isParked,
      };
    },
    getQuestionTypeLabelFromForm(formData) {
      if (!formData || !formData.type) return "";
      if (formData.type === "Multiple Choice") return this.$t("questions.Yes_or_No");
      if (formData.type === "Multiple Choice(other)") return this.$t("questions.Multiple_Choice");
      if (formData.type === "Text Area Field") return this.$t("questions.open_ended");
      return this.$t("questions.Scale");
    },
    getFrequencyLabelFromForm(formData) {
      if (!formData || !formData.frequency) return "";
      const labels = {
        onetime: this.$t("category.frequency.one_time"),
        weekly: this.$t("category.frequency.weekly"),
        monthly: this.$t("category.frequency.monthly"),
        quarterly: this.$t("category.frequency.quarterly"),
        biannually: this.$t("category.frequency.biannually"),
        yearly: this.$t("category.frequency.yearly"),
        "Follow-up": this.$t("questions.follow_up"),
        "Program-only": this.$t("questions.program_only"),
      };
      return labels[formData.frequency] || formData.frequency;
    },
    getScheduleLabelFromForm(formData) {
      if (
        !formData ||
        !formData.frequency ||
        formData.frequency === "Follow-up" ||
        formData.frequency === "Program-only"
      ) {
        return "";
      }

      if (formData.frequency === "onetime" && formData.schedule) {
        try {
          return format(formData.schedule, "dddd, MMMM D, YYYY");
        } catch (error) {
          return `${formData.schedule}`;
        }
      }

      const dayLabel = this.getDayLabel(formData.dayofweek);
      if (formData.frequency === "weekly" && dayLabel) {
        return `${this.$t("questions.every")} ${dayLabel}`;
      }

      if (formData.frequency === "monthly" && dayLabel) {
        return `${this.getWeekLabel(formData.schedule)} ${dayLabel} ${this.$t("questions.of_each_month")}`;
      }

      return "";
    },
    getDayLabel(day) {
      if (!day) return "";
      const labels = {
        monday: this.$t("category.day_of_week.monday"),
        tuesday: this.$t("category.day_of_week.tuesday"),
        wednesday: this.$t("category.day_of_week.wednesday"),
        thursday: this.$t("category.day_of_week.thursday"),
        friday: this.$t("category.day_of_week.friday"),
        saturday: this.$t("category.day_of_week.saturday"),
        sunday: this.$t("category.day_of_week.sunday"),
      };
      return labels[day] || day.charAt(0).toUpperCase() + day.slice(1);
    },
    getWeekLabel(schedule) {
      const labels = {
        week1: this.$t("questions.first"),
        week2: this.$t("questions.second"),
        week3: this.$t("questions.third"),
        week4: this.$t("questions.fourth"),
        week5: this.$t("questions.fifth"),
      };
      return labels[schedule] || "";
    },
    async refreshCalendarCacheAfterSave() {
      this.calendarReady = false;
      try {
        await this.ensureCalendarReady({
          forceRefresh: true,
          showLoader: false,
        });
        if (this.calendarModal) {
          this.getQuestionPerMonth();
          this.addDataToArr();
        }
      } catch (error) {
        console.error("Error refreshing calendar after question save:", error);
        this.calendarReady = false;
      }
    },
    clearQuestionSuccessState() {
      this.questionSaveSuccess = false;
      this.savedQuestionSummary = null;
      if (!this.isOpeningSavedQuestionCalendar) {
        this.recentlyAddedQuestionId = null;
      }
    },
    startAnotherQuestion() {
      this.clearQuestionSuccessState();
      this.cancelAdd();
    },
    finishQuestionSuccess() {
      this.clearQuestionSuccessState();
      this.dialogFormVisible = false;
    },
    async viewSavedQuestionInCalendar() {
      if (!this.savedQuestionSummary || this.savedQuestionSummary.isFollowUp) {
        return;
      }

      const questionId = this.savedQuestionSummary.id;
      this.isOpeningSavedQuestionCalendar = true;
      this.recentlyAddedQuestionId = questionId;
      this.calendarModalMounted = true;
      this.calendarModal = true;
      this.dialogFormVisible = false;

      try {
        await this.ensureCalendarReady({
          forceRefresh: true,
          showLoader: true,
        });
        const targetDate = this.findFirstScheduledDateForSavedQuestion(questionId);
        this.getDateData(targetDate || new Date());
        this.getQuestionPerMonth();
        this.addDataToArr();
        this.selectCalendarDateForQuestion(questionId, targetDate);
        this.questionSaveSuccess = false;
        this.savedQuestionSummary = null;
      } catch (error) {
        console.error("Error opening saved question in calendar:", error);
        this.$message({
          showClose: true,
          message: this.$t("questions.toast_open_calendar_failed"),
          type: "error",
        });
      } finally {
        this.isOpeningSavedQuestionCalendar = false;
      }
    },
    findFirstScheduledDateForSavedQuestion(questionId) {
      if (!questionId) return null;

      const today = this.buildCalendarDate(
        format(new Date(), "YYYY"),
        format(new Date(), "M"),
        format(new Date(), "D")
      );

      for (let offset = 0; offset < 18; offset++) {
        const probeDate = new Date(today.getFullYear(), today.getMonth() + offset, 1, 12, 0, 0, 0);
        this.getDateData(probeDate);
        this.getQuestionPerMonth();
        const question = _.find(this.questionsPerMonth, function (item) {
          return item.key === questionId;
        });

        if (question && question.schedule && question.schedule.length > 0) {
          const scheduledDates = question.schedule
            .map((value) => this.parseCalendarScheduleDate(value))
            .filter((value) => value)
            .sort((a, b) => a.getTime() - b.getTime());
          const nextDate = scheduledDates.find((value) => value.getTime() >= today.getTime());
          if (nextDate) {
            return nextDate;
          }
        }
      }

      return null;
    },
    parseCalendarScheduleDate(value) {
      if (!value || typeof value !== "string") {
        return null;
      }

      const parts = value.split("/");
      if (parts.length !== 3) {
        return null;
      }

      return this.buildCalendarDate(parts[2], parts[1], parts[0]);
    },
    selectCalendarDateForQuestion(questionId, targetDate) {
      if (!questionId || !targetDate) {
        return;
      }

      const targetDay = parseInt(format(targetDate, "D"));
      const targetMonth = parseInt(format(targetDate, "M"));
      const targetYear = parseInt(format(targetDate, "YYYY"));
      const target = _.find(this.calendarData, function (data) {
        return (
          parseInt(data.dayOfMonth) === targetDay &&
          parseInt(data.month) === targetMonth &&
          parseInt(data.year) === targetYear &&
          data.questionList &&
          _.find(data.questionList, { id: questionId })
        );
      });

      if (target) {
        this.selectedDay = `${target.dayOfMonth}_${target.id}`;
        this.addDataToTableOfModal();
      }
    },
    calendarDayHasRecentQuestion(data) {
      if (!this.recentlyAddedQuestionId || !data || !data.questionsShowing) {
        return false;
      }

      return data.questionsShowing.some((question) => question.id === this.recentlyAddedQuestionId);
    },
    addOptions() {
      if (this.dynamicValidateForm.options.length < 6) {
        if (this.dynamicValidateForm.options.length === 2) {
          this.dynamicValidateForm.options.push({
            key: this.$utils("mockingID", 15),
            feeling: "Negative",
            en: "",
            th: "",
            question_id:'',
          });
        } else {
          this.dynamicValidateForm.options.push({
            key: this.$utils("mockingID", 15),
            feeling: "",
            en: "",
            th: "",
            question_id:'',
          });
        }
      }
    },
    deleteOptions(value) {
      this.dynamicValidateForm.options = _.filter(
        this.dynamicValidateForm.options,
        function (currentObject) {
          return currentObject.key !== value.key;
        }
      );
    },
    cancelDialog() {
      this.dialogFormVisible = false;
      if (!this.isOpeningSavedQuestionCalendar) {
        this.clearQuestionSuccessState();
      }
      if (this.$refs["dynamicValidateForm"]) {
        this.$refs["dynamicValidateForm"].clearValidate();
      }
      this.$store.commit("question/setPageOT", 1);
      this.$store.dispatch("question/getScheduleOptions", {
        end_row: "",
      });
    },
    handleSelect(key, keyPath) {
      this.activeIndex = key;
    },
    scheduleChange() {
      if (this.dynamicValidateForm.frequency == "onetime") {
        this.$store.dispatch("question/getScheduleOptions", {
          end_row: "",
          dayofweek: this.dynamicValidateForm.dayofweek,
        });
        this.dynamicValidateForm.schedule = "";
      } else if (this.dynamicValidateForm.frequency == "weekly") {
        this.dynamicValidateForm.schedule = this.dynamicValidateForm.dayofweek;
      } else if (this.dynamicValidateForm.frequency == "monthly") {
        this.dynamicValidateForm.schedule = "";
      } else if (this.dynamicValidateForm.frequency == "quarterly") {
        let day =
          this.dynamicValidateForm.dayofweek.charAt(0).toUpperCase() +
          this.dynamicValidateForm.dayofweek.slice(1);
        this.options_quarterly = [
          {
            label: "First month of the quarter",
            options: [
              {
                value: "1_week1",
                label: "First " + day + " of the first month of the quarter",
              },
              {
                value: "1_week2",
                label: "Second " + day + " of the first month of the quarter",
              },
              {
                value: "1_week3",
                label: "Third " + day + " of the first month of the quarter",
              },
              {
                value: "1_week4",
                label: "Fourth " + day + " of the first month of the quarter",
              },
            ],
          },
          {
            label: "Second month of the quarter",
            options: [
              {
                value: "2_week1",
                label: "First " + day + " of the second month of the quarter",
              },
              {
                value: "2_week2",
                label: "Second " + day + " of the second month of the quarter",
              },
              {
                value: "2_week3",
                label: "Third " + day + " of the second month of the quarter",
              },
              {
                value: "2_week4",
                label: "Fourth " + day + " of the second month of the quarter",
              },
            ],
          },
          {
            label: "Third month of the quarter",
            options: [
              {
                value: "3_week1",
                label: "First " + day + " of the third month of the quarter",
              },
              {
                value: "3_week2",
                label: "Second " + day + " of the third month of the quarter",
              },
              {
                value: "3_week3",
                label: "Third " + day + " of the third month of the quarter",
              },
              {
                value: "3_week4",
                label: "Fourth " + day + " of the third month of the quarter",
              },
            ],
          },
        ];
        this.dynamicValidateForm.schedule = "";
      } else if (this.dynamicValidateForm.frequency == "biannually") {
        let day =
          this.dynamicValidateForm.dayofweek.charAt(0).toUpperCase() +
          this.dynamicValidateForm.dayofweek.slice(1);
          this.options_biannually = [
          {
            label: "First month of the Q1 and Q3",
            options: [
              {
                value: "1_week1_q1_q3",
                label: "First " + day + " of the first month of Q1 and Q3",
              },{
                value: "1_week2_q1_q3",
                label: "Second " + day + " of the first month of Q1 and Q3",
              },{
                value: "1_week3_q1_q3",
                label: "Third " + day + " of the first month of Q1 and Q3",
              },{
                value: "1_week4_q1_q3",
                label: "Forth " + day + " of the first month of Q1 and Q3",
              },{
                value: "1_week5_q1_q3",
                label: "Fifth " + day + " of the first month of Q1 and Q3",
              },
            ],
          },{
            label: "Second month of the Q1 and Q3",
            options: [
              {
                value: "2_week1_q1_q3",
                label: "First " + day + " of the second month of Q1 and Q3",
              },{
                value: "2_week2_q1_q3",
                label: "Second " + day + " of the second month of Q1 and Q3",
              },{
                value: "2_week3_q1_q3",
                label: "Third " + day + " of the second month of Q1 and Q3",
              },{
                value: "2_week4_q1_q3",
                label: "Forth " + day + " of the second month of Q1 and Q3",
              },{
                value: "2_week5_q1_q3",
                label: "Fifth " + day + " of the second month of Q1 and Q3",
              },
            ],
          },{
            label: "Third month of the Q1 and Q3",
            options: [
              {
                value: "3_week1_q1_q3",
                label: "First " + day + " of the third month of Q1 and Q3",
              },{
                value: "3_week2_q1_q3",
                label: "Second " + day + " of the third month of Q1 and Q3",
              },{
                value: "3_week3_q1_q3",
                label: "Third " + day + " of the third month of Q1 and Q3",
              },{
                value: "3_week4_q1_q3",
                label: "Forth " + day + " of the third month of Q1 and Q3",
              },{
                value: "3_week5_q1_q3",
                label: "Fifth " + day + " of the third month of Q1 and Q3",
              },
            ],
          },{
            label: "First month of the Q2 and Q4",
            options: [
              {
                value: "1_week1_q2_q4",
                label: "First " + day + " of the first month of Q2 and Q4",
              },{
                value: "1_week2_q2_q4",
                label: "Second " + day + " of the first month of Q2 and Q4",
              },{
                value: "1_week3_q2_q4",
                label: "Third " + day + " of the first month of Q2 and Q4",
              },{
                value: "1_week4_q2_q4",
                label: "Forth " + day + " of the first month of Q2 and Q4",
              },{
                value: "1_week5_q2_q4",
                label: "Fifth " + day + " of the first month of Q2 and Q4",
              },
            ],
          },{
            label: "Second month of the Q2 and Q4",
            options: [
              {
                value: "2_week1_q2_q4",
                label: "First " + day + " of the second month of Q2 and Q4",
              },{
                value: "2_week2_q2_q4",
                label: "Second " + day + " of the second month of Q2 and Q4",
              },{
                value: "2_week3_q2_q4",
                label: "Third " + day + " of the second month of Q2 and Q4",
              },{
                value: "2_week4_q2_q4",
                label: "Forth " + day + " of the second month of Q2 and Q4",
              },{
                value: "2_week5_q2_q4",
                label: "Fifth " + day + " of the second month of Q2 and Q4",
              },
            ],
          },{
            label: "Third month of the Q2 and Q4",
            options: [
              {
                value: "3_week1_q2_q4",
                label: "First " + day + " of the third month of Q2 and Q4",
              },{
                value: "3_week2_q2_q4",
                label: "Second " + day + " of the third month of Q2 and Q4",
              },{
                value: "3_week3_q2_q4",
                label: "Third " + day + " of the third month of Q2 and Q4",
              },{
                value: "3_week4_q2_q4",
                label: "Forth " + day + " of the third month of Q2 and Q4",
              },{
                value: "3_week5_q2_q4",
                label: "Fifth " + day + " of the third month of Q2 and Q4",
              },
            ],
          },
        ]
        this.dynamicValidateForm.schedule = "";
      } else if (this.dynamicValidateForm.frequency == "yearly") {
        let day =
          this.dynamicValidateForm.dayofweek.charAt(0).toUpperCase() +
          this.dynamicValidateForm.dayofweek.slice(1);
          this.options_yearly = []
          for(let i = 1; i < 13; i++){
            let month = format(`2023-${i}-01`, 'MMMM')
            this.options_yearly.push({
              label: month,
              options: [
                {
                  value: month + "_week1",
                  label: "First " + day + " of " + month,
                },{
                  value: month + "_week2",
                  label: "Second " + day + " of " + month,
                },{
                  value: month + "_week3",
                  label: "Third " + day + " of " + month,
                },{
                  value: month + "_week4",
                  label: "Forth " + day + " of " + month,
                },{
                  value: month + "_week5",
                  label: "Fifth " + day + " of " + month,
                },
              ],
            })
          }
          this.dynamicValidateForm.schedule = "";
      }
    },
    moreOptionClick() {
      this.$store.dispatch("question/nextOnetimeSchedule", {
        dayofweek: this.dynamicValidateForm.dayofweek,
      });
    },
    onInputQuestionEn(text) {
      if (this.engonly) {
        this.dynamicValidateForm.question_th = text;
      }
    },
    async openCalendarModal() {
      if (this.calendarModal) {
        this.calendarModal = false;
        return;
      }

      this.calendarModalMounted = true;
      this.calendarModal = true;
      await this.ensureCalendarReady();
      this.getQuestionPerMonth();
      this.addDataToArr({ syncIntensityBlocks: false });
    },
    async refreshCalendar() {
      if (this.isRefreshingCalendar) return; // Prevent multiple clicks

      this.isRefreshingCalendar = true;

      // Fetch fresh data in background without showing loading spinner
      await this.ensureCalendarReady({
        forceRefresh: true,
        showLoader: false,
      });

      // Navigate to current month/year
      this.getDateData(new Date());

      // Refresh question data
      this.getQuestionPerMonth();
      this.addDataToArr({ syncIntensityBlocks: false });

      this.isRefreshingCalendar = false;
    },
    filterCalendarQuestionsForDay(questions) {
      if (!Array.isArray(questions) || questions.length === 0) return [];
      if (!this.hasConfiguredIntensity) return questions;

      const cadence = this.currentIntensityCadence;
      if (cadence === "intense") return questions;

      const targets = CADENCE_TARGETS[cadence] || {};
      const dailyCap = targets.questions_per_day && targets.questions_per_day.max
        ? targets.questions_per_day.max
        : questions.length;

      const essentials = questions.filter((q) => q && q.notblock);
      const nonEssentials = questions.filter((q) => !(q && q.notblock));

      const selected = [...essentials];
      const limit = Math.max(dailyCap, essentials.length);

      for (const q of nonEssentials) {
        if (selected.length >= limit) break;
        selected.push(q);
      }
      // Keep deterministic ordering in the day cell.
      return _.sortBy(selected, ["order"]);
    },
    isCalendarQuestionDisabled(day, question) {
      if (!question) return false;
      if (question.status === false) return true;
      if (!this.hasConfiguredIntensity) return false;
      if (this.currentIntensityCadence === "intense") return false;
      const selected = Array.isArray(day && day.questionsSelectedByIntensity)
        ? day.questionsSelectedByIntensity
        : [];
      return !selected.some((q) => q && q.id === question.id);
    },
    getOpenEndedBadgeCount(day) {
      const list = Array.isArray(day && day.questionList) ? day.questionList : [];
      return list.filter((q) => q && q.status === true && this.isOpenEndedQuestion(q)).length;
    },
    isOpenEndedQuestion(question) {
      if (!question) return false;
      const type = String(question.questionsType || question.type_name || "").toLowerCase();
      return type === "open-ended" || type === "open_ended" || type === "text area field";
    },
    enforceWeeklyOpenEndedQuota() {
      if (!this.hasConfiguredIntensity || !Array.isArray(this.calendarData) || this.calendarData.length === 0) return;

      const cadence = this.currentIntensityCadence;
      const targets = CADENCE_TARGETS[cadence] || {};
      const weeklyTarget = targets.open_ended_per_week;
      if (weeklyTarget === null || weeklyTarget === undefined) return;

      const dailyCap = targets.questions_per_day && targets.questions_per_day.max
        ? targets.questions_per_day.max
        : Number.POSITIVE_INFINITY;

      for (let i = 0; i < this.calendarData.length; i += 7) {
        const weekCells = this.calendarData.slice(i, i + 7);
        const weekDays = weekCells.filter((day) => {
          return (
            day &&
            day.month === this.calendarShowing.month &&
            Array.isArray(day.questionsSelectedByIntensity) &&
            Array.isArray(day.questionList)
          );
        });
        if (!weekDays.length) continue;

        let openEndedEntries = [];
        weekDays.forEach((day) => {
          (day.questionsSelectedByIntensity || []).forEach((question, idx) => {
            if (this.isOpenEndedQuestion(question)) {
              openEndedEntries.push({ day, idx, question });
            }
          });
        });

        // If over target, remove non-essential open-ended from the end first.
        if (openEndedEntries.length > weeklyTarget) {
          const removable = openEndedEntries.filter((entry) => !(entry.question && entry.question.notblock));
          let extra = openEndedEntries.length - weeklyTarget;
          for (let r = removable.length - 1; r >= 0 && extra > 0; r -= 1) {
            const entry = removable[r];
            const list = entry.day.questionsSelectedByIntensity || [];
            const removeIdx = list.findIndex((q) => q && q.id === entry.question.id);
            if (removeIdx !== -1) {
              list.splice(removeIdx, 1);
              extra -= 1;
            }
          }
        }

        // Re-read count after optional removals.
        let currentOpenEnded = weekDays.reduce((sum, day) => {
          return sum + (day.questionsSelectedByIntensity || []).filter((q) => this.isOpenEndedQuestion(q)).length;
        }, 0);

        // If under target, add open-ended where possible.
        let missing = Math.max(0, weeklyTarget - currentOpenEnded);
        if (missing > 0) {
          for (const day of weekDays) {
            if (missing <= 0) break;
            const currentList = day.questionsSelectedByIntensity || [];
            const availableOpenEnded = (day.questionList || []).filter((q) => {
              if (!q) return false;
              if (!this.isOpenEndedQuestion(q)) return false;
              return !currentList.some((existing) => existing && existing.id === q.id);
            });
            if (!availableOpenEnded.length) continue;

            for (const openEndedQuestion of availableOpenEnded) {
              if (missing <= 0) break;

              if (currentList.length < dailyCap) {
                currentList.push(openEndedQuestion);
                missing -= 1;
                continue;
              }

              // Replace a non-essential non-open-ended question when day is full.
              const replaceIndex = currentList.findIndex((q) => {
                if (!q) return false;
                if (q.notblock) return false;
                return !this.isOpenEndedQuestion(q);
              });
              if (replaceIndex !== -1) {
                currentList.splice(replaceIndex, 1, openEndedQuestion);
                missing -= 1;
              }
            }
          }
        }

        weekDays.forEach((day) => {
          day.questionsSelectedByIntensity = _.sortBy(day.questionsSelectedByIntensity || [], ["order"]);
        });
      }

      // Keep warning badge accurate after weekly balancing.
      this.calendarData = this.calendarData.map((day) => {
        if (!day || !Array.isArray(day.questionsSelectedByIntensity)) return day;
        return {
          ...day,
          openEndedCount: day.questionsSelectedByIntensity.filter((q) => this.isOpenEndedQuestion(q)).length,
        };
      });
    },
    collectProjectedDaysForIntensitySync(monthsHorizon = 12) {
      const snapshot = {
        calendarShowing: { ...this.calendarShowing },
        selectedDay: this.selectedDay,
        calendarData: this.calendarData,
        questionsPerMonth: this.questionsPerMonth,
        questionPreviewText: this.questionPreviewText,
      };

      const projectedDays = [];
      try {
        const baseYear = Number(snapshot.calendarShowing.year);
        const baseMonthIndex = Number(snapshot.calendarShowing.month) - 1;
        for (let offset = 0; offset < monthsHorizon; offset += 1) {
          const projectionDate = new Date(baseYear, baseMonthIndex + offset, 1);
          this.getDateData(projectionDate);
          this.getQuestionPerMonth();
          this.addDataToArr();

          const month = this.calendarShowing.month;
          const year = String(this.calendarShowing.year);
          (this.calendarData || []).forEach((day) => {
            if (!day) return;
            if (day.month !== month) return;
            if (String(day.year) !== year) return;
            if (!Array.isArray(day.questionList)) return;
            projectedDays.push(day);
          });
        }
      } finally {
        this.calendarShowing = { ...snapshot.calendarShowing };
        this.selectedDay = snapshot.selectedDay;
        this.calendarData = snapshot.calendarData;
        this.questionsPerMonth = snapshot.questionsPerMonth;
        this.questionPreviewText = snapshot.questionPreviewText;
      }

      return projectedDays;
    },
    async syncFilteredQuestionsToBlockList() {
      if (this.skipIntensityBlockSyncOnce) {
        this.skipIntensityBlockSyncOnce = false;
        return;
      }
      if (this.syncingIntensityBlocks) return;
      if (!this.hasConfiguredIntensity || !this.user || !this.user.company) return;

      const cadence = this.currentIntensityCadence;
      this.syncingIntensityBlocks = true;
      try {
        const existingSnapshot = await firebase.firestore()
          .collection("block_questions")
          .where("company", "==", this.user.company)
          .get();

        const existingBlockedIds = new Set();
        const autoManagedDocs = [];
        const autoManagedQuestIds = new Set();
        existingSnapshot.forEach((doc) => {
          const data = doc.data() || {};
          if (data.quest_id) {
            existingBlockedIds.add(data.quest_id);
            if (data.source === "intensity_calendar_filter" || data.cadence) {
              autoManagedDocs.push({ id: doc.id, ref: doc.ref, quest_id: data.quest_id });
              autoManagedQuestIds.add(data.quest_id);
            }
          }
        });

        if (cadence === "intense") {
          if (!autoManagedDocs.length) {
            this.intensityBlockSyncSignature = "";
            return;
          }
          const removeSignature = `intense:unblock:${autoManagedDocs.map((doc) => doc.id).sort().join(",")}`;
          if (removeSignature === this.intensityBlockSyncSignature) return;

          const batchSize = 400;
          for (let i = 0; i < autoManagedDocs.length; i += batchSize) {
            const chunk = autoManagedDocs.slice(i, i + batchSize);
            const batch = firebase.firestore().batch();
            chunk.forEach((entry) => {
              batch.delete(entry.ref);
            });
            await batch.commit();
          }

          this.intensityBlockSyncSignature = removeSignature;
          await Promise.all([
            this.$store.dispatch("question/getQuestions", { user: this.user, forceRefresh: true }),
            this.$store.dispatch("custom/getQuestionsCustom", { user: this.user, forceRefresh: true }),
          ]);
          this.getQuestionPerMonth();
          this.skipIntensityBlockSyncOnce = true;
          this.addDataToArr();
          return;
        }

        const projectedDays = this.collectProjectedDaysForIntensitySync(12);
        if (!projectedDays.length) return;

        const enabledCandidates = new Set();
        const excludedIds = new Set();
        projectedDays.forEach((day) => {
          const dayQuestionList = _.sortBy(day.questionList || [], ["order"]);
          const selectedSetForDay = new Set(
            (day.questionsSelectedByIntensity || [])
              .filter((q) => q && q.id)
              .map((q) => q.id)
          );
          dayQuestionList.forEach((question) => {
            if (!question || !question.id) return;
            if (question.notblock) return; // never auto-disable essentials
            if (question.status !== true && !autoManagedQuestIds.has(question.id)) return;
            enabledCandidates.add(question.id);
            if (!selectedSetForDay.has(question.id)) {
              excludedIds.add(question.id);
            }
          });
        });

        const toBlockIds = Array.from(enabledCandidates).filter((id) => excludedIds.has(id));
        const pendingBlockIds = toBlockIds.filter((id) => !existingBlockedIds.has(id));

        // Auto-unblock only questions managed by intensity that are no longer excluded.
        const autoUnblockDocs = autoManagedDocs.filter((entry) => !excludedIds.has(entry.quest_id));

        const signature = `${cadence}:block:${pendingBlockIds.slice().sort().join(",")}:unblock:${autoUnblockDocs.map((doc) => doc.id).sort().join(",")}`;
        if (signature === this.intensityBlockSyncSignature) return;
        if (!pendingBlockIds.length && !autoUnblockDocs.length) {
          this.intensityBlockSyncSignature = signature;
          return;
        }

        const now = format(new Date(), "YYYY-MM-DD HH:mm:ss");
        const batchSize = 400;
        const totalWork = Math.max(pendingBlockIds.length, autoUnblockDocs.length);
        for (let i = 0; i < totalWork; i += batchSize) {
          const blockChunk = pendingBlockIds.slice(i, i + batchSize);
          const unblockChunk = autoUnblockDocs.slice(i, i + batchSize);
          const batch = firebase.firestore().batch();

          blockChunk.forEach((questId) => {
            const ref = firebase.firestore().collection("block_questions").doc();
            batch.set(ref, {
              quest_id: questId,
              company: this.user.company,
              email_updated: this.user.email || "",
              createdate: now,
              source: "intensity_calendar_filter",
              cadence,
            });
          });
          unblockChunk.forEach((entry) => {
            batch.delete(entry.ref);
          });
          await batch.commit();
        }

        this.intensityBlockSyncSignature = signature;
        await Promise.all([
          this.$store.dispatch("question/getQuestions", { user: this.user, forceRefresh: true }),
          this.$store.dispatch("custom/getQuestionsCustom", { user: this.user, forceRefresh: true }),
        ]);

        // Rebuild calendar once with updated block statuses.
        this.getQuestionPerMonth();
        this.skipIntensityBlockSyncOnce = true;
        this.addDataToArr();
      } catch (error) {
        console.error("Failed to sync filtered questions to block_questions:", error);
      } finally {
        this.syncingIntensityBlocks = false;
      }
    },
    addDataToArr({ syncIntensityBlocks = true } = {}) {
      let firstDayOfWeek = this.calendarShowing.firstDayOfWeek;
      this.calendarData = [];
      let dayOfPastMonth =
        this.calendarShowing.lastDayOfPastMonth - firstDayOfWeek + 1;
      if (firstDayOfWeek != 1) {
        for (let i = 1; i < firstDayOfWeek; i++) {
          this.calendarData.push({
            dayOfMonth: dayOfPastMonth + i,
            id: this.$utils("mockingID", 15),
            month: this.calendarShowing.month - 1,
            questionList: [],
            questionsSelectedByIntensity: [],
            openEndedCount: 0,
          });
        }
      }

      for (let i = 0; i < this.calendarShowing.lastDayOfMonth; i++) {
        let mockId = this.$utils("mockingID", 15);
        if (i + 1 == this.calendarShowing.day) {
          this.selectedDay = `${this.calendarShowing.day}_${mockId}`;
          this.addDataToTableOfModal();
        }

        let question = [];
        let questionsShowing = [];
        this.questionsPerMonth.map((value) => {
          var qusetionData = _.find(value.schedule, function (data) {
            let day = data.split("/");
            return day != null && parseInt(day) == i + 1;
          });
          if (qusetionData !== undefined) {
            question.push({
              category: value.category_name,
              questionsType: value.type_name,
              audience: value.company === "main" ? "Standard" : "Custom",
              id: value.key,
              key: value.key,
              question_en: value.question_en,
              status: value.block_status,
              notblock: value.notblock,
              repeats: value.frequency,
              power_skill: value.power_skill?value.power_skill:'',
              powerskill: value.power_skill?`${value.power_skill.slice(0,2)})`:'',
              type: (value.type) ? value.type : '',
              m_choice: (value.m_choice) ? value.m_choice : {},
              custom_type: (value.custom_type) ? value.custom_type : '',
              date: qusetionData,
              optionToName: value.optionToName,
              repeat_every: value.repeat_every,
            });
            if (value.block_status) {
              questionsShowing.push({
                category: value.category_name,
                questionsType: value.type_name,
                audience: value.company === "main" ? "Standard" : "Custom",
                id: value.key,
                key: value.key,
                question_en: value.question_en,
                status: value.block_status,
                notblock: value.notblock,
                repeats: value.frequency,
                power_skill:value.power_skill?value.power_skill:'',
                powerskill: value.power_skill?`${value.power_skill.slice(0,2)})`:'',
                type: (value.type) ? value.type : '',
                m_choice: (value.m_choice) ? value.m_choice : {},
                custom_type: (value.custom_type) ? value.custom_type : '',
                date: qusetionData,
                optionToName: value.optionToName,
                repeat_every: value.repeat_every,
              });
            }
          }
        });
       
        for(const i in questionsShowing){
          
          const date_selected = questionsShowing[i].date.replace(/\//g,'-')
          if(this.questionOrder[date_selected]){
            if(this.questionOrder[date_selected][questionsShowing[i].id]){
              questionsShowing[i]['order']=this.questionOrder[date_selected][questionsShowing[i].id].order
            }else {
              questionsShowing[i]['order']=questionsShowing[i].length
            }
          }else{
            if(questionsShowing[i].question_en=="How do you feel today?"){
              questionsShowing[i]['order']=1
            }else if(questionsShowing[i].audience=="Standard"){
              questionsShowing[i]['order']=2
            }else{
              questionsShowing[i]['order']=3
            }
          }


        }


        for(const i in question){
          
          const date_selected = question[i].date.replace(/\//g,'-')
          if(this.questionOrder[date_selected]){
            if(this.questionOrder[date_selected][question[i].id]){
              question[i]['order']=this.questionOrder[date_selected][question[i].id].order
            }else {
              question[i]['order']=question[i].length
            }
          }else{
            if(question[i].question_en=="How do you feel today?"){
              question[i]['order']=1
            }else if(question[i].audience=="Standard"){
              question[i]['order']=2
            }else{
              question[i]['order']=3
            }
          }

        }

        // Keep full enabled list for display; use filtered list for intensity selection.
        const orderedQuestionsShowing = _.orderBy(questionsShowing,'order');
        const orderedQuestionCandidates = _.orderBy(question,'order');
        const filteredQuestionsShowing = this.filterCalendarQuestionsForDay(orderedQuestionCandidates);
        const openEndedCount = orderedQuestionsShowing.filter(q => q.questionsType === 'Open-ended').length;

        if(this.dataCompany.alldays==false){
          const day = format(
            this.buildCalendarDate(
              this.calendarShowing.year,
              this.calendarShowing.month,
              i + 1
            ),
            "ddd"
          );

          if(day=='Sat'||day=='Sun'){
            this.calendarData.push({
                dayOfMonth: i + 1,
                id: mockId,
                month: this.calendarShowing.month,
                year: this.calendarShowing.year,
                questionList: [],
                questionsSelectedByIntensity: [],
                openEndedCount: 0,
            });

          }else{

            this.calendarData.push({
                dayOfMonth: i + 1,
                id: mockId,
                month: this.calendarShowing.month,
                year: this.calendarShowing.year,
                questionsShowing: orderedQuestionsShowing,
                questionsSelectedByIntensity: filteredQuestionsShowing,
                questionList: _.orderBy(question,'order'),
                openEndedCount: openEndedCount,

            });

          }


        }else{

          this.calendarData.push({
          dayOfMonth: i + 1,
          id: mockId,
          month: this.calendarShowing.month,
          year: this.calendarShowing.year,
          questionsShowing: orderedQuestionsShowing,
          questionsSelectedByIntensity: filteredQuestionsShowing,
          questionList: _.orderBy(question,'order'),
          openEndedCount: openEndedCount,
         });

        }
        
      }     
      let maxDay = Math.ceil(this.calendarData.length / 7) * 7;
      let oldValueMaxDay = this.calendarData.length;
      for (let i = oldValueMaxDay; i < maxDay; i++) {

        this.calendarData.push({
          dayOfMonth: i + 1 - oldValueMaxDay,
          id: this.$utils("mockingID", 15),
          month: this.calendarShowing.month + 1,
          year: this.calendarShowing.year,
          questionList: [],
          questionsSelectedByIntensity: [],
          openEndedCount: 0,
        });
      }
      this.enforceWeeklyOpenEndedQuota();
      if (syncIntensityBlocks) {
        // Previous client-side block_questions writer.
        // Backend sync now runs from intensity/setAdminBias after saving
        // intensity_state.
        // this.syncFilteredQuestionsToBlockList();
      }
    },
    getDateFromWeek(repeattimes, value) {
      const month =
        this.calendarShowing.month >= 10
          ? this.calendarShowing.month.toString()
          : "0" + this.calendarShowing.month;
      const repeatWeeks = this.getRepeatWeeks(value);
      var week_every = [];
      for (var i = 0; i < repeattimes.length; i++) {
        if (
          format(repeattimes[i], "MM") === month &&
          format(repeattimes[i], "YYYY") ===
            this.calendarShowing.year.toString()
        ) {
          {
            var mday = format(repeattimes[i], "D");
            if (
              mday * 1 >= 1 &&
              mday * 1 <= 7 &&
              repeatWeeks.week1 == "Y"
            ) {
              week_every.push(format(repeattimes[i], "DD/MM/YYYY"));
            } else if (
              mday * 1 >= 8 &&
              mday * 1 <= 14 &&
              repeatWeeks.week2 == "Y"
            ) {
              week_every.push(format(repeattimes[i], "DD/MM/YYYY"));
            } else if (
              mday * 1 >= 15 &&
              mday * 1 <= 21 &&
              repeatWeeks.week3 == "Y"
            ) {
              week_every.push(format(repeattimes[i], "DD/MM/YYYY"));
            } else if (
              mday * 1 >= 22 &&
              mday * 1 <= 28 &&
              repeatWeeks.week4 == "Y"
            ) {
              week_every.push(format(repeattimes[i], "DD/MM/YYYY"));
            } else if (
              mday * 1 >= 29 &&
              mday * 1 <= 31 &&
              repeatWeeks.week5 == "Y"
            ) {
              week_every.push(format(repeattimes[i], "DD/MM/YYYY"));
            }
          }
        }
      }
      return week_every;
    },
    isCalendarQuestion(value) {
      // Follow-up and program-only questions are stored as child records, not calendar schedules.
      if (!value || !value.repeats || value.parent == "Y") {
        return false;
      }

      const repeats = value.repeats.toString().toLowerCase();
      return repeats !== "follow-up" && repeats !== "program-only";
    },
    getRepeatDays(value) {
      const repeatDays = value && value.repeat_days;
      const days = {
        Sun: "N",
        Mon: "N",
        Tue: "N",
        Wed: "N",
        Thu: "N",
        Fri: "N",
        Sat: "N",
      };

      if (repeatDays && typeof repeatDays === "object") {
        return {
          ...days,
          ...repeatDays,
        };
      }

      if (typeof repeatDays === "string") {
        const dayMap = {
          sunday: "Sun",
          sun: "Sun",
          monday: "Mon",
          mon: "Mon",
          tuesday: "Tue",
          tue: "Tue",
          wednesday: "Wed",
          wed: "Wed",
          thursday: "Thu",
          thu: "Thu",
          friday: "Fri",
          fri: "Fri",
          saturday: "Sat",
          sat: "Sat",
        };
        const dayKey = dayMap[repeatDays.toLowerCase()];
        if (dayKey) {
          days[dayKey] = "Y";
        }
      }

      return days;
    },
    getRepeatWeeks(value) {
      return {
        week1: "N",
        week2: "N",
        week3: "N",
        week4: "N",
        week5: "N",
        ...((value && value.repeat_weeks) || {}),
      };
    },
    getQuestionPerMonth() {
      let questionsAll = this.questionsAll.concat(this.questionsAllCustom);
      //optionToName
      let questionslist = [];
      const { startDate, endDate } = this.getCalendarScheduleRange();
      const month =
        this.calendarShowing.month >= 10
          ? this.calendarShowing.month.toString()
          : "0" + this.calendarShowing.month;
      questionsAll.map((value) => {
        if (!this.isCalendarQuestion(value)) {
          return;
        }
        var _day = [];
        var week_every = [];
        let repeattimes;
        let  week_2 = 'Y';
        let  week_4 = 'Y';
        let  week_1 = 'Y';
        let  week_3 = 'Y';
        let  week_5 = 'Y';
        let sched;
        let date_weekly = [];
        if (value.repeats != "onetime" && value.repeats) {
          const repeatDays = this.getRepeatDays(value);
          if (repeatDays.Sun == "Y") {
            _day.push(1);
          }
          if (repeatDays.Mon == "Y") {
            _day.push(2);
          }
          if (repeatDays.Tue == "Y") {
            _day.push(3);
          }
          if (repeatDays.Wed == "Y") {
            _day.push(4);
          }
          if (repeatDays.Thu == "Y") {
            _day.push(5);
          }
          if (repeatDays.Fri == "Y") {
            _day.push(6);
          }
          if (repeatDays.Sat == "Y") {
            _day.push(7);
          }
          if (_day.length === 0) {
            return;
          }
        }
        switch (value.repeats) {
          case "onetime":
            if (!value.starton || typeof value.starton !== "string") {
              break;
            }
            let stratOn = value.starton.split("/");
            if (stratOn.length !== 3) {
              break;
            }
            if (
              parseInt(stratOn[1]) === parseInt(this.calendarShowing.month) &&
              stratOn[2] === this.calendarShowing.year.toString()
            ) {
              questionslist.push({
                ...value,
                schedule: [value.starton],
              });
            }
            break;
          case "weekly":
            if (value.repeat_every > 1) {
              if (value.start_weeks == "2") {
                week_every = [2, 4, 6];
                week_2 = 'Y';
                week_4 = 'Y';
                week_1 = 'N';
                week_3 = 'N';
                week_5 = 'N';
              } else {
                week_every = [1, 3, 5];
                week_2 = 'N';
                week_4 = 'N';
                week_1 = 'Y';
                week_3 = 'Y';
                week_5 = 'Y';
              }
            } else {
              week_every = [1, 2, 3, 4, 5];
              week_2 = 'Y';
              week_4 = 'Y';
              week_1 = 'Y';
              week_3 = 'Y';
              week_5 = 'Y';
            }
            sched = {
              schedules: [
                {
                  dw: _day,
                },
              ],
            }; //, wm: week_every
            repeattimes = later.schedule(sched).next(36, startDate, endDate);


             



              
            repeattimes.map((data) => {

              var mday = format(data, "D")
              if((mday*1)>=1 && (mday*1)<=7  && week_1 == 'Y'){
                if (
                format(data, "MM") === month &&
                format(data, "YYYY") === this.calendarShowing.year.toString()
              ) {
                date_weekly.push(format(data, "DD/MM/YYYY"));
              }
                    
                }else if((mday*1)>=8 && (mday*1)<=14 && week_2 == 'Y'){
                  if (
                format(data, "MM") === month &&
                format(data, "YYYY") === this.calendarShowing.year.toString()
              ) {
                date_weekly.push(format(data, "DD/MM/YYYY"));
              }
                   
                }else if((mday*1)>=15 && (mday*1)<=21 && week_3 == 'Y'){
                  if (
                format(data, "MM") === month &&
                format(data, "YYYY") === this.calendarShowing.year.toString()
              ) {
                date_weekly.push(format(data, "DD/MM/YYYY"));
              }
                   
                }else if((mday*1)>=22 &&  (mday*1)<=28 && week_4 == 'Y'){
                  if (
                format(data, "MM") === month &&
                format(data, "YYYY") === this.calendarShowing.year.toString()
              ) {
                date_weekly.push(format(data, "DD/MM/YYYY"));
              }
                  
                }else if((mday*1)>=29 &&  (mday*1)<=31 && week_5 == 'Y'){
                  if (
                format(data, "MM") === month &&
                format(data, "YYYY") === this.calendarShowing.year.toString()
              ) {
                date_weekly.push(format(data, "DD/MM/YYYY"));
              }
                 
                }
              
              
            });
            date_weekly = _.uniqBy(date_weekly, function (e) {
              return e;
            });
            // console.log("date_weekly",date_weekly)
            questionslist.push({ ...value, schedule: date_weekly });

            break;
          case "monthly":
            var month_every = [];
            const result = month % 2 == 0 ? "even" : "odd";
            let check = false;
            if (value.repeat_every > 1) {
              if (value.start_month == "even") {
                month_every = [2, 4, 6, 8, 10, 12];
                if (result == value.start_month) {
                  check = true;
                }
              } else {
                month_every = [1, 3, 5, 7, 9, 11];
                if (result == value.start_month) {
                  check = true;
                }
              }
            } else {
              check = true;
              month_every = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
            }
            sched = {
              schedules: [
                {
                  dw: _day,
                  M: month_every,
                },
              ],
            };
            repeattimes = later
              .schedule(sched)
              .next(36, startDate, endDate);
            if (check) {
              week_every = this.getDateFromWeek(repeattimes, value);
              questionslist.push({ ...value, schedule: week_every });
            }
            break;
          case "biannually":
          case "quarterly":
            var quarter_every = [];
            const quarters = {
              Q1:
                value.repeat_quarters === undefined
                  ? "Y"
                  : value.repeat_quarters.Q1,
              Q2:
                value.repeat_quarters === undefined
                  ? "Y"
                  : value.repeat_quarters.Q2,
              Q3:
                value.repeat_quarters === undefined
                  ? "Y"
                  : value.repeat_quarters.Q3,
              Q4:
                value.repeat_quarters === undefined
                  ? "Y"
                  : value.repeat_quarters.Q4,
            };

            if (quarters.Q1 == "Y") {
              if (value.repeat_quarter == 1) {
                quarter_every.push(1);
              } else if (value.repeat_quarter == 2) {
                quarter_every.push(2);
              } else if (value.repeat_quarter == 3) {
                quarter_every.push(3);
              }
            }

            if (quarters.Q2 == "Y") {
              if (value.repeat_quarter == 1) {
                quarter_every.push(4);
              } else if (value.repeat_quarter == 2) {
                quarter_every.push(5);
              } else if (value.repeat_quarter == 3) {
                quarter_every.push(6);
              }
            }

            if (quarters.Q3 == "Y") {
              if (value.repeat_quarter == 1) {
                quarter_every.push(7);
              } else if (value.repeat_quarter == 2) {
                quarter_every.push(8);
              } else if (value.repeat_quarter == 3) {
                quarter_every.push(9);
              }
            }

            if (quarters.Q4 == "Y") {
              if (value.repeat_quarter == 1) {
                quarter_every.push(10);
              } else if (value.repeat_quarter == 2) {
                quarter_every.push(11);
              } else if (value.repeat_quarter == 3) {
                quarter_every.push(12);
              }
            }

            sched = {
              schedules: [
                {
                  dw: _day,
                  M: quarter_every,
                },
              ],
            };

            repeattimes = later
              .schedule(sched)
              .next(36, startDate, endDate);
            week_every = this.getDateFromWeek(repeattimes, value);
            if (week_every.length > 0) {
              questionslist.push({ ...value, schedule: week_every });
            }
            break;
          default:
            if (this.calendarShowing.monthLabel === value.repeat_months) {
              sched = {
                schedules: [{ dw: _day, M: [this.calendarShowing.month] }],
              };
              repeattimes = later
                .schedule(sched)
                .next(36, startDate, endDate);
              week_every = this.getDateFromWeek(repeattimes, value);
              if (week_every.length > 0) {
                questionslist.push({ ...value, schedule: week_every });
              }
            }
            break;
        }
      });

      this.questionsPerMonth = questionslist;
    },
    addDataToTableOfModal() {
      if (!this.selectedDay) {
        return;
      }
      let id = this.selectedDay.split("_");
      let selected = new _.find(this.calendarData, {
        id: id[1],
      });
      if (!selected || !selected.questionList) {
        this.tableSelectedDay = [];
        return;
      }
      const date_selected = format(
        this.buildCalendarDate(selected.year, selected.month, selected.dayOfMonth),
        "YYYY-MM-DD"
      );
      const dayQuestions = selected.questionList || [];
      for(let i in dayQuestions){
        if(this.questionOrder[date_selected]){
          if(this.questionOrder[date_selected][dayQuestions[i].id]){
            dayQuestions[i]['order']=this.questionOrder[date_selected][dayQuestions[i].id].order
          }else {
            dayQuestions[i]['order']=dayQuestions.length
          }
        }else{
          if(dayQuestions[i].question_en=="How do you feel today?"){
            dayQuestions[i]['order']=1
          }else if(dayQuestions[i].audience=="Standard"){
            dayQuestions[i]['order']=2
          }else{
            dayQuestions[i]['order']=3
        }

        }
        if(dayQuestions[i].optionToName){
          for(let j in dayQuestions[i].optionToName){
            if(j==0){
              dayQuestions[i].delivery=dayQuestions[i].optionToName[j]
            }else{
              dayQuestions[i].delivery+=','+dayQuestions[i].optionToName[j]
            }
            
          }
        }else{
            dayQuestions[i].delivery="All"
          }
       
      }
      selected.questionList = _.sortBy(dayQuestions, ['order']);

      this.tableSelectedDay = selected.questionList; // month
    
      let date = new Date(
        this.calendarShowing.year,
        selected.month - 1,
        selected.dayOfMonth
      );
      let dayLabel = format(date, "dddd");
      let monthLabel = format(date, "MMMM");
      this.questionPreviewText = `Question Preview for ${dayLabel}, ${monthLabel} ${selected.dayOfMonth}, ${this.calendarShowing.year}`;
    },
    async updateCradList(event) {
      const currentPosition = event.newIndex;
      const questionOrder=this.questionOrder
      const month=this.calendarShowing.month>=10?this.calendarShowing.month:'0'+this.calendarShowing.month
      const dateData=this.calendarShowing.year+'-'+month+'-'+this.selectedDay.split("_")[0]
      if(!questionOrder[dateData]){
        questionOrder[dateData]={}
      }
      for(const i in this.tableSelectedDay){
        this.tableSelectedDay[i].order = Number(i)+1
        if(!questionOrder[dateData][this.tableSelectedDay[i].id]){
          questionOrder[dateData][this.tableSelectedDay[i].id]={}
        }
        questionOrder[dateData][this.tableSelectedDay[i].id]={order:Number(i)+1,quest_id:this.tableSelectedDay[i].id,question:this.tableSelectedDay[i].question_en}
      }
      this.questionOrder=questionOrder
      this.$store.dispatch("question/updateCard",{data:this.tableSelectedDay,day:this.selectedDay.split("_")[0],month:this.calendarShowing.month,year:this.calendarShowing.year});
      //  console.log(' this.tableSelectedDay', this.tableSelectedDay)
      
    },
    anotherMonthData(value) {
      let month = this.calendarShowing.month - 1;
      let year = this.calendarShowing.year;
      this.clearCalendarData();
      if (month + value < 0) {
        this.getDateData(new Date(year - 1, 11, 1));
      } else {
        this.getDateData(new Date(year, month + value, 1));
      }
      this.getQuestionPerMonth();
      this.addDataToArr();
    },
    clearCalendarData() {
      this.calendarShowing = {
        day: 1,
        dayLabel: "Mon",
        month: 1,
        monthLabel: "January",
        year: "2010",
        firstDayOfWeek: 0,
        lastDayOfPastMonth: 30,
        lastDayOfMonth: 30,
      };
      this.tableSelectedDay = [];
      this.selectedDay = null;
      this.calendarData = [];
      this.questionPreviewText = "";
    },
    buildCalendarDate(year, month, day) {
      // Use noon to avoid edge cases around midnight/DST when browsers normalize dates.
      return new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0);
    },
    getCalendarScheduleRange() {
      const year = Number(this.calendarShowing.year);
      const currentMonthIndex = Number(this.calendarShowing.month) - 1;
      const previousMonthLastDay = Number(this.calendarShowing.lastDayOfPastMonth);

      // `later` expects actual Date objects here.
      return {
        startDate: new Date(
          year,
          currentMonthIndex - 1,
          previousMonthLastDay,
          12,
          0,
          0,
          0
        ),
        endDate: new Date(
          year,
          currentMonthIndex + 1,
          1,
          12,
          0,
          0,
          0
        ),
      };
    },
    getDateData(date) {
      let month = parseInt(format(date, "M")) - 1;
      let pastMonth = month - 1;
      let year = parseInt(format(date, "YYYY"));
      let dateOfPastMont = endOfMonth(new Date(year, pastMonth, 3));

      let firstDayOfWeek = parseInt(format(new Date(year, month, 1), "d"));
      if (firstDayOfWeek == 0) {
        firstDayOfWeek = 7;
      }
      this.calendarShowing.firstDayOfWeek = firstDayOfWeek;
      this.calendarShowing.day = format(date, "D");
      this.calendarShowing.dayLabel = format(date, "dddd");
      this.calendarShowing.month = parseInt(format(date, "M"));
      this.calendarShowing.monthLabel = format(date, "MMMM");
      this.calendarShowing.year = parseInt(format(date, "YYYY"));
      this.calendarShowing.lastDayOfPastMonth = parseInt(
        format(dateOfPastMont, "D")
      );
      this.calendarShowing.lastDayOfMonth = parseInt(
        format(endOfMonth(date), "D")
      );
    },
    generateRowHeader(form){
      if(form.report_card == true){
        this.$store.dispatch("question/generateRowHeader", form);
      }
    },
    handleBlock(item) {
      if (item.status === false) {
        if (item.audience === "Custom") {
          this.$store.dispatch("custom/blockQuestion", item.key);
        } else {
          this.$store.dispatch("question/blockQuestion", item.key);
        }
      } else {
        if (item.audience === "Custom") {
          this.$store.dispatch("custom/unblockQuestion", item.key);
        } else {
          this.$store.dispatch("question/unblockQuestion", item.key);
        }
      }

      this.calendarData.map((value, i) => {
        let selected = _.find(value.questionList, {
          id: item.id,
        });

        if (item.status && selected !== undefined) {
          value.questionsShowing.push(item);
        } else if (selected !== undefined) {
          value.questionsShowing = _.filter(
            value.questionList,
            function (currentObject) {
              return (
                currentObject.id !== item.id && currentObject.status == true
              );
            }
          );
        }
      });
    },
    questionPlaceholder(type, lang) {
      let placeholder = "Question";
      if (type == "Multiple Choice") {
        placeholder = "Question";
      } else if (type == "Multiple Choice(other)") {
        placeholder = "Question";
      } else if (type == "Scale") {
        placeholder = "On a scale of 0 to 10…";
      } else if (type == "Text Area Field") {
        placeholder = "Question";
      }
      return placeholder + " " + lang;
    },
    previewOptionsText(options) {
      let text = "";
      let firstText = true;
      if (options.length > 1) {
        const length = options.length;
        for (let i = 0; i < length; i++) {
          if (options[i].feeling == "Positive") {
            if (firstText) {
              firstText = false;
              text = "“" + options[i].en;
            } else {
              text = text + "” or “" + options[i].en;
            }
          }
        }
        text = text + "”";
        if (text.length < 3) {
          text = "“" + options[0].en + "”";
        }
      } else if (options) {
        text = options[0].en;
      }
      if (text.length > 3) {
        return text;
      } else {
        return "“Option...”";
      }
    },
    filterToFunction(node, keyword) {
      const searchText = String(keyword || "").trim().toLowerCase();
      const data = node && node.data ? node.data : {};
      if (!searchText) {
        return true;
      }
      if (data.toType == "user" || data.type == "user") {
        const values = [
          data.firstname,
          data.lastname,
          data.email,
          data.label,
        ];
        return values.some((value) => {
          return String(value || "").toLowerCase().indexOf(searchText) >= 0;
        });
      }
      return (
        node
          .getText(false, null)
          .toLowerCase()
          .indexOf(searchText) >= 0
      );
    },
    getfollow_up_question(index,options){
      let label='option '+(index+1)
      if(options){
        if(options.value){
          label=options.value
        }else if(options.en){
          label=options.en
        }
        if(options.question_id){
          this.selectquestionFollewUp = options.question_id
        }
      }else{
        this.selectquestionFollewUp = '';
      }
      this.titleName=`If person responds "{${label}}" then follow up with this question:`
      this.follow_up_question=true
      this.indexOption=index
      const follow_up =_.filter(this.questioncustomAll,(o)=>{return o.frequency == 'Follow-up';})
      this.questionFollewUp=follow_up
    },
    addFollowUpQuestion(){
      
      if(this.dynamicValidateForm.type == 'Multiple Choice'){
        
        this.dynamicValidateForm.options_en[this.indexOption].question_id=this.selectquestionFollewUp
        this.dynamicValidateForm.options_th[this.indexOption].question_id=this.selectquestionFollewUp  
      }else {
        this.dynamicValidateForm.options[this.indexOption].question_id=this.selectquestionFollewUp
      }
      this.selectquestionFollewUp=''
      this.follow_up_question=false
    },
    getstylefollowUp(option){
      if(option.question_id==''){
        return 'background-color:#E0E0E0;color:black;'

      }else {
        return 'background-color:#6C30CC;color:white;'
      }
    },
    async genQuestion() {
      const response = await this.$store.dispatch("custom/getOpenai", {
          questions: this.questions_text,
          languages:this.language
        })
        // console.log('response',response)
        this.putToform(response)

    },
    putToform(temp_data) {
      const data = Array.isArray(temp_data) ? temp_data[0] : temp_data
      if (!data || typeof data !== 'object') {
        return
      }
      const item = _.cloneDeep(data) 
      item.repeats=data.frequency.toLowerCase()
      item.repeat_days=data.schedule.dayOfWeek.toLowerCase()
      const dimension =_.filter(this.optionsCategory,(o)=>{return o.category == data.type.dimension;}) 

      item.category=dimension[0].key
      item.type=data.type.questionType
      
      item.custom_type=data.type.questionType.toLowerCase()
      

      if(data.type.questionType=="Multiple Choice"){
        
        if(item.options){
          if(item.options.length>2){
            item.type="Multiple Choice"
            item.custom_type="multiple_choice"
          }
        }
      }else if(data.type.questionType=='Open-ended'){
        item.type_name=data.type.questionType      
        item.custom_type=data.type.questionType.toLowerCase()
        item.type="Text Area Field"
      }
      this.id = item.key;
      let schedule = "";
      let day = '';
      let option = [];
      if (item.repeats == "weekly") {
        day = item.repeat_days.toLowerCase();
        schedule="Every "+data.schedule.dayOfWeek
      } else if (item.repeats == "monthly") {
        day=item.repeat_days.toLowerCase()
        schedule = "week"+data.schedule.weekOfMonth
      } else if (item.repeats == "quarterly") {
        day=item.repeat_days.toLowerCase()
        // let dayKey = _.invert(item.repeat_days)["Y"];
        // if (dayKey == "Mon") {
        //   day = "monday";
        // } else if (dayKey == "Tue") {
        //   day = "tuesday";
        // } else if (dayKey == "Wed") {
        //   day = "wednesday";
        // } else if (dayKey == "Thu") {
        //   day = "thursday";
        // } else if (dayKey == "Fri") {
        //   day = "friday";
        // } else if (dayKey == "Sat") {
        //   day = "saturday";
        // } else if (dayKey == "Sun") {
        //   day = "sunday";
        // }
        let dayName = day.charAt(0).toUpperCase() + day.slice(1);
        this.options_quarterly = [
          {
            label: "First month of the quarter",
            options: [
              {
                value: "1_week1",
                label:
                  "First " + dayName + " of the first month of the quarter",
              },
              {
                value: "1_week2",
                label:
                  "Second " + dayName + " of the first month of the quarter",
              },
              {
                value: "1_week3",
                label:
                  "Third " + dayName + " of the first month of the quarter",
              },
              {
                value: "1_week4",
                label:
                  "Fourth " + dayName + " of the first month of the quarter",
              },
            ],
          },
          {
            label: "Second month of the quarter",
            options: [
              {
                value: "2_week1",
                label:
                  "First " + dayName + " of the second month of the quarter",
              },
              {
                value: "2_week2",
                label:
                  "Second " + dayName + " of the second month of the quarter",
              },
              {
                value: "2_week3",
                label:
                  "Third " + dayName + " of the second month of the quarter",
              },
              {
                value: "2_week4",
                label:
                  "Fourth " + dayName + " of the second month of the quarter",
              },
            ],
          },
          {
            label: "Third month of the quarter",
            options: [
              {
                value: "3_week1",
                label:
                  "First " + dayName + " of the third month of the quarter",
              },
              {
                value: "3_week2",
                label:
                  "Second " + dayName + " of the third month of the quarter",
              },
              {
                value: "3_week3",
                label:
                  "Third " + dayName + " of the third month of the quarter",
              },
              {
                value: "3_week4",
                label:
                  "Fourth " + dayName + " of the third month of the quarter",
              },
            ],
          },
        ];
        let week = 'week'+data.schedule.weekOfMonth//_.invert(item.repeat_weeks)["Y"];
        schedule = data.schedule.monthOfQuarter+ "_" + week;
      } else if (item.repeats == "biannually") {
        const quarter1=data.schedule.quarterPair.substring(0, 2);
        const quarter2=data.schedule.quarterPair.substring(2, 4);
        day=item.repeat_days.toLowerCase()
        let dayName = day.charAt(0).toUpperCase() + day.slice(1);
          this.options_biannually = [
          {
            label: "First month of the Q1 and Q3",
            options: [
              {
                value: "1_week1_q1_q3",
                label: "First " + dayName + " of the first month of Q1 and Q3",
              },{
                value: "1_week2_q1_q3",
                label: "Second " + dayName + " of the first month of Q1 and Q3",
              },{
                value: "1_week3_q1_q3",
                label: "Third " + dayName + " of the first month of Q1 and Q3",
              },{
                value: "1_week4_q1_q3",
                label: "Forth " + dayName + " of the first month of Q1 and Q3",
              },{
                value: "1_week5_q1_q3",
                label: "Fifth " + dayName + " of the first month of Q1 and Q3",
              },
            ],
          },{
            label: "Second month of the Q1 and Q3",
            options: [
              {
                value: "2_week1_q1_q3",
                label: "First " + dayName + " of the second month of Q1 and Q3",
              },{
                value: "2_week2_q1_q3",
                label: "Second " + dayName + " of the second month of Q1 and Q3",
              },{
                value: "2_week3_q1_q3",
                label: "Third " + dayName + " of the second month of Q1 and Q3",
              },{
                value: "2_week4_q1_q3",
                label: "Forth " + dayName + " of the second month of Q1 and Q3",
              },{
                value: "2_week5_q1_q3",
                label: "Fifth " + dayName + " of the second month of Q1 and Q3",
              },
            ],
          },{
            label: "Third month of the Q1 and Q3",
            options: [
              {
                value: "3_week1_q1_q3",
                label: "First " + dayName + " of the third month of Q1 and Q3",
              },{
                value: "3_week2_q1_q3",
                label: "Second " + dayName + " of the third month of Q1 and Q3",
              },{
                value: "3_week3_q1_q3",
                label: "Third " + dayName + " of the third month of Q1 and Q3",
              },{
                value: "3_week4_q1_q3",
                label: "Forth " + dayName + " of the third month of Q1 and Q3",
              },{
                value: "3_week5_q1_q3",
                label: "Fifth " + dayName + " of the third month of Q1 and Q3",
              },
            ],
          },{
            label: "First month of the Q2 and Q4",
            options: [
              {
                value: "1_week1_q2_q4",
                label: "First " + dayName + " of the first month of Q2 and Q4",
              },{
                value: "1_week2_q2_q4",
                label: "Second " + dayName + " of the first month of Q2 and Q4",
              },{
                value: "1_week3_q2_q4",
                label: "Third " + dayName + " of the first month of Q2 and Q4",
              },{
                value: "1_week4_q2_q4",
                label: "Forth " + dayName + " of the first month of Q2 and Q4",
              },{
                value: "1_week5_q2_q4",
                label: "Fifth " + dayName + " of the first month of Q2 and Q4",
              },
            ],
          },{
            label: "Second month of the Q2 and Q4",
            options: [
              {
                value: "2_week1_q2_q4",
                label: "First " + dayName + " of the second month of Q2 and Q4",
              },{
                value: "2_week2_q2_q4",
                label: "Second " + dayName + " of the second month of Q2 and Q4",
              },{
                value: "2_week3_q2_q4",
                label: "Third " + dayName + " of the second month of Q2 and Q4",
              },{
                value: "2_week4_q2_q4",
                label: "Forth " + dayName + " of the second month of Q2 and Q4",
              },{
                value: "2_week5_q2_q4",
                label: "Fifth " + dayName + " of the second month of Q2 and Q4",
              },
            ],
          },{
            label: "Third month of the Q2 and Q4",
            options: [
              {
                value: "3_week1_q2_q4",
                label: "First " + dayName + " of the third month of Q2 and Q4",
              },{
                value: "3_week2_q2_q4",
                label: "Second " + dayName + " of the third month of Q2 and Q4",
              },{
                value: "3_week3_q2_q4",
                label: "Third " + dayName + " of the third month of Q2 and Q4",
              },{
                value: "3_week4_q2_q4",
                label: "Forth " + dayName + " of the third month of Q2 and Q4",
              },{
                value: "3_week5_q2_q4",
                label: "Fifth " + dayName + " of the third month of Q2 and Q4",
              },
            ],
          },
        ];
        let week ="week"+data.schedule.weekOfMonth
        
        schedule = data.schedule.monthOfQuarter + "_" + week + "_" + quarter1.toLowerCase() + "_" + quarter2.toLowerCase();
      } else if (item.repeats == "yearly") {
        day=item.repeat_days.toLowerCase()
        let dayName = day.charAt(0).toUpperCase() + day.slice(1);
        this.options_yearly = []
        for(let i = 1; i < 13; i++){
          let month = format(`2023-${i}-01`, 'MMMM')
          this.options_yearly.push({
            label: month,
            options: [
              {
                value: month + "_week1",
                label: "First " + dayName + " of " + month,
              },{
                value: month + "_week2",
                label: "Second " + dayName + " of " + month,
              },{
                value: month + "_week3",
                label: "Third " + dayName + " of " + month,
              },{
                value: month + "_week4",
                label: "Forth " + dayName + " of " + month,
              },{
                value: month + "_week5",
                label: "Fifth " + dayName + " of " + month,
              },
            ],
          })
        }
        let week ="week"+data.schedule.weekOfMonth
        let nameMonth = format(`2023-${data.schedule.monthOfYear}-01`, 'MMMM')
        schedule = nameMonth + "_" + week;
      } 
      const lang={}
      for(const i in this.language){
          if(!lang[this.language[i].code]){
            lang[this.language[i].code]=''
          }
      }
      if (item.custom_type == "multiple_choice") {

        for(const i in data.options){
          const lang_option={}
          for(const j in this.language){
            const code=this.language[j].code
            if(code!='en'&&code!='th'){
              lang_option[code]=data.options[i].content[code]
            }
          }
          option.push({
            key: this.$utils("mockingID", 15),
            th: data.options[i].content.th?data.options[i].content.th:data.options[i].content.en,
            en: data.options[i].content.en,
            feeling:data.options[i].sentiment,
            question_id:'',
            ...lang_option
          })

        }
       
        // item.m_choice.en.map((value, i) => {
        //   for(const key in lang){
       
        //       if(item.m_choice[key]&&item.m_choice[key][i]){
        //         lang[key]=item.m_choice[key]?item.m_choice[key][i]:''
        //       }
        //   }
         
        //   option.push({
        //     ...lang,
        //     key: this.$utils("mockingID", 15),
        //     th: item.m_choice.th[i],
        //     en: value,
        //     feeling: item.m_choice.feeling[i],
        //     question_id:item.m_choice&&item.m_choice.quest_id?item.m_choice.quest_id[i]:'',
        //   });
        // });
        
      } else {
        option = [
          {
            ...lang,
            key: this.$utils("mockingID", 15),
            th: "",
            en: "",
            feeling: "Positive",
            question_id:'',
          },
          {
            ...lang,
            key: this.$utils("mockingID", 15),
            th: "",
            en: "",
            feeling: "Neutral",
            question_id:'',
          },
          {
            ...lang,
            key: this.$utils("mockingID", 15),
            th: "",
            en: "",
            feeling: "Negative",
            question_id:'',
          },
        ];
      }

      item.questions=data.question

       
      
      let cascaderTo = [];
      const itemUsers = Array.isArray(item.users)
        ? item.users
        : (item.users ? Object.values(item.users) : []);
      if (!item.teams && !item.cohorts && !itemUsers.length) {
        cascaderTo.push(["all_teams_and_cohorts"]);
      } else {
        if (item.teams) {
          item.teams.map((value) => {
            cascaderTo.push([value]);
          });
        }
        if (item.cohorts) {
          item.cohorts.map((value) => {
            cascaderTo.push([value]);
          });
        }
        if (itemUsers.length) {
          itemUsers.map((value) => {
            cascaderTo.push([`user_${value}`]);
          });
        }
      }
      this.dynamicValidateForm = {
        cascaderTo: cascaderTo,
        oldCascaderTo: cascaderTo,
        showResultsInDaily: true,
        category: item.category,
        type:
          item.custom_type == "multiple_choice"
            ? "Multiple Choice(other)"
            : item.type,
        question_en: item.questions['en'],
        question_th: item.questions['th'],
        questions:item.questions,
        coins:item.reward.toString(),
        frequency: item.repeats,
        schedule: schedule,
        status: "Y",
        feelingY: item.custom_type == "yes_no" ? item.m_choice.feeling[0] : "",
        feelingN: item.custom_type == "yes_no" ? item.m_choice.feeling[1] : "",
        options_YN:{},
        options_en: [
          { key: 1, value: "Yes", weight: "1", placeholder: "Yes",question_id:item.m_choice&&item.m_choice.quest_id?item.m_choice.quest_id[0]:''},
          { key: 2, value: "No", weight: "0", placeholder: "No",question_id:item.m_choice&&item.m_choice.quest_id?item.m_choice.quest_id[1]:'' },
        ],
        options_th: [
          { key: 1, value: "ใช่", placeholder: "Yes",question_id:item.m_choice&&item.m_choice.quest_id?item.m_choice.quest_id[0]:'' },
          { key: 2, value: "ไม่ใช่", placeholder: "No",question_id:item.m_choice&&item.m_choice.quest_id?item.m_choice.quest_id[1]:'' },
        ],
        options: option,
        dayofweek: day,
        less_txts: item.less_txts,
        less_txt: item.less_txt,
        less_txt_en: item.less_txt_en,
        max_txts: item.max_txts,
        max_txt: item.max_txt,
        max_txt_en: item.max_txt_en,
        custom_type: item.custom_type,
        powerSkill: item.powerSkill,
        max: 10,
        min: 0,
        rolling:item.rolling,
        report_card: (item.report_card && item.report_card == 'Y') ? true : false,
        report_card_label: item.report_card_label ? item.report_card_label : '',
        culture_index: item.culture_index ? item.culture_index : false
      };
      
      if(item.custom_type == "yes_no"){
        for(const key in lang){
          if(key!='en'&&key!='th'){
   
              const arr=[
                { key: 1, value: item.m_choice[key]?item.m_choice[key][0]:'', placeholder: 'Yes', question_id:item.m_choice&&item.m_choice.quest_id?item.m_choice.quest_id[0]:'' },
                { key: 2, value: item.m_choice[key]?item.m_choice[key][1]:'', placeholder: 'No', question_id:item.m_choice&&item.m_choice.quest_id?item.m_choice.quest_id[1]:'' },
              ]
              if(this.dynamicValidateForm.options_YN&&!this.dynamicValidateForm.options_YN[key]){
                this.dynamicValidateForm.options_YN[key]=JSON.parse(JSON.stringify(arr));
                this.dynamicValidateForm[`options_${key}`]=JSON.parse(JSON.stringify(arr));
              //   this.$set(this.dynamicValidateForm.options_YN, key, [
              //   { key: 1, value: item.m_choice[key]?item.m_choice[key][0]:'', placeholder: 'Yes', question_id: '' },
              //   { key: 2, value: item.m_choice[key]?item.m_choice[key][1]:'', placeholder: 'No', question_id: '' },
              // ]);
              }
              
          }
        }
     

      }

     
      if(item.frequency == "Follow-up" || item.frequency == "Program-only"){
        this.dynamicValidateForm.frequency = item.frequency
      }
      if(item.type == 'Text Area Field'){
        this.disabledRowHeader = true
      }else{
        this.disabledRowHeader = false
      }
      if(item.report_card_label){
        this.$store.commit("question/setRowHeader", item.report_card_label);
      }else{
        this.$store.commit("question/setRowHeader", '');
      }

  
    },
    truncateText(text) {
      let length = 50; // Maximum length for truncated text
      return text && text.length > length ? text.slice(0, length) + "..." : text;
    },
    getTeamNameInfo(teamId) {
      // Cache the result to avoid repeated calculations
      if (!this._teamNameCache) {
        this._teamNameCache = {};
      }
      
      if (!this._teamNameCache[teamId]) {
        this._teamNameCache[teamId] = this.getAnonymousTeamName(teamId);
      }
      
      return this._teamNameCache[teamId];
    },
    getAnonymousTeamName(teamId, responseCount = 1, isCheckingParent = false) {
      // Use configurable thresholds
      const MIN_RESPONSES = this.MIN_RESPONSES_FOR_TEAM_NAME;
      const MIN_MEMBERS = this.MIN_MEMBERS_FOR_TEAM_NAME;
      
      if (!teamId) {
        return { name: 'Organization', isCascaded: false, teamId: 'organization' };
      }
      
      const team = this.teamArr[teamId];
      if (!team) {
        // If team not found in teamArr, check if it's a deleted team
        if (this.deletedTeam && this.deletedTeam[teamId]) {
          return { name: 'Organization', isCascaded: false, teamId: 'organization' };
        }
        return { name: 'Organization', isCascaded: false, teamId: 'organization' };
      }
      
      // Check if this team is viable (same logic as dropdown)
      if (this.isTeamViable(teamId)) {
        return { name: team.name || team.team_name || 'Team', isCascaded: false, teamId: teamId };
      }
      
      // If not viable, find the viable parent
      const viableParent = this.findViableParentTeam(teamId);
      if (viableParent && viableParent.teamId !== 'organization') {
        const parentTeam = this.teamArr[viableParent.teamId];
        if (parentTeam) {
          return { name: parentTeam.name || parentTeam.team_name || 'Team', isCascaded: true, teamId: viableParent.teamId };
        }
      }
      
      // If no viable parent found, return generic label with consistent ID
      return { name: 'Organization', isCascaded: false, teamId: 'organization' };
    },
    isTeamViable(teamId) {
      // Check if a team meets privacy thresholds
      const responseCount = this.getAggregatedTeamResponseCount(teamId);
      const memberCount = this.getAggregatedTeamMemberCount(teamId);
      return responseCount >= this.MIN_RESPONSES_FOR_TEAM_NAME && 
             (!this.USE_MEMBER_COUNT_CHECK || memberCount >= this.MIN_MEMBERS_FOR_TEAM_NAME);
    },
    getTeamResponseCount(teamId) {
      // Count responses for this team in current dataset
      if (!this.dataopenend || !Array.isArray(this.dataopenend)) return 0;
      
      return this.dataopenend.filter(item => 
        item.team === teamId && !item.Summary
      ).length;
    },
    getTeamMemberCount(teamId) {
      // Get member count from allUsers data
      if (!this.allUsers || !Array.isArray(this.allUsers)) {
        console.log('No allUsers data available');
        return 0;
      }
      
      // Debug: Check what the user data looks like
      const teamUsers = this.allUsers.filter(user => user.team === teamId);
      if (teamUsers.length > 0) {
        console.log(`Sample user for team ${teamId}:`, teamUsers[0]);
      }
      
      // Try different status field names
      return this.allUsers.filter(user => 
        user.team === teamId && (user.status === 'active' || user.status === 'Y' || !user.status)
      ).length;
    },
    getAllDescendantTeams(teamId, descendants = [], visited = new Set()) {
      // Prevent infinite recursion by tracking visited teams
      if (visited.has(teamId)) {
        console.warn(`Circular reference detected for team ${teamId}`);
        return descendants;
      }
      
      visited.add(teamId);
      
      // Recursively get all child teams
      for (const [childId, childTeam] of Object.entries(this.teamArr)) {
        if (childTeam && childTeam.parent === teamId && !visited.has(childId)) {
          descendants.push(childId);
          this.getAllDescendantTeams(childId, descendants, visited);
        }
      }
      return descendants;
    },
    getAggregatedTeamResponseCount(teamId) {
      // Get response count including all child teams
      const allTeams = [teamId, ...this.getAllDescendantTeams(teamId)];
      
      if (!this.dataopenend || !Array.isArray(this.dataopenend)) return 0;
      
      return this.dataopenend.filter(item => 
        allTeams.includes(item.team) && !item.Summary
      ).length;
    },
    getAggregatedTeamMemberCount(teamId) {
      // Get member count including all child teams
      const allTeams = [teamId, ...this.getAllDescendantTeams(teamId)];
      
      if (!this.allUsers || !Array.isArray(this.allUsers)) return 0;
      
      return this.allUsers.filter(user => 
        allTeams.includes(user.team) && user.status === 'active'
      ).length;
    },
    handleTeamFilterChange(teamId) {
      this.selectedTeamFilter = teamId;
    },
    getSelectedTeamInfo() {
      if (this.selectedTeamFilter === 'all') {
        return null;
      }
      
      const selectedOption = this.availableTeamOptions.find(option => option.value === this.selectedTeamFilter);
      return selectedOption;
    },
    getTeamDepth(teamId, depth = 0, visited = new Set()) {
      // Prevent infinite recursion by tracking visited teams
      if (visited.has(teamId)) {
        console.warn(`Circular reference detected in getTeamDepth for team ${teamId}`);
        return depth;
      }
      
      visited.add(teamId);
      
      const team = this.teamArr[teamId];
      if (!team || !team.parent || !this.teamArr[team.parent]) {
        return depth;
      }
      return this.getTeamDepth(team.parent, depth + 1, visited);
    },
    getTeamColorClass(teamId) {
      if (!teamId) return 'team-color-default';
      
      // Special handling for organization fallback
      if (teamId === 'organization') {
        return 'team-color-default';
      }
      
      // Get the first-level child of the root parent for consistent branch coloring
      const branchParent = this.getBranchParentTeam(teamId);
      
      // Create a deterministic color based on branch parent ID
      const colorClasses = [
        'team-color-0', // Blue
        'team-color-1', // Purple
        'team-color-2', // Green  
        'team-color-3', // Orange
        'team-color-4'  // Pink
      ];
      
      // Use a simple hash of the branch parent ID to determine color
      let hash = 0;
      for (let i = 0; i < branchParent.length; i++) {
        hash = ((hash << 5) - hash) + branchParent.charCodeAt(i);
        hash = hash & hash; // Convert to 32bit integer
      }
      
      const colorIndex = Math.abs(hash) % colorClasses.length;
      return colorClasses[colorIndex];
    },
    getBranchParentTeam(teamId) {
      // Find the direct child of the root parent (the branch this team belongs to)
      const rootParent = this.getRootParentTeam(teamId);
      
      // If this team IS the root parent, use its ID
      if (teamId === rootParent) {
        return rootParent;
      }
      
      // Walk up the hierarchy to find the direct child of root parent
      let currentTeamId = teamId;
      let branchParent = teamId;
      
      while (currentTeamId) {
        const team = this.teamArr[currentTeamId];
        if (!team || !team.parent) break;
        
        // If the parent is the root parent, then current team is the branch parent
        if (team.parent === rootParent) {
          branchParent = currentTeamId;
          break;
        }
        
        // If the parent doesn't exist in teamArr, this might be the branch parent
        if (!this.teamArr[team.parent]) {
          branchParent = currentTeamId;
          break;
        }
        
        currentTeamId = team.parent;
      }
      
      return branchParent;
    },
    getRootParentTeam(teamId) {
      // Find the topmost parent team
      let currentTeamId = teamId;
      let rootTeam = teamId;
      const visited = new Set();
      
      while (currentTeamId) {
        // Prevent infinite loop
        if (visited.has(currentTeamId)) {
          console.warn(`Circular reference detected in getRootParentTeam for team ${currentTeamId}`);
          break;
        }
        visited.add(currentTeamId);
        
        const team = this.teamArr[currentTeamId];
        if (!team || !team.parent || !this.teamArr[team.parent]) {
          rootTeam = currentTeamId;
          break;
        }
        currentTeamId = team.parent;
      }
      
      return rootTeam;
    },
    findViableParentTeam(teamId) {
      // Find the closest parent team (including self) that meets privacy thresholds
      let currentTeamId = teamId;
      const visited = new Set();
      
      while (currentTeamId) {
        // Prevent infinite loop
        if (visited.has(currentTeamId)) {
          console.warn(`Circular reference detected in findViableParentTeam for team ${currentTeamId}`);
          break;
        }
        visited.add(currentTeamId);
        
        const team = this.teamArr[currentTeamId];
        if (!team) break;
        
        const responseCount = this.getAggregatedTeamResponseCount(currentTeamId);
        const memberCount = this.getAggregatedTeamMemberCount(currentTeamId);
        const meetsThreshold = responseCount >= this.MIN_RESPONSES_FOR_TEAM_NAME && 
                             (!this.USE_MEMBER_COUNT_CHECK || memberCount >= this.MIN_MEMBERS_FOR_TEAM_NAME);
        
        if (meetsThreshold) {
          return {
            teamId: currentTeamId,
            label: team.name || team.team_name || 'Team',
            responseCount,
            memberCount,
            depth: this.getTeamDepth(currentTeamId)
          };
        }
        
        // Move up to parent
        currentTeamId = team.parent;
      }
      
      // If no viable parent found, return Organization level
      return {
        teamId: 'organization',
        label: 'Organization',
        responseCount: this.getTeamResponseCount(teamId),
        memberCount: 0,
        depth: 0
      };
    },
    getLocalizedQuestionText(question) {
      const selectedLanguage = localStorage.getItem('selectedLanguage') || 'en';
      
      // Check for selected language translation
      if (question.questions && question.questions[selectedLanguage]) {
        return question.questions[selectedLanguage];
      }
      
      // Fallback to English
      return question.question_en;
    },
    hasFollowUpQuestions(question) {
      // Check if question has follow-up questions assigned
      if (!question.m_choice) return false;
      
      // Check quest_id array for Yes/No questions
      if (question.m_choice.quest_id && Array.isArray(question.m_choice.quest_id)) {
        return question.m_choice.quest_id.some(id => id && id.trim() !== '');
      }
      
      // Check question_id field in options for Multiple Choice questions
      const languages = ['en', 'th', 'ms']; // Add other languages as needed
      for (const lang of languages) {
        if (question.m_choice[lang] && Array.isArray(question.m_choice[lang])) {
          for (const option of question.m_choice[lang]) {
            if (option && option.question_id && option.question_id.trim() !== '') {
              return true;
            }
          }
        }
      }
      
      return false;
    },
    getFollowUpQuestionsText(question) {
      try {
        if (!question.m_choice) return [];
        
        // Get questions from both stores to ensure we have all available questions
        const standardQuestions = this.$store.state.question.questions || [];
        const customQuestions = this.$store.state.custom.questions || [];
        const allQuestions = [...standardQuestions, ...customQuestions];
        const selectedLanguage = localStorage.getItem('selectedLanguage') || 'en';
        const followUpMap = new Map(); // Use Map to group identical questions
        
        // Handle Yes/No questions (quest_id array)
        if (question.m_choice.quest_id && Array.isArray(question.m_choice.quest_id)) {
          // Get localized Yes/No labels
          const yesNoOptions = question.m_choice[selectedLanguage] || question.m_choice.en || ['No', 'Yes'];
          
          // Handle weight reversal
          let finalOptions = [...yesNoOptions];
          // if (question.m_choice.weight && (question.m_choice.weight[0] === "0" || question.m_choice.weight[0] === 0)) {
          //   finalOptions = [...yesNoOptions].reverse();
          // }
          
          question.m_choice.quest_id.forEach((id, index) => {
            if (id && id.trim() !== '') {
              const followUpQuestion = allQuestions.find(q => q.key === id);
              if (followUpQuestion) {
                const questionText = this.getLocalizedQuestionText(followUpQuestion);
                const optionText = finalOptions[index] || `Option ${index + 1}`;
                
                // Group identical questions
                if (followUpMap.has(questionText)) {
                  followUpMap.get(questionText).options.push(optionText);
                } else {
                  followUpMap.set(questionText, {
                    text: questionText,
                    options: [optionText]
                  });
                }
              }
            }
          });
        }
        
        // Handle Multiple Choice questions (question_id in options)
        const optionLanguages = [selectedLanguage, 'en', 'th', 'ms'];
        for (const lang of optionLanguages) {
          if (question.m_choice[lang] && Array.isArray(question.m_choice[lang])) {
            question.m_choice[lang].forEach((option) => {
              if (option && option.question_id && option.question_id.trim() !== '') {
                const followUpQuestion = allQuestions.find(q => q.key === option.question_id);
                if (followUpQuestion) {
                  const questionText = this.getLocalizedQuestionText(followUpQuestion);
                  const optionText = option[selectedLanguage] || option.en || option.th || 'Option';
                  
                  // Group identical questions
                  if (followUpMap.has(questionText)) {
                    followUpMap.get(questionText).options.push(optionText);
                  } else {
                    followUpMap.set(questionText, {
                      text: questionText,
                      options: [optionText]
                    });
                  }
                }
              }
            });
            break; // Only process first available language to avoid duplicates
          }
        }
        
        // Convert Map to array and remove duplicate options
        return Array.from(followUpMap.values()).map(item => ({
          text: item.text,
          options: [...new Set(item.options)] // Remove duplicate options
        }));
      } catch (error) {
        console.error('Error in getFollowUpQuestionsText:', error);
        return [];
      }
    },
    handleLanguageChange(langCode) {
      // Force component re-render when language changes
      this.$forceUpdate();
      
      // If a modal is open, refresh the question name
      if (this.currentQuestionData) {
        this.quest_name = this.getLocalizedQuestionText(this.currentQuestionData);
      }
    },
    
    // Font detection and styling methods
    getQuestionFontClass(text) {
      const selectedLanguage = localStorage.getItem('selectedLanguage') || 'en';
      
      // Language-based font selection
      if (selectedLanguage === 'th') {
        return 'font-kanit';
      }
      if (selectedLanguage === 'zh-HK') {
        return 'font-chinese';
      }
      
      // Character-based detection as fallback
      if (text && text.match(/[ก-๙]/)) {
        return 'font-kanit';
      }
      if (text && text.match(/[\u4e00-\u9fff\u3400-\u4dbf]/)) {
        return 'font-chinese';
      }
      
      return 'font-lato';
    },
    
    getQuestionFontStyle(text) {
      const selectedLanguage = localStorage.getItem('selectedLanguage') || 'en';
      
      // Language-based font selection
      if (selectedLanguage === 'th') {
        return "font-family: 'Kanit', sans-serif";
      }
      if (selectedLanguage === 'zh-HK') {
        return "font-family: 'Noto Sans TC', sans-serif";
      }
      
      // Character-based detection as fallback
      if (text && text.match(/[ก-๙]/)) {
        return "font-family: 'Kanit', sans-serif";
      }
      if (text && text.match(/[\u4e00-\u9fff\u3400-\u4dbf]/)) {
        return "font-family: 'Noto Sans TC', sans-serif";
      }
      
      return "font-family: 'Lato', sans-serif";
    },
  },
  
};
</script>

<style lang="scss">
.flex.card {
  margin-left: 70px;
}
.divProfile {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.imgProfile {
  width: 42px;
  height: 42px;
  background: #dbdbdb;
  border-radius: 21px;
  object-fit: cover;
}
.rowDataStyle {
  display: flex;
  align-items: center;
  flex-direction: row;
  padding-top: 8px;
  padding-bottom: 8px;
  padding: 8px 40px;
  transition: 0.3s;
  &:hover {
    background: #f5f5f5;
  }
}
.RecipientsModalStyle {
  box-shadow: 0px 6px 6px #00000014;
}
.divMembersRecipients {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 12px 0 24px;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 55vh;
}
.containerTitle {
  display: flex;
  height: 36px;
  align-items: center;
  margin-top: 28px;
}

.divContentMembers {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  margin-left: 32px;
  margin-top: 32px;
  margin-right: 32px;
}

.btnAddQuestion {
  min-width: 172px;
  height: 52px;
  font-size: 18px;
  font-weight: 700;
  padding: 0 24px;
  white-space: nowrap;
}

.el-button--primary {
  color: #fff;
  background-color: #32c2d5;
  border-color: #32c2d5;
  opacity: 1;
}

.el-button--primary:hover,
.el-button--primary:focus {
  color: #fff;
  background-color: #1da4b6;
  border-color: #1da4b6;
  opacity: 1;
}

.el-button--primary:active {
  color: #fff;
  background-color: #32c2d5;
  border-color: #32c2d5;
  opacity: 1;
}

.txtHeaderModal {
  font-size: 20px;
  color: #2f2f2f;
  font-weight: 800;
}

.styleMoreOption {
  cursor: pointer;
  display: flex;
  justify-content: center;
  color: #4a4a4a;
  font-size: 18px;
  font-weight: 800;
  padding-top: 5px;
  padding-bottom: 3px;
}
.iconminus {
  font-size: 0.8rem;
  padding: 4px;
}
.btnAddOption {
  color: #fff;
  background-color: #6c30cc;
  border-color: #6c30cc;
}
.calendar-modal {
  width: 52px;
  height: 52px;
  background-color: #ffffff;
  border: 1px solid #e2e4ea;
  border-radius: 8px;
  color: #4a4a4a;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.calendar-modal:hover {
  background-color: #f6f7f9;
  border-color: #d4d7de;
  color: #6c30cc;
}

/* ─── Check-in Intensity: header trigger button ──────────────────────────
   Sits beside .calendar-modal so it shares the same 52x52 footprint.
   Inside: a 3-bar meter that progressively lights up by cadence
   (light = 1 bar, balanced = 2 bars, intense = 3 bars). */
.questions-intensity-btn {
  box-sizing: content-box;
  width: 52px;
  height: 52px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e2e4ea;
  color: #4a4a4a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease, box-shadow 0.15s ease;
}

.questions-intensity-btn:hover {
  background-color: #f6f7f9;
  border-color: #d4d7de;
  color: #6c30cc;
}

.questions-intensity-btn:focus-visible {
  outline: none;
  border-color: #6c30cc;
  box-shadow: 0 0 0 3px rgba(108, 48, 204, 0.18);
}

.questions-intensity-btn.is-open {
  background-color: #f2ecfb;
  border-color: rgba(108, 48, 204, 0.35);
  color: #6c30cc;
  box-shadow: 0 1px 2px rgba(108, 48, 204, 0.10);
}

.questions-intensity-btn.is-saving {
  cursor: wait;
}
.questions-intensity-btn.is-saving .qi-meter i.on {
  animation: qi-meter-pulse 1s ease-in-out infinite;
}

/* 3-bar intensity meter (inside the trigger button) */
.qi-meter {
  display: inline-flex;
  align-items: flex-end;
  justify-content: center;
  gap: 4px;
  width: 24px;
  height: 20px;
}
.qi-meter i {
  display: block;
  width: 4px;
  border-radius: 2px;
  background: #d8dade;
  transition: background-color 0.18s ease, transform 0.18s ease;
}
.qi-meter i:nth-child(1) { height: 8px; }
.qi-meter i:nth-child(2) { height: 14px; }
.qi-meter i:nth-child(3) { height: 20px; }
.qi-meter i.on { background: #6c30cc; }
.questions-intensity-btn[data-level="unset"] .qi-meter i {
  background: #cfd2d9;
}
.questions-intensity-btn:hover .qi-meter i {
  background: rgba(108, 48, 204, 0.28);
}
.questions-intensity-btn:hover .qi-meter i.on,
.questions-intensity-btn.is-open .qi-meter i.on {
  background: #6c30cc;
}
.questions-intensity-btn.is-open .qi-meter i:not(.on) {
  background: rgba(108, 48, 204, 0.22);
}
.questions-intensity-btn[data-level="unset"]:hover .qi-meter i,
.questions-intensity-btn[data-level="unset"].is-open .qi-meter i {
  background: #b9bec8;
}

@keyframes qi-meter-pulse {
  0%, 100% { transform: scaleY(1); }
  50% { transform: scaleY(0.65); }
}

/* ─── Check-in Intensity: popover panel ─────────────────────────────────
   Element UI teleports popovers, so layout lives in popper-class scope. */
.qi-panel {
  font-family: 'Lato', system-ui, sans-serif;
}

.qi-panel__head {
  padding: 2px 4px 0;
}

.qi-panel__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8a8a;
}

.qi-panel__title {
  margin-top: 6px;
  font-size: 16px;
  font-weight: 800;
  color: #2f2f2f;
  letter-spacing: -0.1px;
  line-height: 1.25;
}

.qi-panel__lead {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.qi-panel__lead-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9aa0ab;
}

.qi-panel__lead-divider {
  color: #cfd2d9;
  font-weight: 700;
}

.qi-state-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 26px;
  padding: 0 12px 0 10px;
  border-radius: 999px;
  background: rgba(108, 48, 204, 0.08);
  color: #6c30cc;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.01em;
  border: 1px solid rgba(108, 48, 204, 0.18);
}

.qi-state-chip__bars {
  display: inline-flex;
  align-items: flex-end;
  gap: 2px;
  height: 12px;
}
.qi-state-chip__bars i {
  display: block;
  width: 3px;
  border-radius: 2px;
  background: rgba(108, 48, 204, 0.28);
}
.qi-state-chip__bars i:nth-child(1) { height: 5px; }
.qi-state-chip__bars i:nth-child(2) { height: 8px; }
.qi-state-chip__bars i:nth-child(3) { height: 12px; }
.qi-state-chip__bars i.on { background: #6c30cc; }

.qi-state-chip[data-level="unset"] {
  background: #f6f7f9;
  color: #6f7480;
  border-color: #e2e4ea;
}

.qi-state-chip[data-level="unset"] .qi-state-chip__bars i {
  background: #cfd2d9;
}

.qi-panel__summary {
  font-size: 12.5px;
  color: #5a5a73;
  font-weight: 500;
  line-height: 1.4;
}

.qi-panel__divider {
  height: 1px;
  background: #eef0f5;
  margin: 18px 0 16px;
}

.qi-panel__foot {
  margin-top: 18px;
  padding: 0 4px;
  min-height: 18px;
  display: flex;
  align-items: center;
}

.qi-panel__helper {
  font-size: 11.5px;
  color: #9aa0ab;
  font-weight: 500;
}

.qi-saving {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6c30cc;
  font-weight: 700;
}

.qi-saving__spinner {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1.5px solid rgba(108, 48, 204, 0.25);
  border-top-color: #6c30cc;
  animation: spin 0.8s linear infinite;
}

/* Inline Undo affordance — replaces the helper line for ~8s after a
   successful intensity change. Auto-dismisses via a setTimeout in the
   component; the bar at the bottom is purely a visual countdown. */
.qi-undo {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 12px 9px;
  border-radius: 10px;
  background: rgba(108, 48, 204, 0.06);
  border: 1px solid rgba(108, 48, 204, 0.18);
  overflow: hidden;
  animation: qi-undo-enter 220ms ease-out;
}

.qi-undo__check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #6c30cc;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
  flex: 0 0 auto;
}

.qi-undo__text {
  flex: 1 1 auto;
  font-size: 12.5px;
  color: #2f2f2f;
  font-weight: 500;
  line-height: 1.3;
}
.qi-undo__text strong {
  color: #6c30cc;
  font-weight: 800;
}

.qi-undo__btn {
  appearance: none;
  background: transparent;
  border: 1px solid rgba(108, 48, 204, 0.35);
  color: #6c30cc;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.02em;
  height: 26px;
  padding: 0 14px;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.14s ease, border-color 0.14s ease,
    color 0.14s ease, transform 0.14s ease;
  flex: 0 0 auto;
}
.qi-undo__btn:hover {
  background: #6c30cc;
  border-color: #6c30cc;
  color: #fff;
  transform: translateY(-1px);
}
.qi-undo__btn:active {
  transform: translateY(0);
}
.qi-undo__btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(108, 48, 204, 0.22);
}

/* Subtle countdown bar — pinned to the bottom edge, shrinks 100→0 over 8s.
   Pure CSS, so it self-resets each time Vue re-mounts this element (the
   `:key` on the wrapper forces a remount when a fresh snapshot arms). */
.qi-undo__bar {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  width: 100%;
  background: linear-gradient(90deg, #6c30cc 0%, #aa86e0 100%);
  transform-origin: left center;
  animation: qi-undo-countdown 8s linear forwards;
}

@keyframes qi-undo-enter {
  from { opacity: 0; transform: translateY(2px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes qi-undo-countdown {
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
}

.refresh-calendar-btn {
  transition: opacity 0.2s ease;
}

.refresh-calendar-btn.refreshing {
  opacity: 0.5;
  pointer-events: none;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.grid-2-section {
  display: grid;
  grid-template-columns: 402px auto;
}
.grid-left {
  // Preview panel — background + internals now come from the shared
  // `.aq-preview-backdrop` / `.aq-preview-card` tokens in element-variables.scss.
  // Keep the class for the grid layout slot; no bespoke chrome.
}
.grid-right {
  // Form panel — surface is now white so the seam with the preview's light
  // wash reads as a subtle card division. Form-item labels turn into eyebrow-
  // style caps so every field aligns with the broader `qd-*` / `qp-*` language.
  background-color: #ffffff;
  padding: 32px;
  .el-form-item__label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #8a8a8a;
    line-height: 40px;
    padding-right: 12px;
    white-space: nowrap;
    overflow: visible;
    text-align: left;
  }
  // Inputs, selects, and cascaders inside the form share one refined shell.
  .el-input__inner,
  .el-textarea__inner {
    height: 40px;
    border-radius: 10px;
    border-color: #e2e4ea;
    background-color: #ffffff;
    color: #2f2f2f;
    font-size: 14px;
    transition: border-color .15s ease, box-shadow .15s ease;
  }
  .el-textarea__inner {
    height: auto;
    min-height: 40px;
    padding: 10px 12px;
  }
  .el-input__inner:focus,
  .el-textarea__inner:focus {
    border-color: #6c30cc;
    box-shadow: 0 0 0 3px rgba(108, 48, 204, 0.12);
  }
  .el-input.is-disabled .el-input__inner {
    background-color: #f7f8fb;
    color: #b7b7b7;
  }
  // Purple accent for brand-aligned toggles (Rolling Response / Submit to Managers).
  .el-switch.is-checked .el-switch__core {
    background-color: #6c30cc;
    border-color: #6c30cc;
  }
}

// Primary `ADD` / neutral `CANCEL` at the dialog footer. Specificity trick
// (`.btn-add.el-button`) wins over the local `.el-button--primary` teal rule
// further down in this same file.
#questions_form_modal .btn-add.el-button {
  height: 44px;
  min-width: 120px;
  padding: 0 22px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.01em;
  border-radius: 10px;
  background-color: #6c30cc;
  border-color: #6c30cc;
  box-shadow: none;
  transition: background-color .15s ease, border-color .15s ease, box-shadow .15s ease;
}
#questions_form_modal .btn-add.el-button:hover,
#questions_form_modal .btn-add.el-button:focus {
  background-color: #5a25b0;
  border-color: #5a25b0;
  box-shadow: 0 6px 16px rgba(108, 48, 204, 0.18);
}
#questions_form_modal .btn-add.el-button:active {
  background-color: #4f1f9c;
  border-color: #4f1f9c;
  box-shadow: none;
}
#questions_form_modal .btn-add.el-button.is-disabled,
#questions_form_modal .btn-add.el-button.is-disabled:hover {
  background-color: #c9b5ea;
  border-color: #c9b5ea;
  box-shadow: none;
}
#questions_form_modal .btn-cancel.el-button {
  height: 44px;
  min-width: 120px;
  padding: 0 22px;
  font-size: 14px;
  font-weight: 700;
  border-radius: 10px;
  background-color: transparent;
  border-color: #e2e4ea;
  color: #4a4a4a;
  transition: background-color .15s ease, color .15s ease, border-color .15s ease;
}
#questions_form_modal .btn-cancel.el-button:hover,
#questions_form_modal .btn-cancel.el-button:focus {
  background-color: #f7f8fb;
  border-color: #d4d7de;
  color: #2f2f2f;
}
.aq-success-panel {
  margin-top: 28px;
  padding: 28px;
  border: 1px solid #e2e4ea;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 12px 32px rgba(17, 24, 39, 0.08);
}
.aq-success-icon {
  width: 44px;
  height: 44px;
  border-radius: 22px;
  background: #f2ecfb;
  color: #6c30cc;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  margin-bottom: 18px;
}
.aq-success-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8a8a;
  margin-bottom: 8px;
}
.aq-success-title {
  font-size: 20px;
  line-height: 1.35;
  font-weight: 800;
  color: #1a1a1a;
  word-break: break-word;
}
.aq-success-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;

  > span {
    display: inline-flex;
    align-items: center;
    min-height: 28px;
    padding: 5px 10px;
    border-radius: 999px;
    background: #f7f8fb;
    color: #4a4a4a;
    font-size: 12px;
    font-weight: 700;
  }
}
.aq-success-copy {
  margin-top: 18px;
  color: #626262;
  font-size: 14px;
  line-height: 1.55;
}
.aq-success-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 24px;
}
.aq-success-actions .el-button {
  margin-left: 0;
}
.title-input-form {
  font-weight: 700;
}
.recipients-count {
  font-weight: 700;
  color: #6c30cc;
  font-size: 12px;
  height: 0px;
  text-align: right;
  position: relative;
  .top-header {
    position: absolute;
    bottom: -20px;
    right: 0;
    text-decoration: underline;
  }
}
.txtResponse {
  font-weight: 700;
  font-size: 15px;
  color: #505050;
  margin: 0 24px;
  word-wrap: normal;
  word-break: normal;
}
.txtAllSummary {
  font-weight: 700;
  font-size: 15px;
  color: #505050;
  word-wrap: normal;
  word-break: normal;
}
.txtRepeats {
  font: var(--unnamed-font-style-normal) normal var(--unnamed-font-weight-normal) 13px/16px var(--unnamed-font-family-lato);
  letter-spacing: var(--unnamed-character-spacing-0);
  color: var(--unnamed-color-000000);
  text-align: left;
  font: normal normal normal 13px/16px Lato, 'Kanit', sans-serif;
  letter-spacing: 0px;
  color: #000000;
  opacity: 0.6;
}
.divAllSummary {
  margin: 0 24px;
}
.txtHeaderStrategic {
  font: var(--unnamed-font-style-normal) normal var(--unnamed-font-weight-bold) var(--unnamed-font-size-15)/var(--unnamed-line-spacing-18) var(--unnamed-font-family-lato);
  letter-spacing: var(--unnamed-character-spacing-0);
  color: var(--unnamed-color-000000);
  text-align: left;
  font: normal normal bold 15px/18px Lato, 'Kanit', sans-serif;
  letter-spacing: 0px;
  color: #000000;
  // margin: 0 0 0 24px;
}
.txtHeaderSummary {
  margin: 0 24px;
  font: var(--unnamed-font-style-normal) normal var(--unnamed-font-weight-bold) var(--unnamed-font-size-15)/var(--unnamed-line-spacing-18) var(--unnamed-font-family-lato);
  letter-spacing: var(--unnamed-character-spacing-0);
  text-align: left;
  font: normal normal bold 15px/18px Lato, 'Kanit', sans-serif;
  letter-spacing: 0px;
  color: #6C30CC;
  opacity: 1;
}

.txtTotalResponse {
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9ca3af;
  margin-right: 20px;
}
.txtDropdown {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e2e4ea;
  color: #2f2f2f;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  outline: none;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.txtDropdown:hover,
.txtDropdown:focus {
  background: #f6f7f9;
  border-color: #d4d7de;
  color: #1a1a2e;
}

.el-dropdown:focus,
.el-dropdown:focus-visible,
.el-dropdown .txtDropdown:focus,
.el-dropdown .txtDropdown:focus-visible {
  outline: none;
}

.txtDropdown__caret {
  font-size: 14px;
  color: #9ca3af;
  transition: transform 0.2s ease, color 0.2s ease;
}

.el-dropdown.is-opened .txtDropdown .txtDropdown__caret,
.el-dropdown[aria-expanded="true"] .txtDropdown .txtDropdown__caret {
  transform: rotate(180deg);
  color: #6c30cc;
}
.shadowBottom {
  box-shadow: rgba(0, 0, 0, 0.08) 0px 10px 6px -6px;
  height: 16px;
  background: #fff;
  margin-left: -20px;
  margin-right: -20px;
}
.txtTeam {
  font-weight: 700;
  font-size: 13px;
  line-height: 16px;
  color: #505050;
  width: 170px;
  padding-top: 2px;
}
.rowItem {
  height: 50px;
  width: 260px;
  background: #f7f7f7;
}
.headerYesNo {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  margin-bottom: 2px;
}
.headerOpenEnded {
  display: flex;
  justify-content: space-between;
}
.icon-export {
  color: #000;
  cursor: pointer;
  margin-right: 10px;
}
.summaryLayout {
  background: #6C30CC08 0% 0% no-repeat padding-box;
  border: 1px solid #6C30CC;
  border-radius: 7px;
  opacity: 1;
}

div.el-table__body-wrapper.is-scrolling-none {
  height: 520px;
}
.talkingPointsBox {
  height: 40px;
}
.talking-points {
  border-width: 0;
  border-radius: 16px;
  font-weight: 700;
  // box-shadow: 0px 2px 4px #00000029;
  transition: 0.2s;
  margin: 8px 8px 0 0;

  &:hover {
    margin-top: 5px;
    // box-shadow: 0px 5px 6px #00000019;
    margin-bottom: 3px;
  }
}
.tooltipStyleTalkingPoints {
  width: 375px;
  padding: 8px;
}
.tooltip-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.tooltip-item {
  display: flex;
  align-items: flex-start;
  padding-top : 11px;
}

.tooltip-item p {
  margin: 0;
  font-size: 12px;
}
.oi-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  min-height: 24px;
  border-radius: 50%;
  background-color: rgba(108, 48, 204, 0.2); /* สีพื้นหลัง */
  // opacity: 0.2;
  color: #6C30CC; /* สีตัวอักษร */
  font-weight: bold;
  margin-right: 12px;
}
.GreenBoxStyle {
  color: #249d2a;
  background-color: #edffee;
}

.RedBoxStyle {
  color: #f44336;
  background-color: #ffedeb;
}

.GreyBoxStyle {
  color: #484848;
  // border-color: #b2b2b2;
  background-color: #f0f0f0;
}
</style>
<style lang="scss">
#question #questions_form_modal {
  .el-dialog__body {
    padding: 0px;
  }
  .el-dialog__header {
    padding: 0px;
  }
  .el-menu {
    background-color: transparent;
  }
  .el-date-editor .el-range-separator {
    width: 6%;
  }
  .el-form-item {
    margin-bottom: 12px;
  }
  .el-form-item__error {
    font-size: 10px;
    padding-top: 0px;
  }
  .el-form-item__label {
    line-height: 40px;
  }
  .el-cascader__tags .el-tag:not(.is-hit) {
    border-color: #6c30cc;
    background-color: #6c30cc24;
    color: #6c30cc;
    font-size: 15px;
    height: 30px;
    .el-tag .el-icon-close {
      background-color: #6c30cc;
      color: #6c30cc;
    }
  }
}
#question .el-tabs__item {
  height: 52px;
  line-height: 52px;
  width: 200px;
  text-align: center;
}

#question .el-tabs__header {
  margin: 0px;
}

#question .el-menu--horizontal > .el-menu-item {
  color: rgba(0, 0, 0, 0.62);
  font-size: 16px;
  font-weight: 400;
  height: 52px;
}

#question .el-menu--horizontal .el-menu-item:not(.is-disabled):hover,
.el-menu--horizontal .el-menu-item:not(.is-disabled):focus .el-menu-item:hover {
  background-color: transparent;
  color: #6c30cc;
}

#question .el-menu--horizontal > .el-menu-item.is-active {
  background-color: transparent;
  border-bottom: 2px solid #6c30cc;
  color: #6c30cc;
  font-weight: 700;
}

#question .el-form-item__label {
  padding-right: 24px;
}

#question .el-dialog__body {
  padding-top: 0px;
  padding-bottom: 8px;
}

#question .el-dialog__header {
  padding-top: 32px;
  padding-left: 32px;
  padding-bottom: 0px;
}

/* Making the close button the same size across different modal windows */
#question i.el-dialog__close.el-icon.el-icon-close {
  font-size: 26px;
  margin-top: 12px;
  margin-right: 12px;
}
#question i.el-select__caret.el-input__icon.el-icon-arrow-up {
  color: #000;
  font-size: 16px;
  font-weight: bold;
}
#question i.el-icon-caret-right {
  margin-top: 20px;
}
#question .bar-results {
  height: 50px;
  text-align: center;
  color: #fff;
  padding-top: 16px;
  font-size: 16px;
}
#question .box {
  height: 20px;
  width: 20px;
  border-radius: 4px;
}
.box.color-1,
#question .bar-results.color-1 {
  background-color: #d33e3e;
}
.box.color-2,
#question .bar-results.color-2 {
  background-color: #ff4e4e;
}
.box.color-3,
#question .bar-results.color-3 {
  background-color: #eed24e;
}
.box.color-4,
#question .bar-results.color-4 {
  background-color: #76ca7d;
}
.box.color-5,
#question .bar-results.color-5 {
  background-color: #00b383;
}
.box.color-6,
#question .bar-results.color-6 {
  background-color: #068462;
}
#otherTable div.el-table__body-wrapper.is-scrolling-none,
#scaleTable div.el-table__body-wrapper.is-scrolling-none,
#yesNoTable div.el-table__body-wrapper.is-scrolling-none {
  height: 420px !important;

  // thin-scrollbar
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track-piece {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #e0e0e0;
    border-radius: 4px;
  }
}

#otherTable div.el-table__expand-icon,
#scaleTable div.el-table__expand-icon,
#yesNoTable div.el-table__expand-icon {
  color: #ffffff;
}

#otherTable i.el-icon-caret-bottom,
#scaleTable i.el-icon-caret-bottom,
#yesNoTable i.el-icon-caret-bottom {
  color: #000000;
  margin-bottom: 4px;
  cursor: pointer;
}

#otherTable i.el-icon-caret-right,
#scaleTable i.el-icon-caret-right,
#yesNoTable i.el-icon-caret-right {
  color: #000000;
  margin-bottom: 20px;
  cursor: pointer;
}

#question #calendarModal {
  .el-dialog__header {
    padding-top: 40px;
    padding-left: 40px;

    > .el-dialog__title {
      font-size: 20px;
      color: #000;
    }
  }

  .el-dialog__body {
    padding-bottom: 40px;
    padding-left: 40px;

    .line-shadow {
      width: calc(900px + 60px);
      box-shadow: 0px 6px 6px #00000014;
      margin-left: -40px;
      margin-top: -7px;
      height: 6px;
    }

    .calendar-container {
      display: grid;
      grid-template-columns: 150px 150px 150px 150px 150px 68px 68px;

      .day-of-week-title {
        font-size: 16px;
        font-weight: 700;
        color: #000;
        text-align: center;
        padding: 24px 14px 16px;
      }

      .day-box {
        position: relative;
        height: 84px;
        border: 1px solid rgba(0, 0, 0, 0.07);
        padding: 12px;
        transition: 0.2s;

        .title {
          font-size: 13px;
          font-weight: 400;
          color: #000000;
          opacity: 0.5;
        }

        .questions-card {
          background-color: rgba(0, 0, 0, 0.07);
          color: #000000;
          border-radius: 2px;
          font-size: 10px;
          padding: 3px 7px;
          margin-bottom: 1px;
          font-weight: 700;

          > img {
            margin-right: 5px;
          }
        }

        .questions-card.calendar-question-disabled {
          opacity: 0.38;
          background-color: rgba(0, 0, 0, 0.04);
        }
      }
      .day-box.more-than-five {
        // box-shadow: inset 0 0 5px rgba(255, 0, 0, 0.6);
      }
      .day-box.has-recent-question {
        border-color: #6c30cc;
        box-shadow: inset 0 0 0 2px rgba(108, 48, 204, 0.18);
      }
      .more-than-five .questions-card, .more-than-five .legend-box {
        background-color: rgba(255, 115, 0, 0.1);
      }
      .less-than-four .questions-card, .less-than-four .legend-box {
        background-color: rgba(0, 255, 136, 0.1);
      }

      input[type="radio"]:checked + label > .day-box {
        border-color: #d1d1d1;
        background-color: #efe5ff;

        .title {
          color: #6c30cc;
          font-weight: 700;
          opacity: 1;
        }

        .questions-card {
          background-color: #6c30cc;
          color: #ffffff;
          user-select: none;
          > span {
            opacity: 1;
          }
        }
      }

      input[type="radio"] {
        display: none;
      }
    }
  }
}
.list-group-item.is-recent-question {
  background: #fbf9ff;
  border: 1px solid #e9ddfa;
  border-radius: 8px;
  padding-left: 10px;
  margin-left: -10px;
}
.legend-container {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.legend-box {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  margin-right: 6px;
  display: inline-block;
}
.legend-item {
  display: flex;
  align-items: center;
}
.legend-item-label {
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.55);
}
.more-than-five .legend-box {
  background-color: rgba(255, 115, 0, 0.2);
}
.less-than-four .legend-box {
  background-color: rgba(0, 255, 136, 0.2);
}

// Open-ended count badge styles
.open-ended-count-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  height: 16px;
  padding: 1px 4px 1px 3px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 2px;
  z-index: 10;
  user-select: none;

  .badge-icon {
    width: 9px;
    height: 9px;
    flex-shrink: 0;
    opacity: 0.4;
  }

  .badge-count {
    font-size: 10px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.5);
    line-height: 1;
  }

  &.count-warning {
    // height: 18px;
    padding: 1px 7px 1px 5px;
    background-color: #F7B924;
    border: none;
    border-radius: 10px;
    gap: 3px;

    .badge-icon {
      width: 10px;
      height: 10px;
      opacity: 0.8;
    }

    .badge-count {
      font-size: 11px;
      font-weight: 600;
      color: #00000080;
    }
  }
}

.legend-count-badge {
  height: 16px;
  padding: 2px 5px 2px 4px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-right: 6px;
  flex-shrink: 0;

  .legend-badge-icon {
    width: 9px;
    height: 9px;
    opacity: 0.6;
  }

  .legend-badge-count {
    font-size: 10px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.65);
    line-height: 1;
  }

  &.warning {
    height: 18px;
    padding: 2px 6px 2px 4px;
    background-color: #F7B924;
    border: none;
    border-radius: 10px;
    gap: 3px;

    .legend-badge-icon {
      width: 10px;
      height: 10px;
      opacity: 1;
    }

    .legend-badge-count {
      font-size: 11px;
      font-weight: 600;
      color: #ffffff;
    }
  }
}

.open-ended-count,
.open-ended-warning {
  display: flex;
  align-items: center;
}

#yesNoTable .el-table__body tr > td.el-table__cell,
#scaleTable .el-table__body tr > td.el-table__cell,
#otherTable .el-table__body tr > td.el-table__cell {
  &:first-of-type {
    border-radius: 6px 0 0 6px;
  }
  &:last-of-type {
    border-radius: 0 6px 6px 0;
  }
  transition: 0.3s;
  .el-icon-caret-bottom,
  .el-icon-caret-right,
  .rowItem {
    transition: 0.3s;
    .txtTeam {
      transition: 0.3s;
    }
  }
}
// Row hover is a subtle purple wash — the old bright lilac (#e4d2ff) drew the
// eye away from the data. Soft wash keeps focus on the bar/chip while still
// signalling the row is interactive.
#yesNoTable.el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell,
#scaleTable.el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell,
#otherTable.el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell {
  background-color: #fbf9ff;
  &:first-of-type {
    border-radius: 6px 0 0 6px;
  }
  &:last-of-type {
    border-radius: 0 6px 6px 0;
  }
  .el-icon-caret-bottom,
  .el-icon-caret-right {
    color: #6c30cc;
  }
}
#openEndedTable div.el-table__body-wrapper.is-scrolling-none {
  height: 470px !important;
}
.custom-textarea .el-textarea__inner {
  width: 584px;
  height: 104px;
  font-size: 16px;
  resize: none;
  word-wrap:break-word;
  word-break:keep-all;
  padding-top: 16px;
}
.custom-textarea .el-textarea__inner:focus {
  border-color: #6c30cc;
}
// AI prompt block: textarea fills the form panel width, Generate Question
// button sits right-aligned below so it never pokes outside the dialog.
.aq-generate-block {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.aq-generate-block .custom-textarea {
  width: 100%;
}
.aq-generate-block .custom-textarea .el-textarea__inner {
  width: 100%;
  height: 88px;
  font-size: 14px;
  padding: 12px 14px;
  border-radius: 10px;
  border-color: #e2e4ea;
  resize: none;
  line-height: 1.5;
}
.aq-generate-block .custom-textarea .el-textarea__inner:focus {
  border-color: #6c30cc;
  box-shadow: 0 0 0 3px rgba(108, 48, 204, 0.12);
}
.aq-generate-actions {
  display: flex;
  justify-content: flex-end;
}

.generate-question {
  background-color: #6c30cc !important;
  border-color: #6c30cc !important;
  min-width: 148px;
  height: 36px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: 10px !important;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 0 18px;
  text-align: center;
  transition: background-color .15s ease, box-shadow .15s ease;
}
.generate-question:hover,
.generate-question:focus {
  background-color: #5a25b0 !important;
  border-color: #5a25b0 !important;
  box-shadow: 0 6px 16px rgba(108, 48, 204, 0.18);
}

/* Team Filter Dropdown Styles */
.drop-down-container {
  margin-left: 12px;
  height: 40px;
  border-radius: 4px;
  padding: 0 8px 0 12px;
  min-width: 90px;
}

.drop-down-container.active {
  margin-left: 10px;
  background-color: rgba(#32C2D5, 0.1);
  padding: 0 6px 0 10px;
}

.text-drop-down {
  color: #00ACC1;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  
  .aggregation-indicator {
    margin-left: 6px;
    opacity: 0.7;
    vertical-align: text-bottom;
  }
}

.team-dropdown-header {
  padding: 12px 16px;
  background-color: #f8f8f8;
  border-bottom: 1px solid #e8e8e8;
  font-size: 13px;
  font-weight: 700;
  color: #626262;
}

.team-search-container {
  padding: 8px 12px;
  border-bottom: 1px solid #e8e8e8;
  background-color: #fff;
}

.team-dropdown-content {
  max-height: 280px;
  overflow-y: auto;
}

.team-option-content {
  padding: 4px 0;
  min-height: 32px;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
}

.team-option-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 8px;
}

.team-name {
  font-weight: 600;
  color: #333;
  flex: 1;
  text-align: left;
  margin-right: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.team-response-count {
  font-size: 12px;
  color: #626262;
  font-weight: 400;
  margin-left: 8px;
  white-space: nowrap;
}

.selected-team {
  background-color: rgba(50, 194, 213, 0.1) !important;
}

.no-teams-found {
  padding: 16px;
  text-align: center;
  color: #909399;
  font-size: 13px;
  font-style: italic;
}

.feedback-team-label {
  align-self: flex-start;
  margin: 0 24px 8px 24px;
  padding: 4px 12px;
  background-color: #f0f0f0;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #626262;
  display: inline-block;
  
  // Color coding for different team hierarchy levels
  &.team-color-0 {
    background-color: #e3f2fd;
    color: #1976d2;
    border: 1px solid #bbdefb;
  }
  
  &.team-color-1 {
    background-color: #f3e5f5;
    color: #7b1fa2;
    border: 1px solid #ce93d8;
  }
  
  &.team-color-2 {
    background-color: #e8f5e8;
    color: #388e3c;
    border: 1px solid #a5d6a7;
  }
  
  &.team-color-3 {
    background-color: #fff3e0;
    color: #f57c00;
    border: 1px solid #ffcc02;
  }
  
  &.team-color-4 {
    background-color: #fce4ec;
    color: #c2185b;
    border: 1px solid #f8bbd9;
  }
  
  &.team-color-default {
    background-color: #f5f5f5;
    color: #616161;
    border: 1px solid #e0e0e0;
  }
}

.aggregation-info-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #f0fafb;
  border: 1px solid #32c2d5;
  border-radius: 4px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #00ACC1;
  
  i {
    font-size: 16px;
  }
}

.filtered-indicator {
  color: #32c2d5;
  font-size: 14px;
  font-weight: 600;
}

.txtResponse {
  font-weight: 700;
  font-size: 15px;
  color: #505050;
  margin: 0 24px;
  word-wrap: normal;
  word-break: normal;
  text-align: left;
  line-height: 1.5;
}

.headerOpenEnded {
  display: flex;
  justify-content: space-between;
}

.txtTotalResponse {
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9ca3af;
  margin-right: 20px;
}

.txtDropdown {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e2e4ea;
  color: #2f2f2f;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  outline: none;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.txtDropdown:hover,
.txtDropdown:focus {
  background: #f6f7f9;
  border-color: #d4d7de;
  color: #1a1a2e;
}

.el-dropdown:focus,
.el-dropdown:focus-visible,
.el-dropdown .txtDropdown:focus,
.el-dropdown .txtDropdown:focus-visible {
  outline: none;
}

.txtDropdown__caret {
  font-size: 14px;
  color: #9ca3af;
  transition: transform 0.2s ease, color 0.2s ease;
}

.el-dropdown.is-opened .txtDropdown .txtDropdown__caret,
.el-dropdown[aria-expanded="true"] .txtDropdown .txtDropdown__caret {
  transform: rotate(180deg);
  color: #6c30cc;
}

.shadowBottom {
  box-shadow: rgba(0, 0, 0, 0.08) 0px 10px 6px -6px;
  height: 16px;
  background: #fff;
  margin-left: -20px;
  margin-right: -20px;
}

.summaryLayout {
  background: #6C30CC08 0% 0% no-repeat padding-box;
  border: 1px solid #6C30CC;
  border-radius: 7px;
  opacity: 1;
}

.talkingPointsBox {
  height: 40px;
}

.talking-points {
  border-width: 0;
  border-radius: 16px;
  font-weight: 700;
  transition: 0.2s;
  margin: 8px 8px 0 0;

  &:hover {
    margin-top: 5px;
    margin-bottom: 3px;
  }
}

.tooltipStyleTalkingPoints {
  width: 375px;
  padding: 8px;
}

.tooltip-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tooltip-item {
  display: flex;
  align-items: flex-start;
  padding-top: 11px;
}

.tooltip-item p {
  margin: 0;
  font-size: 12px;
}

.oi-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  min-height: 24px;
  border-radius: 50%;
  background-color: rgba(108, 48, 204, 0.2);
  color: #6C30CC;
  font-weight: bold;
  margin-right: 12px;
}

.GreenBoxStyle {
  color: #249d2a;
  background-color: #edffee;
}

.RedBoxStyle {
  color: #f44336;
  background-color: #ffedeb;
}

.GreyBoxStyle {
  color: #484848;
  background-color: #f0f0f0;
}

.txtHeaderSummary {
  margin: 0 24px;
  font: normal normal bold 15px/18px Lato, 'Kanit', sans-serif;
  letter-spacing: 0px;
  color: #6C30CC;
  opacity: 1;
}

.txtAllSummary {
  font-weight: 700;
  font-size: 15px;
  color: #505050;
  word-wrap: normal;
  word-break: normal;
}

.txtRepeats {
  font: normal normal normal 13px/16px Lato, 'Kanit', sans-serif;
  letter-spacing: 0px;
  color: #000000;
  opacity: 0.6;
}

.divAllSummary {
  margin: 0 24px;
}

.icon-export {
  color: #000;
  margin-right: 10px;
}

/* Question Meta Badges */
.question-meta-badges {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 6px;
}

.question-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.badge-multiple-choice {
  background-color: rgba(33, 150, 243, 0.15) !important;
  color: rgba(13, 71, 161, 0.9) !important;
}

.badge-yes-no {
  background-color: rgba(76, 175, 80, 0.15) !important;
  color: rgba(27, 94, 32, 0.9) !important;
}

.badge-scale {
  background-color: rgba(156, 39, 176, 0.15) !important;
  color: rgba(74, 20, 140, 0.9) !important;
}

.badge-text-area {
  background-color: rgba(158, 158, 158, 0.15) !important;
  color: rgba(66, 66, 66, 0.9) !important;
}

.badge-follow-up {
  background-color: rgba(255, 152, 0, 0.15) !important;
  color: rgba(230, 81, 0, 0.9) !important;
}

.badge-default {
  background-color: rgba(158, 158, 158, 0.15) !important;
  color: rgba(97, 97, 97, 0.9) !important;
}

/* Power Skill Badge */
.power-skill-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background-color: rgba(247, 117, 17, 0.15) !important;
  color: rgba(183, 69, 0, 0.9) !important;
  line-height: 1.2;
  white-space: nowrap;
}

/* Frequency Tags */
.frequency-tag {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
  line-height: 1.3;
  white-space: nowrap;
}

.tag-biannual {
  background-color: rgba(239, 108, 0, 0.15) !important;
  color: rgba(191, 54, 12, 0.9) !important;
}

.tag-yearly {
  background-color: rgba(0, 150, 136, 0.15) !important;
  color: rgba(0, 77, 64, 0.9) !important;
}

.tag-follow-up {
  background-color: rgba(158, 158, 158, 0.15) !important;
  color: rgba(97, 97, 97, 0.9) !important;
}

.tag-default {
  background-color: rgba(84, 110, 122, 0.15) !important;
  color: rgba(38, 50, 56, 0.9) !important;
}

/* Question Details dialog polish
   ----------------------------------
   - .question-detail-eyebrow: demotes the old "QUESTION DETAILS" label to a
     quiet caption, so the question text itself reads as the title.
   - .question-detail-divider: replaces the heavy .shadowBottom bleed with a
     subtle hairline matching the global dialog header hairline.
   - .question-detail-actions .el-button--text ensures the Download pill
     survives Element UI's default text-button padding. */
.question-detail-eyebrow {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8a8a;
}

.question-detail-header {
  padding-top: 4px;
}

.question-detail-divider {
  height: 1px;
  background: #eef0f5;
  margin: 20px -20px 0;
}

.question-detail-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.question-detail-actions .header-icon-btn.el-button--text {
  padding: 0 12px;
}
</style>
